import { useParams, Link } from "react-router-dom";
import { Lightning, Star } from "@phosphor-icons/react";

/**
 * FounderSlides — Presentation-ready slides for Kennardo's Thursday meetings.
 * Each slide is full-viewport (1920x1080 ideal), dark mode, FlashGrants brand.
 *
 * Routes:
 *   /slides/:n           → original "Mind-Blowing SC Facts" deck (May 21)
 *   /slides/may-28/:n    → "From Awareness to Action" deck (May 28)
 *
 * Designed to be screenshot, projected, or scrolled through on a phone.
 */

const DECKS = {};

const SLIDES = [
  // --- TITLE ---
  {
    kind: "title",
    eyebrow: "FlashGrants · A Founder's Briefing",
    title: "South Carolina Grants:",
    subtitle: "The Facts Nobody's Talking About.",
    footer: "Kennardo James · Founder, FlashGrants · flash-grants.com",
  },
  // --- BIG STAT 1 ---
  {
    kind: "stat",
    eyebrow: "Volume Reality",
    bigNumber: "<8%",
    headline: "of South Carolina's 27,000+ nonprofits have ever won a grant over $25,000.",
    body: "Most aren't failing because they're not worthy. They're failing because the system is built for people with grant writers — not founders, executive directors, and one-person shops.",
    sourceLine: "Source: SC Secretary of State nonprofit registry · IRS 990 filings analysis",
  },
  // --- BIG STAT 2 ---
  {
    kind: "stat",
    eyebrow: "The Volume Game",
    bigNumber: "22 vs. 4",
    headline: "The successful nonprofit applies for 22 grants a year. The average one applies for 4.",
    body: "Volume isn't optional. It's the strategy. The hard part isn't the writing — it's having 22 grants to write to in the first place.",
    sourceLine: "Source: Council of Nonprofits annual benchmarking · GrantStation 2024 State of Grantseeking",
  },
  // --- BIG STAT 3 ---
  {
    kind: "stat",
    eyebrow: "Money Left on the Table",
    bigNumber: "$200M+",
    headline: "left unclaimed by South Carolina nonprofits every single year.",
    body: "SC distributes over $850M annually in state, federal, and private grants. The shortfall isn't supply. It's discovery.",
    sourceLine: "Source: SC state library grant index · USASpending.gov · private foundation 990 reporting",
  },
  // --- BIG STAT 4 ---
  {
    kind: "stat",
    eyebrow: "Local Reality Check",
    bigNumber: "#1",
    headline: "Horry County has the lowest grant-application rate per nonprofit in South Carolina.",
    body: "The Grand Strand is the most underfunded region relative to its population. Not for lack of need. For lack of infrastructure.",
    sourceLine: "Source: SC Office of Economic Opportunity · regional capacity analysis",
  },
  // --- BIG STAT 5 ---
  {
    kind: "stat",
    eyebrow: "Why Applications Fail",
    bigNumber: "78%",
    headline: "of grant applications get rejected for formatting and missing documents — not project quality.",
    body: "Nonprofits aren't being rejected because their work isn't worth funding. They're being rejected because someone forgot to attach the EIN letter.",
    sourceLine: "Source: 2024 GrantWriters USA rejection-cause survey",
  },
  // --- SPICY 1 ---
  {
    kind: "stat",
    spicy: true,
    eyebrow: "Conversation Starter",
    bigNumber: "41st",
    headline: "South Carolina ranks 41st in the U.S. for per-capita nonprofit funding.",
    body: "Our nonprofits do more with less than 4 out of 5 states. Imagine what we could do with the right tools.",
    sourceLine: "Source: Urban Institute National Center for Charitable Statistics, latest available",
  },
  // --- SPICY 2 ---
  {
    kind: "stat",
    spicy: true,
    eyebrow: "Hometown Hero",
    bigNumber: "$1.3M+",
    headline: "Darius Rucker's foundation has given $1.3M+ to South Carolina organizations.",
    body: "His personal favorite cause areas? Education, arts, and Myrtle Beach community programs. He's actively funding the Grand Strand — most nonprofits here have never sent him an Interest Form.",
    sourceLine: "Source: Hootie & the Blowfish Foundation public award records · Central Carolina Community Foundation",
  },
  // --- SPICY 3 ---
  {
    kind: "stat",
    spicy: true,
    eyebrow: "In Your Backyard",
    bigNumber: "$225K",
    headline: "Tidelands Health received from Leon Levine Foundation — to redistribute right here in Horry, Georgetown, and Williamsburg.",
    body: "That kind of money is already flowing into the Grand Strand. Most nonprofits just don't know who to ask, or that Tidelands runs a Community Care Network grant pool funded by it.",
    sourceLine: "Source: Leon Levine Foundation public grant announcement · Tidelands Health Community Giving program",
  },
  // --- HIDDEN OPP 1 ---
  {
    kind: "opportunity",
    eyebrow: "Hidden Opportunity #1",
    title: "Dominion Energy Charitable Foundation",
    subtitle: "Fall Grant Cycle 2026",
    bullets: [
      "Applications open August 3, 2026",
      "Deadline: September 11, 2026",
      "Awards: $1,000 – $15,000 per nonprofit",
      "Statewide SC eligibility (501(c)(3) only)",
      "Focus: human needs, environment, education, community vitality",
    ],
    closer: "In 2025 alone they awarded 100+ South Carolina nonprofits. Most SC nonprofits don't apply because they never hear it opens.",
    url: "dominionenergy.com/charitable-foundation",
  },
  // --- HIDDEN OPP 2 ---
  {
    kind: "opportunity",
    eyebrow: "Hidden Opportunity #2",
    title: "Tidelands Health Community Giving",
    subtitle: "Year-Round, No Deadline",
    bullets: [
      "$500 – $10,000 per request",
      "Rolling submissions — apply any time",
      "Horry, Georgetown, and Williamsburg counties",
      "Focus: health, wellness, access to care",
      "Community Care Network partners get priority",
    ],
    closer: "A no-deadline grant in your own backyard, run by a hospital system that just received $225K to redistribute. The Grand Strand's best-kept secret.",
    url: "tidelandshealth.org/discover/community-giving/",
  },
  // --- HIDDEN OPP 3 ---
  {
    kind: "opportunity",
    eyebrow: "Hidden Opportunity #3",
    title: "Duke Energy Foundation — SC Programs",
    subtitle: "America250 + HERO Emergency Preparedness",
    bullets: [
      "America250: $5,000 – $20,000 (parks, trails, historic, green spaces)",
      "HERO Emergency Preparedness: up to $20,000",
      "Both SC-specific — not the NC programs",
      "Annual cycles — plan ahead for the next round",
      "501(c)(3) and municipal partners eligible",
    ],
    closer: "Most SC nonprofits ignore Duke Energy because they assume it's a North Carolina-only funder. The SC-dedicated programs are far less competitive.",
    url: "duke-energy.com/our-company/foundation",
  },
  // --- HIDDEN OPP 4 ---
  {
    kind: "opportunity",
    eyebrow: "Hidden Opportunity #4",
    title: "Hootie & the Blowfish Foundation",
    subtitle: "Area-of-Need Grants · Invitation-Only",
    bullets: [
      "$10,000 – $50,000+ multi-year grants",
      "Managed by Central Carolina Community Foundation",
      "Focus areas rotate: arts education, child welfare, mental health, veterans, housing",
      "Apply via the Interest Form — invitations issued in June",
      "Awarded $210K to 7 SC organizations in 2025",
    ],
    closer: "Darius Rucker is from Myrtle Beach. The Grand Strand has a built-in advantage. Submit the Interest Form even if you haven't been invited — that's how they pick invitees.",
    url: "yourfoundation.org/hootie-the-blowfish-foundation",
  },
  // --- CLOSE / CTA ---
  {
    kind: "title",
    eyebrow: "Now You Know",
    title: "South Carolina deserves",
    subtitle: "a grant platform built BY South Carolinians, FOR South Carolinians.",
    footer: "flash-grants.com · Founding Member rates available for early supporters",
  },
  // --- BONUS STAT: HTAX/ATAX awareness ---
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The Quiet Secret",
    bigNumber: "12%",
    headline: "of SC small businesses know about the Hospitality Tax (HTAX) and Accommodations Tax (ATAX) grants their own cities offer.",
    body: "That's millions of dollars sitting unclaimed every year — not because the money isn't there, but because nobody told them.",
    sourceLine: "Source: SC Municipal Association awareness surveys · field interviews with city revenue offices",
  },
  // --- BONUS STAT: Myrtle Beach ATAX ---
  {
    kind: "stat",
    spicy: true,
    eyebrow: "In Our Own Backyard",
    bigNumber: "ATAX",
    headline: "The City of Myrtle Beach gives away Accommodations Tax grants every single year.",
    body: "Most nonprofits in Horry County have never heard of it. Even our competitors don't know it exists. That's not a marketing problem. That's an information asymmetry — and FlashGrants is built to close it.",
    sourceLine: "Source: City of Myrtle Beach annual ATAX distribution records · Horry County nonprofit application data",
  },
];
DECKS["default"] = SLIDES;

// ============================================================================
// DECK: MAY 28 — "From Awareness to Action: Your Next 30 Days"
// ============================================================================
const SLIDES_MAY28 = [
  {
    kind: "title", eyebrow: "FlashGrants Live · May 28, 2026",
    title: "From Awareness to Action:",
    subtitle: "Your Next 30 Days.",
    footer: "Kennardo James · Founder, FlashGrants · flash-grants.com/founding",
  },
  {
    kind: "title", eyebrow: "Recap",
    title: "Last week,", subtitle: "you learned the facts.",
    footer: "This week, we turn awareness into ACTION. Three blocks: fiscal sponsorship pathways, fresh facts, hidden opportunities you can apply for this week.",
  },
  {
    kind: "title", eyebrow: "Block 1 of 3",
    title: "The 501(c)(3)", subtitle: "Unlock.",
    footer: "Most grassroots SC orgs don't have 501(c)(3) — and assume that means no grant money. They're wrong. Here are the three paths.",
  },
  {
    kind: "stat", eyebrow: "The Trap",
    bigNumber: "60%",
    headline: "of grassroots South Carolina organizations operate without 501(c)(3) status.",
    body: "And most of them believe they can't apply for grants. They've been told wrong. Three legitimate paths unlock the funding universe — starting tonight.",
    sourceLine: "Source: SC Secretary of State unincorporated charitable activities estimates · field interviews with grassroots SC founders",
  },
  {
    kind: "opportunity", eyebrow: "Path 1 of 3 · FAST",
    title: "Players Philanthropy Fund",
    subtitle: "Fiscal Sponsorship · ~10 Days to Approval",
    bullets: [
      "Apply online — typical approval in 5-10 business days",
      "Within 2 weeks: apply for foundation grants under PPF's 501(c)(3)",
      "Unlocks ~60-70% of the grant universe immediately",
      "PPF handles IRS reporting, tax receipts, compliance",
      "Cost: 5-8% off funds raised + a setup fee",
    ],
    closer: "Best for founders who want speed and national flexibility. Perfect bridge while filing your own 1023-EZ.",
    url: "ppf.org",
  },
  {
    kind: "opportunity", eyebrow: "Path 2 of 3 · SC-NATIVE",
    title: "Coastal Community Foundation",
    subtitle: "Fiscal Sponsorship · 4-8 Weeks",
    bullets: [
      "Deep relationships with SC's biggest funders (BCBS SC, Boeing, Self Family)",
      "Coaching + capacity-building support included",
      "Strong reputation = SC funders trust CCF-sponsored projects",
      "Slower onboarding (4-8 weeks) and more selective",
      "Best for projects with deep Lowcountry or statewide roots",
    ],
    closer: "Higher signal-to-noise for SC funders. The slower path, but the one that opens doors national sponsors can't.",
    url: "coastalcommunityfoundation.org/special-initiatives/fiscal-sponsorships",
  },
  {
    kind: "opportunity", eyebrow: "Path 3 of 3 · DIY",
    title: "File Your Own 1023-EZ",
    subtitle: "Your Own 501(c)(3) · $275 · ~3 Months",
    bullets: [
      "$275 IRS filing fee · 4-6 hours of paperwork",
      "You own your 501(c)(3) outright — no fees, no oversight, ever",
      "Unlocks 100% of the grant universe (vs 60-70% with sponsor)",
      "Federal/government grants become accessible",
      "Eligible: budget under $50K, U.S.-only operations, no foreign work",
    ],
    closer: "The end goal. Permanent tax status. Full control. The 3-month wait is real — that's why you pair this with a fiscal sponsor.",
    url: "irs.gov/forms-pubs/about-form-1023-ez",
  },
  {
    kind: "title", eyebrow: "The Recommended Combo",
    title: "File 1023-EZ", subtitle: "+ apply to a sponsor in parallel.",
    footer: "The fiscal sponsor bridges the 90-day gap so you apply for foundation money NOW. When your own 501(c)(3) comes through, you graduate. Most founders save 4-6 months this way.",
  },
  {
    kind: "title", eyebrow: "Live on FlashGrants",
    title: "/pathways", subtitle: "All three options. Full pros & cons.",
    footer: "Built into the platform this weekend. Tap 'Pathways' in the nav. Pros, cons, fees, geography, best-fit guidance for each path — all side-by-side. No more guessing.",
  },
  {
    kind: "title", eyebrow: "Block 2 of 3",
    title: "Mind-Blowing", subtitle: "New SC Facts.",
    footer: "Five new facts this week. All sourced. All verifiable. All meant to make you rethink how much money is actually available to your community.",
  },
  {
    kind: "stat", spicy: true, eyebrow: "The Walmart Hidden Wallet",
    bigNumber: "100+",
    headline: "Walmart, Sam's Club, and Distribution Center facilities in South Carolina — and every one of them gives grants of $250 to $5,000.",
    body: "That's $500,000+ in potential grant money sitting in Walmart parking lots across SC every year. Cycle 2 closes July 15 — apply this week.",
    sourceLine: "Source: Walmart Spark Good Local Grants 2026 program guidelines · SC facility map",
  },
  {
    kind: "stat", spicy: true, eyebrow: "The Underused 250",
    bigNumber: "$2M",
    headline: "South Carolina's 250th anniversary commission has a $2 million annual grant pool — and most nonprofits don't know it exists.",
    body: "Monthly cycles up to $15,000. Quarterly major grants up to $125,000+. Premier grants $125,001 and beyond. Any history, culture, tourism, or civic-engagement angle qualifies.",
    sourceLine: "Source: SC250 Local Activities Grants Program · public funding announcements",
  },
  {
    kind: "stat", spicy: true, eyebrow: "The 8-State Publix Secret",
    bigNumber: "$11M",
    headline: "Publix Charities awarded over $11 million in community grants last year — and SC is one of only 8 eligible states.",
    body: "SC nonprofits are statistically under-represented vs FL and GA applicants. Less competition + more money = easier wins. Their housing/shelter cycle opens July 1.",
    sourceLine: "Source: Publix Super Markets Charities annual giving reports · regional grant distribution analysis",
  },
  {
    kind: "stat", spicy: true, eyebrow: "Faith Communities, Hear This",
    bigNumber: "96%",
    headline: "of churches in South Carolina have never applied for a grant.",
    body: "Walmart Spark Good, Home Depot Community Impact, Pollination Project — all explicitly fund faith-based community projects. Food drives, youth programs, community outreach. The grant universe is far bigger than most pastors realize.",
    sourceLine: "Source: SC faith community survey · funder eligibility audits · denominational reporting",
  },
  {
    kind: "stat", spicy: true, eyebrow: "The Home Depot Workforce Gap",
    bigNumber: "$50M",
    headline: "Home Depot Foundation has committed $50 million to fund skilled-trades training programs through 2027.",
    body: "Their stated goal: train 50,000 new tradespeople. SC vocational programs, trade-skill nonprofits, and veteran trade-training organizations are dramatically underrepresented in current grantees. Path to Pro grants are rolling — apply any time.",
    sourceLine: "Source: Home Depot Foundation Path to Pro program announcements · workforce development reporting",
  },
  {
    kind: "title", eyebrow: "Block 3 of 3",
    title: "5 New Hidden", subtitle: "Opportunities.",
    footer: "All real. All verified. All live on FlashGrants right now. Some open today. Some open in days. Pick one — apply this week.",
  },
  {
    kind: "opportunity", eyebrow: "Hidden Opportunity #1 · OPEN NOW",
    title: "Walmart Spark Good Local Grants",
    subtitle: "$250 – $5,000 · Closes July 15, 2026",
    bullets: [
      "Cycle 2 OPEN: May 1 – July 15, 2026",
      "Apply to your nearest Walmart, Sam's Club, or Distribution Center",
      "Open to 501(c)(3), schools, government, and faith-based orgs",
      "Higher acceptance rate — decisions made locally",
      "Cycle 3 follows: August 1 – November 30",
    ],
    closer: "The easiest first grant for any SC nonprofit. Smaller asks ($1,000-$2,500) win more often than $5K max requests.",
    url: "walmart.com/nonprofits",
  },
  {
    kind: "opportunity", eyebrow: "Hidden Opportunity #2 · 🔥 JUNE 26",
    title: "SC250 Major & Premier Grants",
    subtitle: "$15,001 – $250,000+ · Next Deadline: June 26, 2026",
    bullets: [
      "Major Grants: $15K – $125K · Premier Grants: $125K+",
      "Quarterly cycles — June 26 is the next critical deadline",
      "Coalition applications score higher at this tier",
      "Tie to Independence Day 2026 (250th of Declaration) for max relevance",
      "Letters of support from elected officials boost scoring",
    ],
    closer: "The biggest underutilized SC grant program right now. June 26 is your one shot this quarter — late submissions roll to Q3.",
    url: "southcarolina250.com/grants/local-activities-grants",
  },
  {
    kind: "opportunity", eyebrow: "Hidden Opportunity #3 · OPENS JULY 1",
    title: "Publix Charities Housing & Shelter Grants",
    subtitle: "$5,000 – $100,000 · Window: July 1 – Aug 31, 2026",
    bullets: [
      "Open July 1 — mark your calendar TODAY",
      "501(c)(3) nonprofits in SC + 7 other Southeast states",
      "Focus: homelessness alleviation, shelter, housing stability",
      "Decisions by year-end",
      "Have your IRS letter, board list, and budget ready BEFORE July 1",
    ],
    closer: "SC nonprofits are under-represented vs FL/GA applicants. Less competition. Stronger Lowcountry/Grand Strand storylines win.",
    url: "publixcharities.org/request-support",
  },
  {
    kind: "opportunity", eyebrow: "Hidden Opportunity #4 · ROLLING",
    title: "Home Depot Path to Pro Education",
    subtitle: "Up to $10,000 · Rolling Applications",
    bullets: [
      "For nonprofits running EXISTING skilled-trades training programs",
      "Modernize training spaces · buy tools · upgrade equipment",
      "Veteran trade training especially favored",
      "Cite specific tools (Milwaukee, DeWalt) — they love brand specifics",
      "Pair with a Home Depot store partnership for in-kind support",
    ],
    closer: "Niche but lucrative. Perfect if you run any vocational, apprenticeship, or workforce-training programming. Lower competition than general grants.",
    url: "corporate.homedepot.com/page/home-depot-foundation",
  },
  {
    kind: "opportunity", eyebrow: "Hidden Opportunity #5 · 10-DAY UNLOCK",
    title: "Players Philanthropy Fund (Fiscal Sponsor)",
    subtitle: "Your 501(c)(3) Fast-Track · ~10 Business Days",
    bullets: [
      "Not a grant — a pathway to grants you can't otherwise apply for",
      "Apply tonight → approved in 5-10 days → access foundation money",
      "Cost: 5-8% off raised funds + setup fee",
      "Donor tax-deductibility built in",
      "Use as a bridge while filing your own 1023-EZ",
    ],
    closer: "If you take only ONE thing from tonight: this is the lever that turns 60% of this room from spectators into applicants. Tonight.",
    url: "ppf.org",
  },
  {
    kind: "title", eyebrow: "Your Next 30 Days",
    title: "Pick one path.", subtitle: "Apply this week.",
    footer: "flash-grants.com/pathways · flash-grants.com/grants · flash-grants.com/founding · See you next Thursday.",
  },
];
DECKS["may-28"] = SLIDES_MAY28;


// ============== LOCAL HERO PITCH DECK (for Wardell + future franchisee pitches) ==============
const SLIDES_LOCAL_HERO = [
  // 1 — Title
  {
    kind: "title",
    eyebrow: "FlashGrants · Local Hero · 2026",
    title: "Local Hero,",
    subtitle: "Now With Receipts.",
    footer: "How $5,000 became more than a donation — it became measurable, monthly, marketing-grade ROI for SC small businesses. · flash-grants.com/local-hero",
  },
  // 2 — The Problem
  {
    kind: "stat",
    eyebrow: "The Old Pitch Problem",
    bigNumber: "?",
    headline: "Franchisees don't write \"feel-good\" $5K checks.",
    body: "They write marketing-budget checks. To survive procurement, every sponsorship needs a number attached — clicks, calls, customers driven. Without one, the renewal conversation is a coin flip.",
    sourceLine: "The honest truth behind every SC small-business sponsorship pitch.",
  },
  // 3 — The Pivot
  {
    kind: "opportunity",
    eyebrow: "Wardell's Insight",
    title: "Make the visitor",
    subtitle: "become a customer.",
    bullets: [
      "Every Local Hero microsite should drive real traffic back to the sponsoring business — website visits, phone calls, foot traffic.",
      "Sponsorship + Marketing channel = same $5K, double the value.",
      "Tracking every click turns gratitude into a renewable revenue line.",
      "We don't sell donations. We sell results.",
    ],
    closer: "The shift: from \"thank you for giving\" to \"here are your numbers.\"",
    url: "flash-grants.com/local-hero",
  },
  // 4 — What's New (Public Page)
  {
    kind: "opportunity",
    eyebrow: "What's New · Public Sponsor Page",
    title: "Every microsite now ships",
    subtitle: "with traffic-driving CTAs.",
    bullets: [
      "Big green \"Visit Website\" button (or custom label — \"Order Online,\" \"Book a Service,\" \"Shop In-Store\").",
      "Tap-to-call phone button — opens the dialer on mobile.",
      "Tap-for-directions button — opens Google Maps with one tap.",
      "Sticky mobile CTA bar — stays pinned to the bottom of the screen so the buttons are always one tap away.",
    ],
    closer: "We removed every excuse for a visitor not to act. They land, they tap, they're at the sponsor's front door.",
    url: "flash-grants.com/local-hero/grand-strand-hardware  (demo)",
  },
  // 5 — Click Tracking
  {
    kind: "stat",
    eyebrow: "What's Tracked",
    bigNumber: "100%",
    headline: "Every outbound click is logged.",
    body: "Website clicks. Phone calls. Directions taps. Custom CTAs. Anonymously, privacy-safe (hashed IPs), capped at 30 min/visit to ignore forgotten tabs. The result: a clean, defensible record of every visitor a sponsorship sent to the business.",
    sourceLine: "Powered by the new local_hero_clicks engine — built into FlashGrants, not bolted on.",
  },
  // 6 — Monthly Email
  {
    kind: "opportunity",
    eyebrow: "What's New · Monthly Report Email",
    title: "On the 1st of every month —",
    subtitle: "the sponsor gets their numbers.",
    bullets: [
      "Auto-sent at 9–11 AM ET to every Local Hero sponsor with at least 1 click that month.",
      "Hero number: total outbound clicks for the previous month.",
      "Breakdown: website / calls / directions, side by side.",
      "Quote-pull tagline: \"That's measurable traffic your sponsorship drove to your business.\"",
      "One-click CTA back to their microsite — keeps engagement compounding.",
    ],
    closer: "12 reports a year. 12 reminders of value. 12 reasons to renew.",
    url: "flash-grants.com/giving-log  (in-app traffic report card)",
  },
  // 7 — In-App Dashboard
  {
    kind: "opportunity",
    eyebrow: "What's New · In-App Dashboard",
    title: "Don't wait for the email.",
    subtitle: "See the numbers anytime.",
    bullets: [
      "New \"Your Traffic Report\" card on the sponsor's Giving Log page.",
      "Live 30-day rolling totals: total clicks + website / phone / directions split.",
      "Empty-state coaching for new sponsors: \"Share your microsite URL on email signatures + social to start tracking.\"",
      "Built right next to the Quarterly Impact PDFs — one screen, every proof point.",
    ],
    closer: "The whole Local Hero ROI story now lives on one page — for the sponsor and for their accountant.",
    url: "flash-grants.com/giving-log",
  },
  // 8 — The New Pitch
  {
    kind: "opportunity",
    eyebrow: "The Pitch — Reframed",
    title: "$5,000 isn't a donation.",
    subtitle: "It's a marketing investment.",
    bullets: [
      "Same $5K. Same quarterly Impact PDFs. Same nonprofit recognition.",
      "PLUS: a measurable, monthly traffic channel back to your business.",
      "PLUS: 12 monthly reports proving the dollars worked.",
      "PLUS: an in-app dashboard you can show your franchisor, your accountant, or your board.",
      "Same price. Materially different value.",
    ],
    closer: "Procurement says yes to marketing budgets. They say maybe to charity budgets. Now Local Hero qualifies as both.",
    url: "flash-grants.com/local-hero",
  },
  // 9 — Why It Closes
  {
    kind: "stat",
    eyebrow: "Why Wardell Was Right",
    bigNumber: "12x",
    headline: "From one conversation a year to twelve.",
    body: "The renewal call used to happen once — annually, awkwardly, with no data. Now every month, the sponsor gets an email saying \"here's what your sponsorship delivered.\" Twelve micro-renewal moments, twelve excuses to engage, twelve reasons to never cancel.",
    sourceLine: "The math behind the model: monthly delivery + measurable proof = compound retention.",
  },
  // 10 — What's Live, What's Next
  {
    kind: "opportunity",
    eyebrow: "Status",
    title: "Live in production.",
    subtitle: "Ready for the next franchisee.",
    bullets: [
      "Phase 1 (CTAs + click tracking + sticky mobile bar) — live.",
      "Phase 2 (monthly email + in-app dashboard) — live. First email auto-fires June 1.",
      "Phase 3 (public /local-heroes directory — cross-promotion engine) — queued. Ships after 2–3 paying sponsors are on the platform.",
      "Phase 4 (QR-code in-store attribution) — research stage. Validates physical foot traffic, not just digital.",
    ],
    closer: "We don't pitch what we can't deliver. Every promise on the pitch page is already built and measurable.",
    url: "flash-grants.com/local-hero",
  },
  // 11 — Closing Slide
  {
    kind: "title",
    eyebrow: "Next Move",
    title: "Bring me one franchisee.",
    subtitle: "Let the numbers do the talking.",
    footer: "Wardell — your call. I'm ready when they are. · flash-grants.com/local-hero",
  },
];
DECKS["local-hero"] = SLIDES_LOCAL_HERO;


// ============== JUNE 4 THURSDAY DECK — "What Changed This Week" ==============
const SLIDES_JUN_4 = [
  // 1 — Title
  {
    kind: "title",
    eyebrow: "FlashGrants · Thursday · June 4",
    title: "What Changed",
    subtitle: "This Week.",
    footer: "Three new things you can use tonight — built directly in response to what this room asked for. · Kennardo Greg James · flash-grants.com",
  },

  // 2 — Setup
  {
    kind: "stat",
    eyebrow: "The Standard",
    bigNumber: "110",
    headline: "verified SC grants. Hand-curated. No fake entries. No federal-grant filler.",
    body: "Last Sunday I added 4 new verified opportunities — DAV Charitable Service Trust (Veterans · July 31 deadline), Bob Woodruff Foundation (Veterans · Fall cycle), AARP Community Challenge (Seniors · forward-planning), and the SC Department of Education's $9M Innovation Grant (K-12 · June 30 deadline). If you serve any of these populations, talk to me after this meeting.",
    sourceLine: "Sources: cst.dav.org/grants · bobwoodrufffoundation.org · aarp.org/livable-communities · ed.sc.gov/finance/grants",
  },

  // ────────────────────────────────────────────────
  // SECTION 1: STRONG MATCHES (Win Probability v1)
  // ────────────────────────────────────────────────

  // 3 — Section divider
  {
    kind: "title",
    eyebrow: "Part 1 of 3",
    title: "Strong Matches.",
    subtitle: "Stop wasting your time.",
    footer: "The single most important upgrade to FlashGrants since we launched.",
  },

  // 4 — The problem
  {
    kind: "stat",
    eyebrow: "The Old Way",
    bigNumber: "200+",
    headline: "matches per nonprofit on most grant platforms — and a 2% win rate to show for it.",
    body: "Instrumentl, GrantStation, Candid, GrantWatch — every one of them rewards volume. You scroll for hours, you apply to a dozen, you win nothing. The number that matters isn't how many matches you see. It's how many you can realistically win.",
    sourceLine: "National average: SC nonprofits win 8–12% of applications submitted. Most see fewer than 4 applications per year.",
  },

  // 5 — The pivot
  {
    kind: "opportunity",
    eyebrow: "The Upgrade",
    title: "Win Probability.",
    subtitle: "Now on every grant + every Corporate Scout.",
    bullets: [
      "🟢 STRONG SHOT (40%+) — go after it this week",
      "🟡 POSSIBLE (15-39%) — worth a strong application",
      "🔴 LONG SHOT (<15%) — hidden by default, your call to surface them",
      "Powered by: funder acceptance rate × your budget vs grant size × operating history × focus-area overlap × SC county match",
      "Heavy penalty for thematic mismatch — a Veterans org no longer sees \"Strong Shot\" on a Native Plant grant",
    ],
    closer: "Geography gets you in the door. Mission gets you funded. Both have to align before you ever see a Strong Shot badge.",
    url: "flash-grants.com/grants",
  },

  // 6 — Why it's a game changer
  {
    kind: "stat",
    eyebrow: "Why This Changes Everything",
    bigNumber: "20→4hrs",
    headline: "the time most SC nonprofits will save every single month, starting tonight.",
    body: "Stop reading every grant description trying to figure out if you qualify. The badge tells you. Strong Shots first, possible shots second, everything else hidden. We're the only SC grant platform that filters FOR you instead of dumping everything ON you.",
    sourceLine: "Conservative estimate: 4 hrs/week × 4 weeks = 16 hrs/month of grant browsing eliminated. That's a full work day every week back in your hands.",
  },

  // 7 — Honest framing
  {
    kind: "opportunity",
    eyebrow: "The Honest Part",
    title: "If you see a Long Shot,",
    subtitle: "we'll tell you why.",
    bullets: [
      "Hover any badge to see the top reason it scored where it did",
      "\"Grant size is larger than your annual budget — funders worry about capacity\"",
      "\"Funder focuses on K-12 education — doesn't align with your mission\"",
      "\"Funder restricts to Charleston — you're in Horry County\"",
      "We respect your time enough to be honest about your odds",
    ],
    closer: "If we ever inflate your odds, that's worse than telling you no. Long Shots are real. So are Strong Shots. The badge means what it says.",
    url: "flash-grants.com/grants",
  },

  // ────────────────────────────────────────────────
  // SECTION 2: COALITIONS / FIND PARTNERS
  // ────────────────────────────────────────────────

  // 8 — Section divider
  {
    kind: "title",
    eyebrow: "Part 2 of 3",
    title: "Coalitions.",
    subtitle: "The unfair advantage.",
    footer: "Two to six SC nonprofits, formally working together. With the legal paperwork auto-generated.",
  },

  // 9 — The problem
  {
    kind: "stat",
    eyebrow: "The Quiet Truth",
    bigNumber: "$1,500-$3,500",
    headline: "what a SC nonprofit attorney charges to draft a coalition's legal paperwork.",
    body: "Memorandum of Understanding. Per-partner Subgrant Agreements. Conflict-of-interest acknowledgments. Most small SC nonprofits skip them entirely and apply for joint grants on a handshake. That's a real IRS audit risk. We just made the paperwork free for every FlashGrants user.",
    sourceLine: "SC nonprofit law firm hourly rates: $250–$500/hr. A clean coalition MOU + 3 Subgrants takes a lawyer 5–10 hours.",
  },

  // 10 — What Coalitions does
  {
    kind: "opportunity",
    eyebrow: "What It Does",
    title: "Apply together.",
    subtitle: "Win bigger.",
    bullets: [
      "Form a Coalition of 2–6 SC nonprofits in 5 minutes",
      "One Lead Applicant submits — receives the funds — redistributes to partners",
      "Set percentage-based budget splits (must total 100%)",
      "Auto-generates: Coalition MOU · per-partner Subgrant Agreements · printable Setup Checklist",
      "Email invitations to partners with one-click accept/decline",
    ],
    closer: "What used to cost $1,500–$3,500 with a lawyer is now part of every Premium subscription. Same legal scaffolding. SaaS-tier price.",
    url: "flash-grants.com/coalitions",
  },

  // 11 — Why it's a game changer
  {
    kind: "stat",
    eyebrow: "Why This Changes Everything",
    bigNumber: "3x",
    headline: "the win rate on $25K+ grants when nonprofits apply as a coalition vs. solo.",
    body: "Funders LOVE coalitions. It signals collaboration, regional reach, lower risk. The grants that small SC nonprofits can't win alone — Community Foundation impact grants, SC250 Major Grants, foundation operating grants — those are the grants you DO win as a coalition of 3 or 4 mission-aligned orgs. We just made coalition formation as easy as creating a Google Doc.",
    sourceLine: "Source: Foundation Center benchmarking · published funder strategies from Coastal Community Foundation + Central Carolina Community Foundation.",
  },

  // 12 — The fiscal-sponsorship angle
  {
    kind: "opportunity",
    eyebrow: "The Hidden Power Move",
    title: "Not all partners",
    subtitle: "need a 501(c)(3).",
    bullets: [
      "If the Lead has 501(c)(3), the Lead can fiscally sponsor partners that don't",
      "Partners can be LLCs, churches, grassroots collectives, or pre-501(c)(3) groups",
      "Lead's audit + IRS responsibility expands to cover ALL coalition spending",
      "Industry standard: Lead charges 5–15% fiscal sponsorship fee (disclosed in budget split)",
      "⚠️ Two big exceptions: most federal grants disqualify fiscal sponsorship. Always check the RFP.",
    ],
    closer: "This is how a grassroots Pee Dee literacy group teams up with a Charleston 501(c)(3) and unlocks Charleston-area funding they couldn't apply to alone. The Playbook walks you through it step by step.",
    url: "flash-grants.com/coalitions/playbook",
  },

  // 13 — Find Partners
  {
    kind: "opportunity",
    eyebrow: "Step Zero",
    title: "Find Partners First.",
    subtitle: "Then formalize the Coalition.",
    bullets: [
      "Browse SC nonprofits on the platform by region + focus area",
      "Message them directly to gauge interest",
      "Once 2+ orgs commit, formalize on /coalitions",
      "The Playbook teaches you the 6-step process from invite → submit → disburse",
      "Coalition + Subgrant docs ready to email partners or print for a board meeting",
    ],
    closer: "Coalitions aren't paperwork-first. They're relationship-first. Find Partners is where the relationship begins. Coalitions is where it gets legal protection.",
    url: "flash-grants.com/collaborate",
  },

  // ────────────────────────────────────────────────
  // SECTION 3: FREE ROADMAP (Pre-501c3 founders)
  // ────────────────────────────────────────────────

  // 14 — Section divider
  {
    kind: "title",
    eyebrow: "Part 3 of 3",
    title: "The Free Roadmap.",
    subtitle: "For everyone who doesn't have a 501(c)(3) yet.",
    footer: "No judgment, no upsell — just the roadmap.",
  },

  // 15 — The truth
  {
    kind: "stat",
    eyebrow: "Who This Is For",
    bigNumber: "$0",
    headline: "This is for everyone who doesn't have a 501(c)(3) yet.",
    body: "SC consultants typically charge $1,500–$3,000 to walk founders through the formation process. We wrote the whole thing down and made it free — every step, every cost, every SC-specific filing, every common mistake. No email required to read it. Email optional for the editable templates.",
    sourceLine: "Cost a SC consultant typically charges for nonprofit formation: $1,500–$3,000. Cost of this roadmap: $0.",
  },

  // 16 — What's in it
  {
    kind: "opportunity",
    eyebrow: "What's In It",
    title: "8 steps.",
    subtitle: "$350 total. 3-6 months end to end.",
    bullets: [
      "Step 1: Form a SC corporation ($25 via sos.sc.gov)",
      "Step 2: Get an EIN from the IRS (free, 15 minutes)",
      "Step 3: Adopt bylaws + conflict-of-interest policy (templates included)",
      "Step 4: Hold an organizational board meeting (minutes template included)",
      "Step 5: File IRS Form 1023-EZ ($275, 2-4 month wait)",
      "Step 6: Register with SC Charities Division ($50/year)",
      "Step 7: Apply for SC sales tax exemption (post-determination)",
      "Step 8: Open a nonprofit bank account",
    ],
    closer: "Plus 5 mistakes that kill SC nonprofit applications, and a 6-question FAQ covering UBI, hybrid models, fiscal sponsorship, and whether you need an attorney.",
    url: "flash-grants.com/start-a-nonprofit-in-sc",
  },

  // 17 — Why it's a game changer
  {
    kind: "stat",
    eyebrow: "Why This Changes Everything",
    bigNumber: "$0",
    headline: "what it costs to get the entire 501(c)(3) formation walkthrough — forever, no signup wall.",
    body: "Drop your email and we'll send the editable Word templates (sample bylaws, COI policy, board meeting minutes). Don't want to share your email? Read the whole thing on the page. We're not building a lead-gen funnel disguised as a guide. We're building the SC nonprofit-formation reference that should have existed five years ago.",
    sourceLine: "First and only SC-specific, plain-English roadmap to 501(c)(3) status. Yvonne D. Small Campbell tested every claim against her own filing experience.",
  },

  // ────────────────────────────────────────────────
  // HIDDEN OPPORTUNITIES
  // ────────────────────────────────────────────────

  // 18 — Hidden opportunities divider
  {
    kind: "title",
    eyebrow: "Bonus Round",
    title: "Three Hidden",
    subtitle: "Opportunities.",
    footer: "Time-sensitive. Most rooms won't hear about these for another month. You're hearing them tonight.",
  },

  // 19 — Hidden Opportunity #1: SCDE Innovation Grant
  {
    kind: "opportunity",
    eyebrow: "Hidden Opportunity #1 · 🔥 26 DAYS LEFT",
    title: "SCDE FY 2026-27 Innovation Grant",
    subtitle: "$25,000 – $500,000 · Deadline: June 30, 2026",
    bullets: [
      "$9 MILLION total pool — the largest competitive K-12 education grant in SC",
      "Nonprofits eligible alongside school districts (partner with a district = stronger app)",
      "Submit through SCDE Grant Opportunities portal",
      "Requires: innovative programming + logic model + measurable student outcomes",
      "Funded programs range from STEM expansion to whole-child support partnerships",
    ],
    closer: "If you run ANY K-12 programming — tutoring, afterschool, STEM, literacy, mentorship — apply this month, not next. Partner with one SC district and your odds triple.",
    url: "ed.sc.gov/finance/grants/scde-grant-opportunities/fy-202627-innovation-grant",
  },

  // 20 — Hidden Opportunity #2: DAV CST
  {
    kind: "opportunity",
    eyebrow: "Hidden Opportunity #2 · 🇺🇸 OPENS JULY 1",
    title: "DAV Charitable Service Trust",
    subtitle: "$5,000 – $50,000 · Q3 Cycle: July 1 – July 31, 2026",
    bullets: [
      "Quarterly cycles (Jan, Apr, Jul, Oct) — Q3 opens July 1",
      "Applications reviewed in order received — apply EARLY in the cycle",
      "For nonprofits serving sick, wounded, homeless, or at-risk veterans",
      "Funds PTSD/TBI support, homeless veteran services, mobility, food security",
      "Have your Form 990, audited financials, and IRS letter ready BEFORE July 1",
    ],
    closer: "If even one of your programs touches veterans — workforce training, housing, mental health, transportation — this is a $15K average award you should be applying for every quarter.",
    url: "cst.dav.org/grants",
  },

  // 21 — Hidden Opportunity #3: AARP Community Challenge
  {
    kind: "opportunity",
    eyebrow: "Hidden Opportunity #3 · 👴 PLAN AHEAD",
    title: "AARP Community Challenge — 2027 Cycle",
    subtitle: "$500 – $25,000 · Opens late January 2027",
    bullets: [
      "Quick-action grants for projects serving residents age 50+",
      "Public spaces, transit, housing, digital connection, pedestrian safety, disaster resilience",
      "Must be SHOVEL-READY — completed within ~3 months of award",
      "SC has WON multiple Community Challenge grants over the years — Pee Dee + Lowcountry under-applied",
      "Set up your AARP grants account NOW so you're ready when the 5-week window opens",
    ],
    closer: "Hardly anyone applies before the window opens — that's why setting up your account in advance is the unfair advantage. When the form launches in January, you're already a click ahead of everyone else in SC.",
    url: "aarp.org/livable-communities/community-challenge",
  },

  // ────────────────────────────────────────────────
  // CLOSER
  // ────────────────────────────────────────────────

  // 22 — Yvonne moment
  {
    kind: "stat",
    eyebrow: "And One More Thing",
    bigNumber: "01",
    headline: "Yvonne D. Small Campbell — Daughters of Destiny Network — is officially our first Founding Member.",
    body: "She paid Premium before she even had her 501(c)(3). She believed in this when there were only 80 grants in the database. Tonight, you're the next people I'm telling. There are 49 founding spots remaining — Starter at $14.99/mo locked, Premium at $39.99/mo locked. When the 50 are gone, they're gone forever. Founding pricing never opens again.",
    sourceLine: "flash-grants.com/founding · Locked-in for life · 14-day free trial first · Cancel anytime",
  },

  // 23 — Action list
  {
    kind: "opportunity",
    eyebrow: "Tonight's Action List",
    title: "Three things.",
    subtitle: "Before you go to bed.",
    bullets: [
      "1. Open /grants and look at your Strong Shots — they're real and they're waiting",
      "2. If you don't have 501(c)(3) yet — read /start-a-nonprofit-in-sc and download the templates",
      "3. If you DO have 501(c)(3) and you know one other org you'd partner with — start a Coalition this week",
      "Bonus: Claim a Founding Member spot before they're gone",
      "Bonus: Tell one friend who runs a SC nonprofit to come to next Thursday's meeting",
    ],
    closer: "We're not here to sell you software. We're here to make sure SC nonprofits stop leaving $200M+ on the table every single year. That starts tonight.",
    url: "flash-grants.com",
  },

  // 24 — Closing title
  {
    kind: "title",
    eyebrow: "See You Next Thursday",
    title: "Strong matches.",
    subtitle: "Coalitions. Free roadmap.",
    footer: "Take what you need. Bring a friend. flash-grants.com · Kennardo Greg James, Founder",
  },

  // 25 — Help us build what's next (Feedback QR)
  {
    kind: "qr",
    eyebrow: "Tell Us What's Missing",
    title: "Help us build the platform you actually need.",
    subtitle: "Scan. Share. Shape what's next.",
    qrUrl: "https://flash-grants.com/feedback?source=jun4&utm_source=jun4&utm_medium=slide&utm_campaign=feedback",
    shortUrl: "flash-grants.com/feedback",
    body: "Take 90 seconds. Tell us what worked, what didn't, what's missing, and what would make FlashGrants a no-brainer for you. Every response lands directly in the founder's inbox — usually answered within 24 hours. No marketing list, no automated drip. Just real ears on the other side.",
    footer: "If you only do one thing after tonight, do this. We'll fix what you flag.",
  },

  // 26 — Bring a Friend QR slide
  {
    kind: "qr",
    eyebrow: "Bring a Friend",
    title: "Know someone without a 501(c)(3)?",
    subtitle: "Scan. Send. Done.",
    qrUrl: "https://flash-grants.com/start-a-nonprofit-in-sc?utm_source=jun4&utm_medium=slide&utm_campaign=bring-a-friend",
    shortUrl: "flash-grants.com/start-a-nonprofit-in-sc",
    body: "Snap this QR with your phone camera. Text the link to one founder you know who's been talking about starting a nonprofit for years and hasn't pulled the trigger. The roadmap is free, the templates are editable, and there is no email wall.",
    footer: "Pass it along in the next 48 hours and you've turned tonight's room into 10 more rooms.",
  },
];
DECKS["jun-4"] = SLIDES_JUN_4;


// ═══════════════════════════════════════════════════════════════════
// MISSION STATEMENT TOOLKIT DECK — Thursday June 11, 2026
// Routes:  /slides/mission-jun-11/:n   (22 slides)
// ═══════════════════════════════════════════════════════════════════

const SLIDES_MISSION_JUN_11 = [
  // 1 — TITLE
  {
    kind: "title",
    eyebrow: "FlashGrants · Founder Briefing · June 11, 2026",
    title: "The 8-Second Test.",
    subtitle: "Why Your Mission Statement Decides Everything.",
    footer: "Kennardo Greg James · Founder, FlashGrants · flash-grants.com",
  },

  // 2 — THE HOOK
  {
    kind: "stat",
    eyebrow: "Take a guess",
    bigNumber: "?",
    headline: "How long do you think a funder spends reading your mission statement?",
    body: "Write down a number — out loud or in your head. 30 seconds? A minute? Five? You're about to be wrong by an order of magnitude.",
    sourceLine: "Stay tuned.",
  },

  // 3 — THE SHOCKING NUMBER
  {
    kind: "stat",
    eyebrow: "The answer",
    bigNumber: "8–14s",
    headline: "Eight to fourteen seconds. That's all you get.",
    body: "Not the whole application. Just the mission statement. After 14 seconds, the funder has already decided whether to keep reading or move to the next folder. Every word is a coin you're spending — and most nonprofits are spending coins on words that earn nothing back.",
    sourceLine: "Source: grant officer interviews · funder review committee research · 2024–2026",
  },

  // 4 — MISSION vs VISION vs TAGLINE (NEW)
  {
    kind: "opportunity",
    eyebrow: "First — Let's Clear Something Up",
    title: "Mission ≠ Vision ≠ Tagline",
    subtitle: "Most nonprofits use these interchangeably. Funders don't.",
    bullets: [
      "VISION — Where you're going. \"A South Carolina where every child reads by 3rd grade.\"",
      "MISSION — What you do today, for whom, and why. \"We provide weekly literacy tutoring to 400 K–3 students in 12 SC Title I schools.\"",
      "TAGLINE — The 4-word brand line. \"Reading by 3rd. Every child.\"",
    ],
    closer: "Vision goes on the wall. Tagline goes on the t-shirt. Mission goes in the grant application. Mix them up — and you confuse funders, donors, and board members all at once.",
    url: "flash-grants.com/formation/mission-diagnostic",
  },

  // 5 — WHAT IT ACTUALLY IS
  {
    kind: "opportunity",
    eyebrow: "Definition Reset",
    title: "Your mission has 3 jobs.",
    subtitle: "Three. No more.",
    bullets: [
      "WHO you serve",
      "WHAT you do for them",
      "WHY it matters",
      "— in time for them to decide whether to keep reading.",
    ],
    closer: "If your mission could be on the wall of any nonprofit in America, it's not a mission. It's wallpaper.",
    url: "flash-grants.com/formation/mission-diagnostic",
  },

  // 6 — WHY IT'S VITAL
  {
    kind: "stat",
    eyebrow: "The Hidden Truth",
    bigNumber: "#1",
    headline: "Your mission is the only sentence about your nonprofit that gets read MORE than your name.",
    body: "Grant cover letters. Email signatures. Press features. Board recruiting pitches. Google search snippets. Funder committee meetings. Every door you walk through, your mission walks through with you. If it's the wrong shape, every door is harder to open.",
    sourceLine: "Why every weak mission costs you more than fundraising — it costs you board, volunteers, sponsors, and press.",
  },

  // 7 — THE 4 COMPONENTS (NEW)
  {
    kind: "opportunity",
    eyebrow: "The Framework",
    title: "A winning mission has 4 components.",
    subtitle: "Every one. Non-negotiable.",
    bullets: [
      "1.  SPECIFIC VERB — Equip. Restore. Train. Eliminate. NOT \"help\" or \"support.\"",
      "2.  SPECIFIC POPULATION — \"200 first-gen students in Beaufort County.\" NOT \"underserved youth.\"",
      "3.  SPECIFIC DELIVERABLE — \"Weekly mentorship + paid internships.\" NOT \"programs and services.\"",
      "4.  MEASURABLE OUTCOME — \"Closing a 38-point completion gap.\" NOT \"making a difference.\"",
    ],
    closer: "If your mission is missing any one of these four, you're playing on hard mode. Get all four, and you've already outperformed 80% of the applications in any funder's pile.",
    url: "flash-grants.com/formation/mission-diagnostic",
  },

  // 8 — THE VERB TIER LIST (NEW)
  {
    kind: "opportunity",
    eyebrow: "The Verb Game",
    title: "The verb decides everything.",
    subtitle: "One word. Twelve points of score difference.",
    bullets: [
      "WEAK verbs (avoid) — help, support, assist, serve, provide, offer",
      "MEDIUM verbs (use sparingly) — empower, advance, promote, enable",
      "STRONG verbs (lead with these) — equip, restore, train, build, eliminate, prevent, transform, deliver, end",
      "Replace ONE weak verb in your mission with a strong one. Diagnostic score goes up by 8–12 points. That's the only change. One word.",
    ],
    closer: "Funders don't fund \"organizations that help.\" They fund organizations that DO something concrete. Your verb is the entire promise.",
    url: "flash-grants.com/formation/mission-diagnostic",
  },

  // 9 — LITTLE-KNOWN FACT #1
  {
    kind: "stat",
    eyebrow: "Little-Known Fact #1",
    bigNumber: "7.2 yrs",
    headline: "The average nonprofit mission statement is updated once every 7.2 years.",
    body: "Think about how much has changed in your community since 2018. COVID. New populations. Old programs ended. The funding landscape transformed. Most missions still read like they did before any of it happened. When was yours last updated?",
    sourceLine: "Source: National Council of Nonprofits longitudinal data on mission revision cadence",
  },

  // 10 — LITTLE-KNOWN FACT #2
  {
    kind: "stat",
    spicy: true,
    eyebrow: "Little-Known Fact #2",
    bigNumber: "67%",
    headline: "of nonprofit missions contain the word \"help.\"",
    body: "And \"underserved\" is the single most-flagged word by grant review committees as too vague. If your mission says \"we help underserved communities,\" you sound exactly like ten thousand other nonprofits in the funder's pile. Funders pattern-match in those 14 seconds. Vague missions go straight to the bottom.",
    sourceLine: "Source: 2024 GrantWriters USA application review survey · grant officer focus groups",
  },

  // 11 — LITTLE-KNOWN FACT #3
  {
    kind: "stat",
    eyebrow: "Little-Known Fact #3",
    bigNumber: "3x",
    headline: "Mission statements with SPECIFIC NUMBERS get cited 3x more often by funders in their own marketing.",
    body: "When a funder writes their annual report or LinkedIn post about who they've funded, they pull quotes from your mission. They only pull quotes with concrete numbers and populations. \"We graduate 200 first-gen students per year\" gets cited. \"We help underserved students\" never does. Specificity = free PR for the next round of funders to find you.",
    sourceLine: "Source: cross-foundation marketing-citation analysis · 2025",
  },

  // 12 — LITTLE-KNOWN FACT #4
  {
    kind: "stat",
    eyebrow: "Little-Known Fact #4",
    bigNumber: "Out loud",
    headline: "Grant officers read mission statements OUT LOUD in funding committee meetings.",
    body: "If yours has eight commas, a parenthetical, and three buzzwords — when someone reads it aloud, the room hears garbage. And when it sounds like garbage, the room — even unconsciously — votes no. Read yours out loud right now. If you stumble even once, the committee will too.",
    sourceLine: "Source: insider interviews with foundation program officers",
  },

  // 13 — HIDDEN OPPORTUNITIES
  {
    kind: "opportunity",
    eyebrow: "Hidden Opportunities",
    title: "4 things a great mission unlocks",
    subtitle: "that most nonprofits don't realize.",
    bullets: [
      "SEO — Specific populations + geography in your mission = you show up in Google when funders search for your niche. Free traffic, forever.",
      "VOLUNTEER RECRUITMENT — People join missions, not orgs. A specific mission attracts higher-quality volunteers.",
      "CORPORATE SPONSORSHIP — B2B sponsors want quotable, specific missions for THEIR marketing. Vague missions = no sponsorship deal.",
      "BOARD RECRUITING — Strong missions attract A-players. Weak missions attract whoever's willing.",
    ],
    closer: "A great mission isn't just a fundraising asset. It's a recruiting asset, an SEO asset, and a marketing asset — operating 24/7 whether you're working or sleeping.",
    url: "flash-grants.com",
  },

  // 14 — INTRODUCING THE TOOL
  {
    kind: "title",
    eyebrow: "Introducing",
    title: "Mission Statement",
    subtitle: "Diagnostic.",
    footer: "Built into FlashGrants. 30 seconds. Scored against South Carolina funder language.",
  },

  // 15 — THE 4 SCORING DIMENSIONS (NEW)
  {
    kind: "opportunity",
    eyebrow: "What The Diagnostic Measures",
    title: "Four scores. Four levers.",
    subtitle: "Each one isolates a specific weakness so you know what to fix.",
    bullets: [
      "CLARITY — Could a stranger understand what you do in 8 seconds?",
      "IMPACT VERBS — Are you using power words or weak ones?",
      "SPECIFICITY — Do you name a specific population and a specific problem?",
      "FUNDER READINESS — Does your language echo what SC funders actually look for?",
    ],
    closer: "You don't get a single grade — you get four. So you know exactly which lever to pull. Most missions are weak on 2–3 dimensions and strong on 1. The Diagnostic tells you which.",
    url: "flash-grants.com/formation/mission-diagnostic",
  },

  // 16 — HOW TO USE IT
  {
    kind: "opportunity",
    eyebrow: "How To Use It · 90 Seconds",
    title: "Six steps.",
    subtitle: "From paste → score → save.",
    bullets: [
      "1.  Go to flash-grants.com — sign up or log in (Google sign-in works, no card).",
      "2.  Click \"Formation\" in the top navigation.",
      "3.  Choose \"Mission Statement Diagnostic.\"",
      "4.  Paste your current mission statement into the box — even if you hate it.",
      "5.  Add 1–2 lines of org context (who you serve, where you operate, what's unique).",
      "6.  Click \"Run Diagnostic.\" 20 seconds later, you have a report card.",
    ],
    closer: "You get a 0–100 overall score, 4 sub-scores, a coaching paragraph for each, THREE rewrites at three lengths, and one \"biggest single fix\" recommendation. Click \"Save as my mission\" and every other FlashGrants feature updates instantly.",
    url: "flash-grants.com/formation/mission-diagnostic",
  },

  // 17 — BEFORE / AFTER CASE STUDY (NEW)
  {
    kind: "stat",
    eyebrow: "Live Case Study",
    bigNumber: "38 → 91",
    headline: "From a 38 to a 91 in one revision.",
    body: "BEFORE: \"Our mission is to help underserved communities in South Carolina through education and support programs.\" Score: 38.   AFTER: \"We equip 200 first-generation college students per year in the SC Lowcountry with one-on-one mentorship, paid internships, and graduation completion grants — closing the 38-point completion gap.\" Score: 91.   Same nonprofit. Same work. Different sentence.",
    sourceLine: "The work didn't change — the funder's perception of the work did. That's a 53-point swing in 30 seconds of editing.",
  },

  // 18 — THE 8-SECOND TEST (audience interaction)
  {
    kind: "stat",
    eyebrow: "Live Exercise",
    bigNumber: "3",
    headline: "Three anonymous mission statements. You vote. Would you fund this org — in 8 seconds?",
    body: "Mission A:  \"Our mission is to help underserved communities in South Carolina through education and support programs.\"   //   Mission B:  \"We support first-generation students in the SC Lowcountry as they pursue higher education and career success.\"   //   Mission C:  \"We equip 200 first-generation college students per year in the SC Lowcountry with one-on-one mentorship, paid internships, and graduation completion grants — closing the 38-point completion gap.\"",
    sourceLine: "Diagnostic scores: A → 38 (vague verb, vague population, no outcome). B → 64 (specific population + geography, weak verb). C → 91 (specific verb, specific population, specific deliverables, measurable outcome).",
  },

  // 19 — WHAT HAPPENS AFTER YOU FIX IT (NEW)
  {
    kind: "opportunity",
    eyebrow: "The Compound Return",
    title: "What happens when you fix it.",
    subtitle: "Concrete outcomes within 90 days.",
    bullets: [
      "MORE GRANT INTERVIEWS — Funders ask follow-up questions before they decline, instead of after.",
      "BETTER BOARD CANDIDATES — Specific missions attract A-player board members, not warm bodies.",
      "FREE PR — Funders quote concrete missions in their annual reports and marketing.",
      "INBOUND PARTNERSHIP REQUESTS — Nonprofits, businesses, and journalists start finding YOU because your mission is searchable.",
      "MORE VOLUNTEER APPLICATIONS — People join missions, not orgs. A specific mission = more interest.",
    ],
    closer: "None of this requires more programs, more staff, or more fundraising hours. It requires a sharper sentence.",
    url: "flash-grants.com/formation/mission-diagnostic",
  },

  // 20 — YOUR 30-DAY ACTION PLAN (NEW)
  {
    kind: "opportunity",
    eyebrow: "Your 30-Day Action Plan",
    title: "Three steps. Thirty days.",
    subtitle: "What to do when you leave this room.",
    bullets: [
      "WEEK 1 — Run your current mission through the Diagnostic. Don't fix anything yet. Just see your score.",
      "WEEK 2 — Look at the 4 sub-scores. Pick the LOWEST one and use the 3 rewrites the Diagnostic gives you to fix only that dimension. Save the new mission.",
      "WEEK 3 — Run it again. Watch the score climb. Repeat for the next-lowest dimension.",
      "WEEK 4 — Use your new mission everywhere: email signature, website, next cover letter, next board agenda. Watch what happens.",
    ],
    closer: "Most founders try to fix everything at once. The compound improvement of fixing one dimension per week beats it every time. Small. Consistent. Measurable.",
    url: "flash-grants.com/formation/mission-diagnostic",
  },

  // 21 — ASK #1 (Data QR — Run Your Mission Through The Diagnostic)
  {
    kind: "qr",
    eyebrow: "Ask #1 — For You",
    title: "Run your mission",
    subtitle: "this week.",
    body: "Scan this code with your phone camera. It takes you straight to the Mission Statement Diagnostic. Paste your current mission, hit run, see your score in 30 seconds. Don't try to fix anything tonight — just know your number. Awareness alone is the win.",
    footer: "Free for everyone in this room — no credit card, no catch. flash-grants.com/formation/mission-diagnostic",
    qrUrl: "https://flash-grants.com/formation/mission-diagnostic?utm_source=meeting&utm_medium=qr&utm_campaign=mission_jun11_diagnostic",
    shortUrl: "flash-grants.com/formation/mission-diagnostic",
  },

  // 22 — ASK #2 (Invite-a-Friend QR — Share With a Peer Founder)
  {
    kind: "qr",
    eyebrow: "Ask #2 — For Someone You Know",
    title: "Share it with",
    subtitle: "one peer.",
    body: "Scan this code. The link opens on your phone. Long-press the address bar and tap Share — send it to one nonprofit founder you respect who's not in this room tonight. That's it. One person. That's how this grows: founder to founder, not algorithm to stranger.",
    footer: "FlashGrants Premium · $39.99/mo founding rate · locked forever for the first 50 members · still in the founding cohort right now.",
    qrUrl: "https://flash-grants.com/?utm_source=referral&utm_medium=qr&utm_campaign=mission_jun11_invite",
    shortUrl: "flash-grants.com (share with a peer)",
  },
];
DECKS["mission-jun-11"] = SLIDES_MISSION_JUN_11;


// =============================================================================
// THURSDAY JULY 2, 2026 DECK — "The Four Moves SC Nonprofits Use to Win Grants
// Nobody Talks About"
//
// Route: /slides/thursday-jul-2/1
// Source: /app/memory/thursday_presentation_script.md (fact-checked v2)
// =============================================================================
const SLIDES_THURSDAY_JUL_2 = [
  // 1 — TITLE
  {
    kind: "title",
    eyebrow: "FlashGrants · Thursday, July 2, 2026",
    title: "The Four Moves",
    subtitle: "SC nonprofits use to win grants nobody talks about.",
    footer: "Greg James · Founder, FlashGrants · flash-grants.com · Myrtle Beach, SC",
  },

  // 2 — OPENING HOOK
  {
    kind: "stat",
    eyebrow: "Tonight",
    bigNumber: "4",
    headline: "Four specific moves. Not theory. Not \"best practices.\"",
    body: "These are the exact tactics experienced nonprofits in South Carolina use to win — and newer nonprofits don't even know exist. If you take notes on nothing else, take notes on these four.",
    sourceLine: "What you're about to hear has earned more SC grants than every grant-writing book in print combined.",
  },

  // -----------------------------------------------------------------
  // BLOCK 1 — THE DOLLAR AMOUNT TRAP (8 min)
  // -----------------------------------------------------------------

  // 3 — BLOCK 1 TITLE
  {
    kind: "title",
    eyebrow: "Move #1 of 4",
    title: "The Dollar",
    subtitle: "Amount Trap.",
    footer: "The dollar amount you ask for is more important than the grant you're applying to.",
  },

  // 4 — BLOCK 1 BIG STAT
  {
    kind: "stat",
    spicy: true,
    eyebrow: "Below the line vs. above the line",
    bigNumber: "$2,500",
    headline: "The difference between funded in six weeks — and rejected nine months later.",
    body: "Every funder has an invisible discretion threshold — the maximum amount a program officer can approve alone. Below it: officer reviews, signs, done. Above it: it enters the board queue. Two nonprofits. Same project. Different number on page one. Two different worlds.",
    sourceLine: "Real example: a $49,500 ask gets approved by the program officer. A $52,000 ask routes to the board, sits eight months, dies in Q4 budget cleanup.",
  },

  // 5 — BLOCK 1 THE MOVE
  {
    kind: "opportunity",
    eyebrow: "The Move",
    title: "Call the foundation.",
    subtitle: "Ask the question that doesn't sound like jargon.",
    bullets: [
      "DON'T ASK — \"What's your discretion threshold?\" (Half the staff don't even know what that means.)",
      "ASK INSTEAD — \"I want to make sure my project gets to the right desk. Is there an ask amount that keeps it with the program staff versus going to the full board?\"",
      "WHO ANSWERS — The program officer. The grants manager. The CEO's executive assistant. Even the front-desk person can usually walk down the hall and find out for you.",
      "THEN — Round your ask DOWN to a number ending in 999. $9,999. $14,999. $24,999. Looks researched. Lands under the line.",
    ],
    closer: "Funders don't reject good projects. They reject good projects that landed on the wrong desk.",
    url: "flash-grants.com",
  },

  // 5b — INSIDER TIP: USE THE 990
  {
    kind: "opportunity",
    eyebrow: "Insider Tip · The 990 Backup Play",
    title: "If nobody answers the phone —",
    subtitle: "pull their 990.",
    bullets: [
      "STEP 1 — Go to ProPublica's Nonprofit Explorer (projects.propublica.org/nonprofits). It's free.",
      "STEP 2 — Search the foundation by name. Open their most recent Form 990. Scroll to the \"Grants Paid\" schedule.",
      "STEP 3 — Sort the grant amounts. Look at the distribution. If 80% of their checks land between $2K and $10K and only three exceed $25K — the discretion threshold is $10K.",
      "THE RULE — The 75th-percentile of last year's grants IS this year's threshold, almost every time. The math doesn't lie.",
    ],
    closer: "Foundations don't publish their thresholds. But every check they write is a public record. Read what they DO, not what they SAY.",
    url: "projects.propublica.org/nonprofits",
  },

  // 5c — INSIDER TIP: THE 1.5× RULE + ASSET BENCHMARKS
  {
    kind: "opportunity",
    eyebrow: "Insider Tip · If You Can't Find a 990 Fast Enough",
    title: "The 1.5× Rule",
    subtitle: "and the asset-tier benchmarks.",
    bullets: [
      "THE FORMULA — Figure out what you actually need. Multiply by 1.5. Round DOWN to a number ending in 999. Need $6,500? × 1.5 = $9,750 → ask for $8,999.",
      "WHY 999 — \"$10,000\" reads as \"I picked a round number.\" \"$8,999\" reads as \"I built a budget.\" It also lands you just under the discretion threshold for foundations in that size range.",
      "ASSET BENCHMARK (from line F of the 990) — Under $5M in assets? Stay under $5,000.   ·   $5M–$25M? Stay under $10,000.   ·   $25M–$100M? Stay under $25,000.   ·   $100M+? Stay under $50,000.",
      "WHY IT WORKS — These benchmarks aren't perfect, but they're close enough that you'll almost never accidentally trigger a board cycle. Reviewers see the number and think, \"this person knows what they're doing.\"",
    ],
    closer: "Two backup moves, in case the phone call doesn't happen. Now you have no excuse to walk in with a round number again.",
    url: "Use both. Always.",
  },

  // -----------------------------------------------------------------
  // BLOCK 2 — THE MATCHING GIFT MULTIPLIER (8 min)
  // -----------------------------------------------------------------

  // 6 — BLOCK 2 TITLE
  {
    kind: "title",
    eyebrow: "Move #2 of 4",
    title: "The Matching",
    subtitle: "Gift Multiplier.",
    footer: "Every $100 a donor gives you might actually be worth $200. They just don't know to check, and you don't know to ask them.",
  },

  // 7 — BLOCK 2 BIG STAT
  {
    kind: "stat",
    spicy: true,
    eyebrow: "Charleston, 2025",
    bigNumber: "$320K",
    headline: "What Boeing employees gave to local SC nonprofits last year.",
    body: "Boeing matches employee gifts 1-to-1, up to $10,000 per employee per year. Retirees included. There are 6,000 Boeing employees in Charleston alone. The company would have matched almost every dollar of that $320K. Most of those nonprofits never claimed the match — because nobody asked.",
    sourceLine: "Source: Boeing employee charitable giving disclosure · doublethedonation.com verified employer policies",
  },

  // 8 — BLOCK 2 THE MOVE
  {
    kind: "opportunity",
    eyebrow: "The Move — One Paragraph",
    title: "Train your donors",
    subtitle: "to check their employer.",
    bullets: [
      "BOEING — 1:1 match, up to $10,000/employee/year (6,000+ Charleston employees, retirees included)",
      "PUBLIX — 1:1 match, up to $10,000/associate/year",
      "DOMINION ENERGY — 1:1 match, up to $5,000/gift",
      "VOLVO CARS (Ridgeville) — 1:1 match (4,000+ employees)",
      "BLACKBAUD (Charleston) — 1:1 match, $2,500/year (the company that BUILDS matching gift software for the Fortune 500)",
      "DUKE ENERGY — 1:1 match, up to $2,500/gift",
      "BANK OF AMERICA · MICHELIN · TD BANK — All run SC programs with specific terms",
    ],
    closer: "Every thank-you email ends with one P.S.: \"Does your employer match? Check at doublethedonation.com.\" That's it. Free money. Most SC nonprofits never ask.",
    url: "doublethedonation.com",
  },

  // -----------------------------------------------------------------
  // BLOCK 3 — PERSISTENT POVERTY COUNTIES (7 min)
  // -----------------------------------------------------------------

  // 9 — BLOCK 3 TITLE
  {
    kind: "title",
    eyebrow: "Move #3 of 4",
    title: "Persistent Poverty",
    subtitle: "Counties.",
    footer: "If your service area touches certain South Carolina counties, the federal government gives you a scoring advantage on grants that nobody tells you about.",
  },

  // 10 — BLOCK 3 BIG STAT
  {
    kind: "stat",
    spicy: true,
    eyebrow: "Federal scoring boost — written into law",
    bigNumber: "12",
    headline: "South Carolina counties with a poverty rate above 20% for 30+ consecutive years.",
    body: "The U.S. Census Bureau published the most recent list in May 2023. South Carolina has more Persistent Poverty Counties per capita than any state in the country except Mississippi.",
    sourceLine: "Source: U.S. Census Bureau, \"Persistent Poverty in Counties and Census Tracts\" (May 2023)",
  },

  // 11 — BLOCK 3 LIST (the 12 counties)
  {
    kind: "opportunity",
    eyebrow: "The 12 — Some Will Surprise You",
    title: "South Carolina's",
    subtitle: "Persistent Poverty Counties.",
    bullets: [
      "ALLENDALE  ·  BAMBERG  ·  BARNWELL",
      "DILLON  ·  HAMPTON  ·  LEE",
      "MARION  ·  MARLBORO",
      "ORANGEBURG  ·  SUMTER  ·  UNION",
      "WILLIAMSBURG",
    ],
    closer: "Some of you just heard your county. Or your grandmother's county. Or the county where the kids you serve actually live. That matters more than you think.",
    url: "census.gov · 2023 publication",
  },

  // 12 — BLOCK 3 THE MOVE
  {
    kind: "opportunity",
    eyebrow: "The Move",
    title: "Use the exact phrase.",
    subtitle: "Reviewers search for it.",
    bullets: [
      "WRITE THIS — \"Our program serves residents of Persistent Poverty Counties as designated by the U.S. Census Bureau in its May 2023 publication.\"",
      "WHY — The federal \"10-20-30 framework\" (codified in the Consolidated Appropriations Act of 2021) requires multiple programs to direct at least 10% of funds to PPCs.",
      "BONUS — USDA programs like the Rural Business Development Grant award 10 priority points automatically when this phrase appears.",
      "EVEN 5% — If just five percent of the people you serve come from a PPC, lead with it. Reviewers search the document for that phrase. If it's missing, they can't give you the points.",
    ],
    closer: "Same pantry. Same truck. Same volunteers. Ten points apart on the federal rubric — based on one sentence.",
    url: "flash-grants.com",
  },

  // -----------------------------------------------------------------
  // BLOCK 4 — THE SECOND CYCLE PLAY (7 min)
  // -----------------------------------------------------------------

  // 13 — BLOCK 4 TITLE
  {
    kind: "title",
    eyebrow: "Move #4 of 4",
    title: "The Second",
    subtitle: "Cycle Play.",
    footer: "Most nonprofits give up after one rejection. Experienced ones know rejection is the first step of the acceptance — if you reply correctly.",
  },

  // 14 — BLOCK 4 BIG STAT
  {
    kind: "stat",
    spicy: true,
    eyebrow: "What the NIH publishes — and nobody outside research reads",
    bigNumber: "2–3×",
    headline: "How much more often a resubmitted federal application succeeds compared to a first-time submission.",
    body: "First-time NIH R01 applications succeed about 12% of the time. Resubmissions of the same projects — revised based on reviewer feedback — succeed at 20-30%. Same applicant. Same project. The only difference is they listened to the feedback and came back.",
    sourceLine: "Source: NIH funding statistics (annual) · Bouvier Grant Group resubmission analysis · Center for Effective Philanthropy program officer benchmark study",
  },

  // 15 — BLOCK 4 THE MOVE (the rejection email)
  {
    kind: "opportunity",
    eyebrow: "The Move",
    title: "Send this email",
    subtitle: "after every rejection.",
    bullets: [
      "STEP 1 — Send a real thank-you. Not \"please reconsider.\" A real thank-you. They reviewed your work. Their time is worth thanking.",
      "STEP 2 — Ask one specific question. NOT \"what did we do wrong.\" Ask: \"Was there one specific area of our proposal where you'd want to see more detail before funding? Even a single sentence helps us strengthen our work — for you, and for future applicants who learn from our improvement.\"",
      "STEP 3 — When you resubmit, open with: \"Since our last application, we've strengthened [specific thing] by [specific change].\"",
      "WHY IT WORKS — Program officers love being teachers. Most nonprofits never give them the chance. The CEP benchmark study found responsiveness from the program officer is the single biggest driver of grantee satisfaction.",
    ],
    closer: "A rejection is not a no. A rejection is \"not yet, and here's why.\" You just have to be brave enough to ask the why.",
    url: "flash-grants.com",
  },

  // -----------------------------------------------------------------
  // CLOSING + QR
  // -----------------------------------------------------------------

  // 16 — CLOSING SUMMARY
  {
    kind: "opportunity",
    eyebrow: "Tonight — In Four Sentences",
    title: "The four moves.",
    subtitle: "Memorize these.",
    bullets: [
      "ONE — The dollar amount you ask for is more important than the grant you're applying to. Call. Ask. Go in under the line.",
      "TWO — Every $100 a donor gives you might be worth $200. Boeing, Publix, Dominion, Duke, Volvo, Blackbaud, BoA, Michelin, TD — they all match. Ask. Always ask.",
      "THREE — Allendale, Bamberg, Barnwell, Dillon, Hampton, Lee, Marion, Marlboro, Orangeburg, Sumter, Union, Williamsburg. If you touch one of these, use the exact Census Bureau phrase.",
      "FOUR — A rejection is not a no. NIH proves it. Resubmissions succeed 2-3× more often. Send the email. Ask the question. Come back stronger.",
    ],
    closer: "Tonight wasn't about grant writing. Tonight was about respect. For your work. For your time. For the donors and funders who want to support you — but who don't know how, unless you make it easy. The nonprofits in this state who win grants aren't smarter than you. They just learned these four things. Now you know them too.",
    url: "flash-grants.com",
  },

  // 17 — Feedback Survey QR (help shape the platform)
  {
    kind: "qr",
    eyebrow: "Help Us Build What's Next",
    title: "Tell us what's missing.",
    subtitle: "Two minutes. Real ears.",
    body: "Scan this code and tell us what worked tonight, what didn't, what you wish FlashGrants did, and what would make it a no-brainer for your nonprofit. Every response lands directly in the founder's inbox — usually answered within 24 hours. No marketing list. No automated drip. Just real ears on the other side of the screen.",
    footer: "If you only do ONE thing after tonight, do this. We'll build what you flag.",
    qrUrl: "https://flash-grants.com/feedback?source=thursday_jun18&utm_source=meeting&utm_medium=qr&utm_campaign=thursday_jun18_feedback",
    shortUrl: "flash-grants.com/feedback",
  },

  // 18 — Bring a Friend QR (invite others to the platform)
  {
    kind: "qr",
    eyebrow: "Bring a Friend",
    title: "Know someone who needs this?",
    subtitle: "Scan. Send. Done.",
    body: "Snap this QR with your phone camera. Text the link to one founder you know — someone running a nonprofit, thinking about starting one, or just tired of leaving grant money on the table. FlashGrants is free to browse, free to bookmark, free to use the mission diagnostic. No email wall. Just the platform.",
    footer: "Pass it along in the next 48 hours and you've turned tonight's room into 10 more rooms.",
    qrUrl: "https://flash-grants.com/?utm_source=meeting&utm_medium=qr&utm_campaign=thursday_jun18_bring_a_friend",
    shortUrl: "flash-grants.com",
  },

  // 19 — QR / ASK
  {
    kind: "qr",
    eyebrow: "Ask — One Thing Before You Leave",
    title: "Get the cheat",
    subtitle: "sheet from tonight.",
    body: "Scan this code. I send one email every Sunday — five SC grants, five minutes. No spam. No fluff. The cheat sheet with every employer matching program, every Persistent Poverty County, and every one of these four moves goes out in this week's drop.",
    footer: "FlashGrants · Built in Myrtle Beach · For South Carolina founders nobody else is building for.",
    qrUrl: "https://flash-grants.com/?utm_source=meeting&utm_medium=qr&utm_campaign=thursday_jun18_four_moves",
    shortUrl: "flash-grants.com",
  },
];
DECKS["thursday-jul-2"] = SLIDES_THURSDAY_JUL_2;


// =============================================================================
// SLIDES — Thursday, June 25, 2026
// Route: /slides/thursday-jun-25/1
// Topic: "From Ground Zero to Funded" — the 5-stage path for SC nonprofit founders
// Source content reviewed against /app/memory/verified_facts.md
// =============================================================================
const SLIDES_THURSDAY_JUN_25 = [
  // 1 — TITLE
  {
    kind: "title",
    eyebrow: "FlashGrants · Thursday, June 25, 2026",
    title: "From Ground Zero",
    subtitle: "to Funded.",
    footer: "Greg James · Founder, FlashGrants · flash-grants.com · Myrtle Beach, SC",
  },

  // 2 — OPENING HOOK
  {
    kind: "stat",
    eyebrow: "Tonight",
    bigNumber: "5",
    headline: "Five stages. Every funded SC nonprofit walks them in order.",
    body: "You're already doing the work. The paperwork hasn't caught up. Tonight is not a grant list — it's the map that comes BEFORE grant lists actually work for you. Skip a stage, you stall. Walk them in order, you get funded.",
    sourceLine: "Same framework used by the founders quietly winning Bunnelle, Sisters of Charity, and Brown Family grants right now.",
  },

  // -----------------------------------------------------------------
  // STAGE 1 — FILED RIGHT
  // -----------------------------------------------------------------

  // 3 — STAGE 1 TITLE
  {
    kind: "title",
    eyebrow: "Stage 1 of 5",
    title: "Filed",
    subtitle: "Right.",
    footer: "The legal foundation most founders set up wrong on day one.",
  },

  // 4 — STAGE 1 THE MOVE
  {
    kind: "opportunity",
    eyebrow: "What you need from day one",
    title: "The foundation-ready six.",
    subtitle: "What foundations expect to see before they'll take you seriously.",
    bullets: [
      "SC ARTICLES OF INCORPORATION — filed with the SC Secretary of State (sos.sc.gov), under $30. This is the legal birth certificate of the nonprofit.",
      "EIN — Employer Identification Number from the IRS. Free. Takes 10 minutes online at irs.gov.",
      "3-PERSON BOARD MINIMUM — required by SC nonprofit corporation law AND expected by the IRS for public charity status. No shortcuts.",
      "BYLAWS — a 1-page version is fine to start. Free template at councilofnonprofits.org.",
      "CONFLICT-OF-INTEREST POLICY — every board member signs annually. Template online.",
      "SC BUSINESS LICENSE — required to legally operate in your home county/city. NOT required for IRS 501(c)(3) approval, but foundations expect you to be operating legally. Local clerk's office.",
    ],
    closer: "Without these six, no foundation officer will take your call. With them, you're operating at the level foundations expect — and most pre-501(c)(3) founders aren't.",
    url: "councilofnonprofits.org",
  },

  // 5 — STAGE 1 INSIDER TIP
  {
    kind: "opportunity",
    eyebrow: "Insider Tip · Stage 1",
    title: "Recruit 5 board candidates.",
    subtitle: "Confirm 3. Keep 2 in reserve.",
    bullets: [
      "THE TRAP — Most founders barely scrape together 3 board members at filing time. When one resigns, quits, moves, or gets sick, you lose quorum mid-grant-application.",
      "THE FIX — Recruit 5 candidates from day one. Confirm the 3 the IRS needs. Hold the other 2 as pre-vetted alternates who already know the mission.",
      "WHY IT MATTERS — Foundation due-diligence calls sometimes ask, \"Tell me about your board.\" \"We have 3 directors plus 2 advisors\" sounds 10× more mature than \"we have 3 people, that's all we have.\"",
      "BONUS — Two alternates often become full board members within year one. You've already built your year-2 succession plan.",
    ],
    closer: "The IRS minimum is 3. Real-world insurance is 5. Cost difference: $0.",
    url: "flash-grants.com",
  },

  // -----------------------------------------------------------------
  // STAGE 2 — THE HYBRID PATH
  // -----------------------------------------------------------------

  // 6 — STAGE 2 TITLE
  {
    kind: "title",
    eyebrow: "Stage 2 of 5",
    title: "The Hybrid",
    subtitle: "Path.",
    footer: "You don't have to choose between fiscal sponsor OR your own 501(c)(3). Do both.",
  },

  // 7 — STAGE 2 BIG STAT
  {
    kind: "stat",
    spicy: true,
    eyebrow: "What most founders don't know",
    bigNumber: "4–6 weeks",
    headline: "Fiscal sponsorship unlocks foundation grants in a month and a half.",
    body: "IRS 501(c)(3) approval takes 4 weeks to 6 months. Most foundation grants require it. So most founders just WAIT — and lose half a year of grant cycles in the process. Better play: get fiscal-sponsored under an existing 501(c)(3) AND file your own IRS application AT THE SAME TIME.",
    sourceLine: "Coastal Community Foundation (coastalcommunityfoundation.org) sponsors organizations in their 9-county SC service area: Beaufort, Berkeley, Charleston, Colleton, Dorchester, Georgetown, Hampton, Horry, Jasper.",
  },

  // 8 — STAGE 2 INSIDER TIP (honesty caveat)
  {
    kind: "opportunity",
    eyebrow: "Insider Tip · Stage 2",
    title: "Know what fiscal sponsorship",
    subtitle: "DOES and DOES NOT unlock.",
    bullets: [
      "✅ UNLOCKS — most private foundation grants, community foundation grants, many corporate giving programs.",
      "❌ DOES NOT UNLOCK — most federal grants (Grants.gov, HHS, ACL). Federal funders generally require YOUR OWN IRS letter — they don't fund through sponsors.",
      "WHY THIS MATTERS — Some consultants pitch fiscal sponsorship as a fix-everything button. It isn't. It's a foundation-grant unlock, not a federal-grant unlock. Plan accordingly.",
      "THE RIGHT PLAY — File 1023 in parallel with your sponsorship onboarding. When your IRS letter arrives 3-6 months later, you graduate out of sponsorship and federal grants open up too.",
    ],
    closer: "Sponsor + IRS in parallel = 6 months of progress compressed into 90 days.",
    url: "irs.gov · pay.gov for current user fee",
  },

  // -----------------------------------------------------------------
  // STAGE 3 — THE NARRATIVE
  // -----------------------------------------------------------------

  // 9 — STAGE 3 TITLE
  {
    kind: "title",
    eyebrow: "Stage 3 of 5",
    title: "The",
    subtitle: "Narrative.",
    footer: "Foundations don't fund applications. They fund moments they remember.",
  },

  // 10 — STAGE 3 THE MOVE
  {
    kind: "opportunity",
    eyebrow: "The Move · Stage 3",
    title: "Lead with one person.",
    subtitle: "Specific beats sweeping. Every time.",
    bullets: [
      "WHAT KILLS APPLICATIONS — \"Our nonprofit aims to improve outcomes for at-risk populations through comprehensive wraparound services that address social determinants.\" Reviewer #3 forgets you by lunch.",
      "WHAT GETS FUNDED — \"After a Tuesday basketball practice, one of the kids didn't have a ride home. I drove him. By week three, three more kids needed rides. That's the day this organization started.\"",
      "THE PATTERN — One person. One specific moment. One organic outcome. Then back into the data.",
      "WHY IT WORKS — Foundation officers read 200 applications a week. Statistics blur. Stories with names and Tuesdays don't.",
    ],
    closer: "Your origin story is the application. The numbers just confirm what the story already promised.",
    url: "flash-grants.com — Mission Diagnostic refines this in 5 minutes",
  },

  // -----------------------------------------------------------------
  // STAGE 4 — THE LANE
  // -----------------------------------------------------------------

  // 11 — STAGE 4 TITLE
  {
    kind: "title",
    eyebrow: "Stage 4 of 5",
    title: "The",
    subtitle: "Lane.",
    footer: "Most SC nonprofits chase the same dollar. Pick a different lane.",
  },

  // 12 — STAGE 4 THE MOVE
  {
    kind: "opportunity",
    eyebrow: "The Move · Stage 4",
    title: "Under-contested lanes",
    subtitle: "SC funders actually want to fill.",
    bullets: [
      "SENIOR SERVICES + ADULT POPULATIONS — Sisters of Charity Foundation of SC explicitly funds this. Most SC nonprofits chase kids; fewer chase seniors.",
      "JUSTICE-IMPACTED ADULTS + REENTRY — rare program area in SC, well-funded when found. Foundations want to fund it; few applicants exist.",
      "RURAL HEALTHCARE ACCESS — federal + state money chasing too few applicants. ACL, HRSA, USDA all have unspent dollars here.",
      "FAITH-ADJACENT SECULAR COMMUNITY WORK — works for BOTH faith funders (SC Christian Foundation) AND secular funders. Rare double-eligible position.",
      "GEOGRAPHIC GAPS — Williamsburg, Marion, parts of Horry are persistently under-served vs. metro Greenville. Place-based funders pay attention.",
    ],
    closer: "When 100 applications hit a foundation's desk and yours is the only one in its lane — you've already won 60% of the review.",
    url: "flash-grants.com",
  },

  // 13 — STAGE 4 INSIDER TIP
  {
    kind: "opportunity",
    eyebrow: "Insider Tip · Stage 4",
    title: "Pull the 990",
    subtitle: "before you apply anywhere.",
    bullets: [
      "STEP 1 — Go to ProPublica Nonprofit Explorer (projects.propublica.org/nonprofits). Free. No login.",
      "STEP 2 — Search the foundation by name. Open their most recent Form 990. Scroll to \"Grants Paid.\"",
      "STEP 3 — Read the last 3 years of grants. Look at WHO they've funded, WHAT lane those orgs serve, and HOW MUCH they typically give.",
      "WHAT YOU LEARN — If your lane isn't represented in 3 years of giving, you're either an opportunity (rare) or the wrong fit (more likely). Either way, you'll save 8 hours of wasted application work.",
    ],
    closer: "Foundations don't publish their actual preferences. But every check they write IS a public record. Read what they DO — not what they SAY.",
    url: "projects.propublica.org/nonprofits",
  },

  // -----------------------------------------------------------------
  // STAGE 5 — THE DOOR
  // -----------------------------------------------------------------

  // 14 — STAGE 5 TITLE
  {
    kind: "title",
    eyebrow: "Stage 5 of 5",
    title: "The",
    subtitle: "Door.",
    footer: "Pre-talk the grant officer. Always. Before you submit anything.",
  },

  // 15 — STAGE 5 BIG STAT
  {
    kind: "stat",
    eyebrow: "The thing most founders don't realize",
    bigNumber: "Pre-call.",
    headline: "Pre-calling the grant officer is the single highest-leverage move most founders skip.",
    body: "Most founders submit cold. Foundation officers actually WANT to coach the applications coming in — they have time blocked specifically for it. Most just never call. The orgs that win consistently are the ones who treat the grant officer as a partner, not a gatekeeper.",
    sourceLine: "Real example: Kevin McCray (Grants & Data Manager at Frances P. Bunnelle Foundation) has explicit \"Contact Kevin for Application Assistance / Proposal Feedback\" on Bunnelle's own staff page.",
  },

  // 16 — STAGE 5 THE MOVE
  {
    kind: "opportunity",
    eyebrow: "The Move · Stage 5",
    title: "Email them",
    subtitle: "two to three weeks before the deadline.",
    bullets: [
      "WHO TO EMAIL — The Grants Manager, Program Officer, or Program Associate. Find them on the foundation's \"Staff\" or \"Our People\" page. Skip the CEO.",
      "WHAT TO WRITE — 3 short paragraphs. Who you are. What you do (with the one-person story from Stage 3). Two sentences on the program you want to fund. ONE specific question only they can answer.",
      "WHAT YOU GET — Often a reply with helpful context. Sometimes a 15-min Zoom. Either way, your application now has an internal champion.",
      "REAL SC EXAMPLES — Kevin McCray (Bunnelle Foundation) · Tori Hyder (Coastal Community Foundation, Tri-County intro meetings) · Most community foundations have a Program Associate whose entire job is this conversation.",
    ],
    closer: "Skipping this step is leaving real money on the table. Don't.",
    url: "flash-grants.com",
  },

  // -----------------------------------------------------------------
  // SYNTHESIS + REVEAL
  // -----------------------------------------------------------------

  // 17 — PUTTING IT TOGETHER
  {
    kind: "stat",
    eyebrow: "Putting it together",
    bigNumber: "90 days",
    headline: "Honest arc from \"filed-but-blocked\" to \"first LOI submitted.\"",
    body: "Weeks 1–2: legal cleanup + board confirmation. Weeks 2–4: fiscal sponsor application submitted + IRS 1023 filed in parallel. Weeks 3–6: narrative refined + funder lane chosen. Weeks 6–8: sponsorship approved + first grant officer conversations. Weeks 8–12: first two LOIs submitted with sponsor backing.",
    sourceLine: "90 days is not fast. Not slow either. It's the honest time it takes when you don't skip stages.",
  },

  // 18 — THE BROWN FAMILY REVEAL
  {
    kind: "opportunity",
    eyebrow: "🎯 The Reveal · Live Example",
    title: "Brown Family Foundation.",
    subtitle: "A brand-new SC funder almost nobody in this room has heard of yet.",
    bullets: [
      "FUNDER — Brown Family Foundation · SC-based private foundation funding 501(c)(3) nonprofits in both North Carolina and South Carolina.",
      "GRANT SIZE — $5,000 to $50,000. Two-step process: LOI first, then if invited, full application.",
      "TIMELINE — LOI cycle opened June 15, 2026 · LOI deadline August 10 · Full app deadline November 9 · Awards announced December 18.",
      "ELIGIBILITY — 501(c)(3) tax-exempt organization OR local/state government unit in NC or SC. Fiscal-sponsored projects can pursue this through their 501(c)(3) sponsor, but verify directly with Brown Family before submitting.",
      "THE EXCLUSIVE — You're hearing this TONIGHT. The public sees it on flash-grants.com Monday morning. 3.5-day head start before the rest of SC catches up.",
    ],
    closer: "This is what the 5-stage path unlocks: early access to grants the rest of the world hasn't found yet.",
    url: "brownfamilyfoundationsc.org/grant_program/",
  },

  // -----------------------------------------------------------------
  // CLOSE + QR
  // -----------------------------------------------------------------

  // 19 — FOUNDING MEMBER CLOSE
  {
    kind: "opportunity",
    eyebrow: "What FlashGrants does",
    title: "Free teaches the map.",
    subtitle: "Founding Member walks the road.",
    bullets: [
      "FREE — 100+ verified SC grants · 1 Mission Diagnostic run · Sunday Drop (5 grants/week in your inbox) · fiscal sponsor directory.",
      "PREMIUM FOUNDING MEMBER · $39.99/MO LOCKED — unlimited Mission Diagnostic iterations · Application Co-Pilot for LOI drafting · 1023-EZ Walkthrough Wizard · early access to grants like Brown Family · ONE 30-minute 1:1 consultation with Greg + a custom personalized playbook built for YOUR organization.",
      "WHY FOUNDING MEMBER MATTERS — public price will rise. Founding rate is locked for life. About $1.33 a day.",
      "WHAT'S DIFFERENT TONIGHT — sign up free at the link below before you leave the room and Greg will personally walk you through Stage 1 this week.",
    ],
    closer: "If something in this room tonight made you think \"I want this for my organization\" — Founding Members get the consultation AND the custom playbook to walk it. That's the difference between knowing the map and being handed the keys.",
    url: "flash-grants.com",
  },

  // 20 — QR CLOSER
  {
    kind: "qr",
    eyebrow: "Sign up tonight",
    title: "flash-grants.com",
    subtitle: "Scan. Sign up. We'll handle Stage 1 this week.",
    qrUrl: "https://flash-grants.com/?utm_source=meeting&utm_medium=qr&utm_campaign=thursday_jun25_groundzero",
    shortUrl: "flash-grants.com",
  },
];
DECKS["thursday-jun-25"] = SLIDES_THURSDAY_JUN_25;

// =============================================================================
// DECK: THURSDAY-JUL-9 — Grant Application Playbook (Step by Step)
// Route: /slides/thursday-jul-9/1
// =============================================================================
const SLIDES_THURSDAY_JUL_9 = [
  // 1 — TITLE
  {
    kind: "title",
    eyebrow: "FlashGrants · Thursday, July 9, 2026",
    title: "The Grant Application",
    subtitle: "Playbook. Step by step. Cheat codes included.",
    footer: "Greg James · Founder, FlashGrants · flash-grants.com · Myrtle Beach, SC",
  },

  // 2 — OPENING HOOK
  {
    kind: "stat",
    eyebrow: "The uncomfortable truth",
    bigNumber: "7",
    headline: "Minutes. That's how long a reviewer spends on your application.",
    body: "Not seven hours. Not seventy minutes. Seven minutes. They read the first paragraph, they scan the budget, they look for one specific outcome sentence, and they decide. Everything we teach tonight is designed for that reality.",
    sourceLine: "Source: 2024 review-time study across 300+ program officers at U.S. foundations.",
  },

  // 3 — BIG PICTURE
  {
    kind: "opportunity",
    eyebrow: "The 4 Phases",
    title: "Every winning application",
    subtitle: "moves through the same four phases.",
    bullets: [
      "PHASE 1 — PREP. The work you do before you ever touch the application.",
      "PHASE 2 — READ. Decoding what the funder actually wants.",
      "PHASE 3 — WRITE. Building the narrative and the numbers.",
      "PHASE 4 — SHIP. Submission, follow-up, and what happens after.",
    ],
    closer: "We're walking each one tonight — with the cheat codes and little-known facts baked in.",
  },

  // -----------------------------------------------------------------
  // PHASE 1 — PREP
  // -----------------------------------------------------------------

  // 4 — PHASE 1 TITLE
  {
    kind: "title",
    eyebrow: "Phase 1 of 4",
    title: "PREP.",
    subtitle: "Before you open a single application.",
    footer: "If you build these five assets ONCE, you can respond to 90% of grant applications in under 4 hours.",
  },

  // 5 — THE FIVE PREP DOCUMENTS
  {
    kind: "opportunity",
    eyebrow: "Your prep kit",
    title: "The Five Documents",
    subtitle: "every founder should have ready before starting.",
    bullets: [
      "USE OF FUNDS STATEMENT — line by line, dollar by dollar (the spine).",
      "YOUR STORY IN THREE LENGTHS — 150 words, 500 words, 1,500 words.",
      "COMMUNITY IMPACT ONE-PAGER — outcomes, numbers, testimonials.",
      "KEY FINANCIALS — current budget, most recent 990 or bank statement.",
      "PHOTO LIBRARY — 8 to 12 real photos of your work, ready to attach.",
    ],
    closer: "Build them once. Reuse them forever.",
  },

  // 6 — CHEAT CODE #1
  {
    kind: "stat",
    spicy: true,
    eyebrow: "🎯 Cheat Code #1",
    bigNumber: "REVERSE",
    headline: "Winners write applications backwards.",
    body: "Most founders write forward: \"Here's my mission, here's what I want.\" Winners read the funder's approved-grant list FIRST, find the funded projects that look like theirs, and reverse-engineer the language the funder already rewarded. Grant reviewers don't want novelty — they want confidence they're funding something that works.",
    sourceLine: "Show them you look like their last winner.",
  },

  // 7 — LITTLE-KNOWN FACT #1
  {
    kind: "opportunity",
    eyebrow: "📌 Little-Known Fact #1",
    title: "The 990-PF is public.",
    subtitle: "Almost nobody reads it before applying.",
    bullets: [
      "EVERY foundation publishes a 990-PF (or grants list) each year showing every grant they made, to whom, and for how much.",
      "It's free. It's on ProPublica Nonprofit Explorer. It takes 20 minutes to read.",
      "Founders who read it before writing produce completely different applications than the 95% who don't.",
      "It also tells you the funder's real check-size range — protecting you from asking for too much or too little.",
    ],
    closer: "20 minutes of reading = a completely different application.",
  },

  // -----------------------------------------------------------------
  // PHASE 2 — READ
  // -----------------------------------------------------------------

  // 8 — PHASE 2 TITLE
  {
    kind: "title",
    eyebrow: "Phase 2 of 4",
    title: "READ.",
    subtitle: "The RFP, three times.",
    footer: "Skip any of the three reads, and you're guessing. Funders can tell.",
  },

  // 9 — THREE READS
  {
    kind: "opportunity",
    eyebrow: "The 3-pass method",
    title: "Read the RFP three times.",
    subtitle: "Each read has a different job.",
    bullets: [
      "READ 1 — Fast scan for eligibility. Are you even eligible? Kill it in 10 minutes if not.",
      "READ 2 — Highlight every deadline, word count, required document, and mandatory attachment.",
      "READ 3 — Underline every scoring criterion. Those are the exact words your application must echo.",
    ],
    closer: "Three reads. Every time. No shortcuts.",
  },

  // 10 — CHEAT CODE #2
  {
    kind: "stat",
    spicy: true,
    eyebrow: "🎯 Cheat Code #2",
    bigNumber: "UNDERLINE",
    headline: "The Underline Test.",
    body: "For every scoring criterion the funder publishes, can you underline the sentence in YOUR application that directly answers it? If a category has no matching underlined sentence, that category is scoring you zero. Add a sentence. Underline it. Do this before every submission.",
    sourceLine: "15 minutes. Wins grants.",
  },

  // 11 — LITTLE-KNOWN FACT #2
  {
    kind: "stat",
    eyebrow: "📌 Little-Known Fact #2",
    bigNumber: "90",
    headline: "Days. Application to decision. Minimum.",
    body: "Funders say \"decisions quarterly\" or \"review is 60 days.\" The real answer is 90 days, minimum. Need money by December? Submit no later than August. Need money for a January event? Submit by October. Founders who submit \"just in time\" for their program are begging for a no.",
    sourceLine: "Plan grants like you plan pregnancies — the calendar doesn't negotiate.",
  },

  // -----------------------------------------------------------------
  // PHASE 3 — WRITE (NARRATIVE)
  // -----------------------------------------------------------------

  // 12 — PHASE 3 TITLE
  {
    kind: "title",
    eyebrow: "Phase 3 of 4",
    title: "WRITE.",
    subtitle: "The narrative + the numbers.",
    footer: "Write like a founder. Not a marketing team.",
  },

  // 13 — 3-ACT STRUCTURE
  {
    kind: "opportunity",
    eyebrow: "The narrative arc",
    title: "Every strong grant story",
    subtitle: "has three acts.",
    bullets: [
      "ACT 1 — THE ORIGIN MOMENT. One specific scene where the need became undeniable to you.",
      "ACT 2 — THE WORK. What you actually do, in concrete terms. No abstractions.",
      "ACT 3 — THE UNLOCK. What THIS specific grant makes possible that isn't possible without it.",
    ],
    closer: "Grant reviewers reward founders who write like humans about real people.",
  },

  // 14 — CHEAT CODE #3
  {
    kind: "opportunity",
    eyebrow: "🎯 Cheat Code #3",
    title: "The One Specific Person Rule.",
    subtitle: "Amateurs write \"we serve women in need.\"",
    bullets: [
      "WINNERS WRITE — \"When Renee walked through our door last April, she had eight dollars in her account and a court date the next morning.\"",
      "Every strong grant narrative has ONE named person — real, anonymized, or a composite.",
      "It converts abstract mission into concrete outcome.",
      "Use once per application. Never more, or it starts feeling like fiction.",
    ],
    closer: "Reviewers read the person, feel something, score higher.",
  },

  // 15 — LITTLE-KNOWN FACT #3
  {
    kind: "opportunity",
    eyebrow: "📌 Little-Known Fact #3",
    title: "Reviewers don't read top to bottom.",
    subtitle: "Here's the real reading order.",
    bullets: [
      "1st — BUDGET / USE OF FUNDS (do the numbers make sense?)",
      "2nd — COMMUNITY IMPACT (does this matter?)",
      "3rd — FOUNDER BIO (can this person execute?)",
      "4th — FULL NARRATIVE (LAST — only if the first three earned it).",
    ],
    closer: "Your budget and impact language must be strong in isolation. If someone reads only those two, do they still fund you?",
  },

  // 16 — PHASE 3B NUMBERS
  {
    kind: "opportunity",
    eyebrow: "The Numbers",
    title: "Every dollar you ask for",
    subtitle: "must map to a specific measurable outcome.",
    bullets: [
      "WEAK — \"Expand our marketing.\"",
      "STRONG — \"Six-month contract with a professional editor to publish 24 short-form video pieces reaching 15,000 new listeners.\"",
      "THE FORMULA — [Dollar amount] → [Specific action] → [Measurable outcome]",
      "If a line item doesn't fit the formula, it's not ready.",
    ],
    closer: "Reviewers audit budgets first. Don't give them a reason to stop reading.",
  },

  // 17 — CHEAT CODE #4
  {
    kind: "stat",
    spicy: true,
    eyebrow: "🎯 Cheat Code #4",
    bigNumber: "10–15%",
    headline: "The contingency reserve line.",
    body: "Every professional budget includes a 10–15% contingency line. Almost no first-time founder does. Adding this line signals to a reviewer: \"I've built a real budget before.\" It's the single fastest way to jump from amateur to credible on the numbers page.",
    sourceLine: "Costs you nothing. Wins you attention.",
  },

  // 18 — LITTLE-KNOWN FACT #4
  {
    kind: "opportunity",
    eyebrow: "📌 Little-Known Fact #4",
    title: "Owned obstacles score higher.",
    subtitle: "Founders think they should hide weaknesses. Reviewers reward honesty.",
    bullets: [
      "WEAK — \"We are well-positioned to succeed.\"",
      "STRONG — \"The biggest thing standing between us and 60 more clients is that I'm still the only person doing the visits. This grant funds that hire.\"",
      "Naming your real obstacle in ONE sentence tells the reviewer you know your business.",
      "Founders who hide weaknesses look like they haven't stress-tested their own plan.",
    ],
    closer: "Honesty is a scoring category disguised as humility.",
  },

  // -----------------------------------------------------------------
  // PHASE 4 — SHIP
  // -----------------------------------------------------------------

  // 19 — PHASE 4 TITLE
  {
    kind: "title",
    eyebrow: "Phase 4 of 4",
    title: "SHIP.",
    subtitle: "The last hour is where applications die.",
    footer: "Every point earned in Phase 1–3 can be lost in the final 60 minutes.",
  },

  // 20 — CHEAT CODE #5
  {
    kind: "stat",
    spicy: true,
    eyebrow: "🎯 Cheat Code #5",
    bigNumber: "48–72",
    headline: "Hours early. Never on deadline day.",
    body: "Server crashes, upload errors, and typos on the final page kill applications every cycle. Submit 48–72 hours before deadline. Program officers can flag missing pieces. Tech failures don't matter. You get the final 48 hours for the NEXT application.",
    sourceLine: "Founders who submit at 11:47 PM on deadline night are gambling with 40 hours of work.",
  },

  // 21 — LITTLE-KNOWN FACT #5
  {
    kind: "opportunity",
    eyebrow: "📌 Little-Known Fact #5",
    title: "The 24-hour thank-you email.",
    subtitle: "Nobody sends it. Everybody should.",
    bullets: [
      "Within 24 hours of submission, send a short thank-you to the program officer.",
      "TEMPLATE — \"Just wanted to say thanks for making the application process clear. Confirmation number [X]. Available for any follow-up questions.\"",
      "Program officers remember the ones who do it.",
      "When two applications are tied on scoring, that email breaks the tie 100% of the time.",
    ],
    closer: "The cheapest 2 minutes you'll ever spend on grant work.",
  },

  // 22 — CHEAT CODE #6 (BIG ONE)
  {
    kind: "stat",
    spicy: true,
    eyebrow: "🎯 Cheat Code #6 — The Big One",
    bigNumber: "3–5",
    headline: "Grants at once. Never submit one at a time.",
    body: "The Stack Strategy: apply to 3–5 aligned grants in the SAME 30-day window, using the same narrative core, same Use of Funds, same photos — customized per funder. One \"yes\" funds you. Multiple \"yes\"es let you preserve future relationships. Reviewers who see support from other funders score you higher.",
    sourceLine: "The prep effort amortizes across 5 applications. Winners submit consistently — that's the whole game.",
  },

  // 23 — PITFALLS
  {
    kind: "opportunity",
    eyebrow: "The pitfalls",
    title: "Seven things",
    subtitle: "that kill applications before they're read.",
    bullets: [
      "Applying to grants you're not eligible for (read eligibility 3 times before you start).",
      "Vague budgets. Every dollar must have a job.",
      "Corporate voice. Write like a founder, not a marketing team.",
      "Ignoring page limits. Reviewers stop reading at the cap. Every word after is dead.",
      "Missing attachments. #1 cause of auto-rejection.",
      "Submitting on deadline day.",
      "No thank-you email.",
    ],
    closer: "Every one is 100% avoidable. Every one still kills applications every cycle.",
  },

  // 24 — 30-DAY PORTFOLIO PLAN
  {
    kind: "opportunity",
    eyebrow: "Your assignment",
    title: "The 30-day portfolio plan.",
    subtitle: "Turn this into a system — not a one-shot.",
    bullets: [
      "WEEK 1 — Build your Use of Funds Statement + your 3-length story.",
      "WEEK 2 — Identify 3–5 aligned grants opening in the next 60 days.",
      "WEEK 3 — Reverse-engineer 2 recent winners for each funder.",
      "WEEK 4 — Draft all 3–5 applications simultaneously.",
    ],
    closer: "The goal isn't one perfect application. It's a portfolio machine that ships 3–5 per quarter, forever.",
  },

  // 25 — TONIGHT'S LIVE AMBER GRANT FILL
  {
    kind: "opportunity",
    eyebrow: "Tonight · Live Application",
    title: "The Amber Grant.",
    subtitle: "Yvonne and Melissa are filling theirs out with us — right now.",
    bullets: [
      "$10,000 monthly grant from WomensNet, for women-owned nonprofits and small businesses.",
      "Two questions total. That's the whole application.",
      "$15 application fee. Monthly deadline: last day of every month, 11:59:59 PM ET.",
      "Winner announced by the 21st of the following month.",
      "APPLY — https://ambergrantsforwomen.com/get-an-amber-grant/apply-now",
    ],
    closer: "Show up next month with a submission. That's the assignment.",
    url: "ambergrantsforwomen.com/get-an-amber-grant/apply-now",
  },
];
DECKS["thursday-jul-9"] = SLIDES_THURSDAY_JUL_9;

// =============================================================================
// DECK: KAREN-JUL21 — Backup deck for the Karen Riordan / Together SC call
// Route: /slides/karen-jul21/1
// USE WITH DISCIPLINE: DO NOT pull this deck up unless Karen asks for evidence
// or a snapshot. First move on the call is LISTEN, not present.
// =============================================================================
const SLIDES_KAREN_JUL21 = [
  // 1 — THE EVIDENCE
  {
    kind: "opportunity",
    eyebrow: "FlashGrants · A snapshot",
    title: "What we've verified.",
    subtitle: "Numbers pulled live from the platform on Jul 11, 2026.",
    bullets: [
      "99 active grants — every one primary-source verified before it goes live.",
      "74 unique verified funders — federal, state, corporate, foundation, community.",
      "35 individualized prospect playbooks delivered — each one primary-source-checked before send.",
      "83 grants tagged for nonprofits · 27 for small business/startup · pre-501(c)(3) segment identified and served.",
      "12+ focus areas: Education, Mental Health, Capacity Building, Community Development, Youth, Housing, Workforce, Environment, Health, Arts, Career Pathways, Affordable Housing.",
    ],
    closer: "The discipline is what makes it useful — not the volume.",
  },

  // 2 — THE ACTIVE FOUNDERS
  {
    kind: "opportunity",
    eyebrow: "The founders in-flight this week",
    title: "Five South Carolinians.",
    subtitle: "Real work moving right now — not a customer list, a coverage map.",
    bullets: [
      "MARGARET HARRIOTT — Horry County · Women-veteran-founded 501(c)(3) · food pantry + community garden buildout · USDA CF Direct + Walmart Spark Good in-flight.",
      "RICKY SAPP — Grand Strand · Youth Foundation · Nov 14 jazz-themed charity gala · HTC + Brittain Resorts + Walmart Spark Good sponsor asks in-flight.",
      "CHARLES CAMPBELL — Myrtle Beach · Jazz lounge / community venue · CommunityWorks CDFI loan preparation · Spring 2027 open.",
      "MELISSA MOSES — Horry County · Senior Care Network · pre-501(c)(3) · post-hospital senior visits + Awesome Foundation application this week.",
      "YVONNE CAMPBELL — SC statewide · Daughters of Destiny Network podcast · pre-501(c)(3) · women-founder documentation series + Awesome Foundation application this week.",
    ],
    closer: "Every one of these is verified work. No hypotheticals.",
  },
];
DECKS["karen-jul21"] = SLIDES_KAREN_JUL21;


// =============================================================================
// DECK: 1023-EZ-THURSDAY — Step-by-step tutorial on IRS Form 1023-EZ
// Route: /slides/1023-ez-thursday/1
// All facts sourced from IRS primary documents (form, instructions, Pay.gov).
// Verify current fee + eligibility at IRS before filing.
// =============================================================================
const SLIDES_1023EZ_THURSDAY = [
  // 1 — TITLE
  {
    kind: "title",
    eyebrow: "FlashGrants · Thursday Live Session",
    title: "The 1023-EZ",
    subtitle: "Playbook. Step by step. Cheat codes included.",
    footer: "Kennardo \"Greg\" James · Founder, FlashGrants · flash-grants.com · Myrtle Beach, SC",
  },

  // 2 — OPENING HOOK
  {
    kind: "stat",
    eyebrow: "The whole game in one line",
    bigNumber: "$275",
    headline: "One form. One fee. One IRS letter that changes what your nonprofit can do.",
    body: "Form 1023-EZ is the streamlined path to federal 501(c)(3) status. If you qualify, it takes about two hours to complete and the IRS typically responds in 2–4 weeks — versus 3–6+ months for the long-form 1023. Tonight we walk it, part by part, with the cheat codes.",
    sourceLine: "Source: IRS — About Form 1023-EZ · irs.gov/forms-pubs/about-form-1023-ez",
  },

  // 3 — WHAT IT IS
  {
    kind: "opportunity",
    eyebrow: "Definition",
    title: "What is Form 1023-EZ?",
    subtitle: "The IRS's streamlined path to 501(c)(3) status.",
    bullets: [
      "Full name: 'Streamlined Application for Recognition of Exemption Under Section 501(c)(3) of the Internal Revenue Code.'",
      "Introduced by the IRS in July 2014 to clear a massive backlog of small-org applications.",
      "3 pages long (vs. 26+ pages for the standard Form 1023).",
      "Filed electronically ONLY, through Pay.gov — no paper option.",
      "Fee: $275 (verify current fee at Pay.gov before filing — IRS updates in Rev. Proc. each January).",
    ],
    closer: "Same legal outcome as the long form. Same determination letter. Just faster — if you qualify.",
    url: "irs.gov/forms-pubs/about-form-1023-ez",
  },

  // 4 — WHO QUALIFIES (BIG STAT)
  {
    kind: "stat",
    eyebrow: "The Ceiling",
    bigNumber: "$50K",
    headline: "Projected gross receipts must stay under $50,000 in each of your first 3 years — and total assets under $250,000.",
    body: "If you'll blow past those numbers in year 1, 2, or 3, you file the long-form 1023. If you're a brand-new small nonprofit — food pantry, youth org, community group, faith-adjacent nonprofit — you almost certainly qualify for the EZ.",
    sourceLine: "Source: Form 1023-EZ Eligibility Worksheet · irs.gov/pub/irs-pdf/i1023ez.pdf",
  },

  // 5 — THE 5 THINGS YOU NEED BEFORE STARTING
  {
    kind: "opportunity",
    eyebrow: "The prep list",
    title: "5 things you need BEFORE you start.",
    subtitle: "Do not open Pay.gov until you have all five.",
    bullets: [
      "EIN — Employer Identification Number (free, 10 minutes online at IRS.gov).",
      "STATE INCORPORATION — Articles of Incorporation filed with your Secretary of State (SC = $25).",
      "PURPOSE + DISSOLUTION CLAUSES — two specific paragraphs in your Articles (details in Slide 12).",
      "BOARD OF 3 — minimum three directors with names, addresses, titles.",
      "BYLAWS — not filed with 1023-EZ, but strongly recommended before you apply.",
    ],
    closer: "Miss any one of these and the IRS will reject the application. That's your $275 fee, gone.",
  },

  // -----------------------------------------------------------------
  // PHASE 1 — ELIGIBILITY
  // -----------------------------------------------------------------

  // 6 — PHASE 1 TITLE
  {
    kind: "title",
    eyebrow: "Phase 1 of 4",
    title: "ELIGIBILITY.",
    subtitle: "Prove you qualify before you touch the form.",
    footer: "The IRS built a 30-question worksheet for this. Anyone who skips it either wastes $275 or commits perjury.",
  },

  // 7 — THE ELIGIBILITY WORKSHEET
  {
    kind: "opportunity",
    eyebrow: "Step 1 — Non-negotiable",
    title: "Complete the Eligibility Worksheet.",
    subtitle: "30 yes/no questions inside the official Instructions PDF.",
    bullets: [
      "Located at the front of the Form 1023-EZ Instructions PDF (pages 13–22).",
      "You must answer NO to every single one of the 30 questions to be eligible.",
      "One YES = you cannot use 1023-EZ, and must file the standard Form 1023 instead.",
      "Save your completed worksheet — the IRS requires you to keep it, and can audit later.",
      "Do NOT lie on this worksheet. That is federal fraud, and the IRS revokes exempt status when caught.",
    ],
    closer: "20 minutes with the worksheet saves 2 months of headache — or a $275 rejection.",
    url: "irs.gov/pub/irs-pdf/i1023ez.pdf",
  },

  // 8 — THE 5 BIGGEST DEALBREAKERS
  {
    kind: "opportunity",
    eyebrow: "Common disqualifiers",
    title: "The 5 dealbreakers.",
    subtitle: "If any of these describe you, use the long-form Form 1023 instead.",
    bullets: [
      "You'll bring in more than $50K/year in years 1, 2, or 3 (projected OR looking back).",
      "You're a church, school, hospital, or medical research organization.",
      "You're an LLC formed BEFORE October 2021 (post-2021 LLCs can now use 1023-EZ under Rev. Proc. 2022-8, but with strict rules).",
      "You're a successor to a for-profit business.",
      "You're formed under the laws of a foreign country, or don't have a US mailing address.",
    ],
    closer: "Full disqualifier list is questions 1–30 of the worksheet. Read every one.",
  },

  // 9 — CHEAT CODE #1 — 27-MONTH RULE
  {
    kind: "stat",
    spicy: true,
    eyebrow: "🎯 Cheat Code #1",
    bigNumber: "27",
    headline: "Months. The retroactive-exemption window.",
    body: "If you file Form 1023-EZ within 27 months of your state incorporation date, the IRS makes your exempt status RETROACTIVE to the day you incorporated. That means every donation received since day one is tax-deductible for your donors — and any income earned is exempt. Miss 27 months and you only get exempt status from the postmark date forward.",
    sourceLine: "Source: Form 1023-EZ Instructions, Part IV & Rev. Proc. 2014-11 · irs.gov",
  },

  // -----------------------------------------------------------------
  // PHASE 2 — PRE-FILING PREP
  // -----------------------------------------------------------------

  // 10 — PHASE 2 TITLE
  {
    kind: "title",
    eyebrow: "Phase 2 of 4",
    title: "PRE-FILING.",
    subtitle: "The three assets you build BEFORE opening Pay.gov.",
    footer: "Skip these and the IRS bounces your application — even if the 1023-EZ itself is filled out perfectly.",
  },

  // 11 — GET YOUR EIN
  {
    kind: "opportunity",
    eyebrow: "Asset 1 of 3",
    title: "Get your EIN.",
    subtitle: "Free. Online. 10 minutes.",
    bullets: [
      "The EIN is your nonprofit's tax ID — the IRS's version of a Social Security number for your organization.",
      "Apply online at IRS.gov — you get the number IMMEDIATELY at the end of the session.",
      "You need: your incorporator's name + SSN, your nonprofit's legal name, formation date, state.",
      "Application closes at 10 PM ET each day. It's ONLY available Monday–Friday.",
      "Save the CP 575 confirmation letter — you'll upload it to Pay.gov later.",
    ],
    closer: "You cannot start Form 1023-EZ without an EIN. Get this out of the way first.",
    url: "irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online",
  },

  // 12 — ARTICLES OF INCORPORATION
  {
    kind: "opportunity",
    eyebrow: "Asset 2 of 3",
    title: "Your Articles of Incorporation.",
    subtitle: "The two specific paragraphs the IRS will look for.",
    bullets: [
      "PURPOSE CLAUSE — must restrict activities to 501(c)(3) purposes: 'charitable, religious, educational, scientific...' etc.",
      "DISSOLUTION CLAUSE — must state that upon dissolution, assets go to another 501(c)(3) or to the government.",
      "If your Articles are missing EITHER clause, the IRS will reject you — even if everything else is perfect.",
      "In South Carolina: file Articles with the SC Secretary of State ($25 online fee).",
      "The IRS provides model language in Publication 557, Appendix A — copy it verbatim if unsure.",
    ],
    closer: "These two paragraphs are the #1 reason 1023-EZ applications get denied. Get them right.",
    url: "irs.gov/pub/irs-pdf/p557.pdf",
  },

  // 13 — BOARD OF THREE
  {
    kind: "opportunity",
    eyebrow: "Asset 3 of 3",
    title: "Recruit your board of 3+.",
    subtitle: "The IRS wants distinct humans, not just you and your spouse.",
    bullets: [
      "Minimum 3 directors — the IRS does not officially require this, but state law usually does, and IRS reviewers expect it.",
      "Related parties (spouses, siblings, business partners) count — but the IRS looks harder at boards where they're the majority.",
      "You'll need each director's name, mailing address, and title (President / Secretary / Treasurer minimum).",
      "Hold your first board meeting BEFORE filing — adopt bylaws, elect officers, authorize the 1023-EZ filing. Document it in minutes.",
      "Bylaws are NOT uploaded to Pay.gov, but the IRS can request them later. Have them ready.",
    ],
    closer: "A board of 3 unrelated community members signals seriousness. A board of just you signals a hobby.",
  },

  // 14 — CHEAT CODE #2 — DISSOLUTION CLAUSE
  {
    kind: "stat",
    spicy: true,
    eyebrow: "🎯 Cheat Code #2",
    bigNumber: "PASTE",
    headline: "The IRS gives you the exact dissolution language. Use it verbatim.",
    body: "In IRS Publication 557, Appendix A, the IRS publishes model dissolution clause language you can copy word-for-word into your Articles of Incorporation. Founders who try to write their own dissolution clause get denied constantly. Founders who paste the IRS's own language get approved. This is a $275 test where the answer key is public.",
    sourceLine: "Source: IRS Publication 557, Appendix A · irs.gov/pub/irs-pdf/p557.pdf",
  },

  // -----------------------------------------------------------------
  // PHASE 3 — THE FORM, PART BY PART
  // -----------------------------------------------------------------

  // 15 — PHASE 3 TITLE
  {
    kind: "title",
    eyebrow: "Phase 3 of 4",
    title: "THE FORM.",
    subtitle: "Six parts. Walked one at a time.",
    footer: "Form 1023-EZ is filed on Pay.gov — you cannot download and mail it. Bookmark the link now.",
  },

  // 16 — PART I: IDENTIFICATION
  {
    kind: "opportunity",
    eyebrow: "Part I of VI",
    title: "Identification of Applicant.",
    subtitle: "Basic facts — take your time, spelling matters.",
    bullets: [
      "Legal name of organization — must match your Articles of Incorporation EXACTLY (comma, LLC, Inc., etc.).",
      "Mailing address — physical address preferred, not a PO box if you can help it.",
      "Month tax year ends — most nonprofits pick December (calendar year).",
      "Date of formation — the date your Articles were filed with the Secretary of State, not the date you dreamed it up.",
      "Person to contact — usually the incorporator or Board President. Include a working email.",
    ],
    closer: "This part is boring. That's why founders rush it. Rushed = mismatches = IRS rejection.",
    url: "pay.gov/public/form/start/62759871",
  },

  // 17 — PART II: ORGANIZATIONAL STRUCTURE
  {
    kind: "opportunity",
    eyebrow: "Part II of VI",
    title: "Organizational Structure.",
    subtitle: "Corporation, LLC, unincorporated association, or trust.",
    bullets: [
      "Pick one: Corporation, LLC (post-Oct 2021 only), Unincorporated Association, or Trust.",
      "99% of 1023-EZ applicants are Corporations — that's what you want unless you have specific tax counsel telling you otherwise.",
      "Enter your date of formation and state of formation (SC = South Carolina).",
      "Confirm your Articles contain the 501(c)(3) purpose clause AND the dissolution clause (from Slide 12).",
      "One click here misrepresents your entity type and voids the application. Take five minutes.",
    ],
    closer: "This is where lying (accidentally or not) about your structure gets applications revoked years later.",
  },

  // 18 — PART III: SPECIFIC ACTIVITIES — THE MAIN EVENT
  {
    kind: "opportunity",
    eyebrow: "Part III of VI · The main event",
    title: "Your Specific Activities.",
    subtitle: "NTEE code + one paragraph describing what you do.",
    bullets: [
      "NTEE CODE — 3-character code that classifies your work. Pick the SPECIFIC one, not a vague one. (E.g., 'B21 - K-12 Elementary Schools' beats 'B - Education').",
      "MISSION DESCRIPTION — one paragraph, 250 words max. Plain English. What you do, for whom, and how.",
      "Do NOT invent activities you don't actually do — the IRS matches your description against your Articles' purpose clause.",
      "Do NOT over-promise (e.g., 'international humanitarian work' if you're a Horry County food pantry). Stay true.",
      "AVOID prohibited activities — no political campaign work, no substantial lobbying, no private-benefit language.",
    ],
    closer: "This is the section IRS reviewers actually read. Everything else is a checklist. THIS is the essay.",
    url: "irs.gov/pub/irs-tege/eo_ntee_codes_summary.pdf",
  },

  // 19 — CHEAT CODE #3 — NTEE PRECISION
  {
    kind: "stat",
    spicy: true,
    eyebrow: "🎯 Cheat Code #3",
    bigNumber: "SPECIFIC",
    headline: "The narrower your NTEE code, the faster your approval.",
    body: "The IRS reviewer scans your NTEE code first. A specific code ('P24 - Salvation Army', 'A25 - Arts Education') signals a founder who knows their lane. A vague code ('P - Human Services', 'A - Arts, Culture') signals someone who might be doing anything — which triggers a closer read and a longer wait. Pick the narrowest code that honestly fits.",
    sourceLine: "Source: IRS NTEE Codes Summary · irs.gov/pub/irs-tege/eo_ntee_codes_summary.pdf",
  },

  // 20 — PART IV: FOUNDATION CLASSIFICATION
  {
    kind: "opportunity",
    eyebrow: "Part IV of VI",
    title: "Foundation Classification.",
    subtitle: "Are you a public charity or a private foundation?",
    bullets: [
      "You want to be classified as a PUBLIC CHARITY — that's the box most 1023-EZ filers check.",
      "Two sub-options: 509(a)(1) (broad public support — most nonprofits) or 509(a)(2) (fee-for-service programs).",
      "Rule of thumb: If most of your revenue will be donations and grants → 509(a)(1).",
      "Rule of thumb: If most will be program fees (tuition, tickets, services) → 509(a)(2).",
      "If you're unsure, 509(a)(1) is the safer default for a brand-new small nonprofit.",
    ],
    closer: "Getting this wrong doesn't kill your application — but the wrong classification can trigger complex filing rules later.",
  },

  // 21 — PART V + PART VI
  {
    kind: "opportunity",
    eyebrow: "Parts V + VI",
    title: "Reinstatement (V) + Signature (VI).",
    subtitle: "Two short sections. Then you're done with the form.",
    bullets: [
      "PART V — Reinstatement. ONLY applies if you previously had 501(c)(3) status and it was auto-revoked (usually for 3 years of unfiled 990-Ns). Most first-time applicants leave this blank.",
      "PART VI — Signature. An authorized officer (usually your Board President) signs under penalty of perjury.",
      "The signer must be listed as an officer in Part I. No proxies. No spouses signing 'on behalf of.'",
      "By signing, you're attesting that the Eligibility Worksheet answers were all 'No' — that's the perjury piece.",
      "After signature, you'll be sent to Pay.gov to pay the $275 fee via bank draft, credit card, or debit card.",
    ],
    closer: "Sign it, pay it, get your confirmation number. You're now waiting on the IRS.",
  },

  // -----------------------------------------------------------------
  // PHASE 4 — FILE + AFTER
  // -----------------------------------------------------------------

  // 22 — PHASE 4 TITLE
  {
    kind: "title",
    eyebrow: "Phase 4 of 4",
    title: "FILE. WAIT. WIN.",
    subtitle: "What happens after you click submit.",
    footer: "The IRS response window is 2–4 weeks for 1023-EZ. Sometimes as fast as 10 days.",
  },

  // 23 — SUBMIT ON PAY.GOV
  {
    kind: "opportunity",
    eyebrow: "The submission",
    title: "Submit + pay on Pay.gov.",
    subtitle: "One session. Do not close the browser mid-way.",
    bullets: [
      "Filing URL — pay.gov/public/form/start/62759871 (bookmark it).",
      "You must create a Pay.gov account (free) — do this BEFORE you start filling out the form.",
      "The 1023-EZ session times out after 30 minutes of inactivity. Prep everything beforehand.",
      "Payment options: bank draft (free), credit/debit card (small fee).",
      "You'll receive a Pay.gov confirmation email within 5 minutes. SAVE IT. That's your receipt.",
    ],
    closer: "Pay.gov is the ONLY channel for Form 1023-EZ. Anyone selling you a 'faster route' is running a scam.",
    url: "pay.gov/public/form/start/62759871",
  },

  // 24 — CHEAT CODE #4 — CONFIRMATION HYGIENE
  {
    kind: "stat",
    spicy: true,
    eyebrow: "🎯 Cheat Code #4",
    bigNumber: "SAVE 3",
    headline: "Three things to screenshot the moment you hit submit.",
    body: "1) Your Pay.gov Tracking ID. 2) Your $275 payment confirmation. 3) The final PDF of the 1023-EZ Pay.gov generates. Save them into a folder called 'IRS 501c3 Application' before you close the tab. Nine out of ten founders forget to do this, then panic in month two when they can't find their tracking number.",
    sourceLine: "The IRS will use your Tracking ID as the ONLY reference in any follow-up correspondence.",
  },

  // 25 — WHAT HAPPENS NEXT
  {
    kind: "opportunity",
    eyebrow: "Post-submission",
    title: "What happens after you file.",
    subtitle: "The 2–4 week timeline, decoded.",
    bullets: [
      "WEEK 1 — Pay.gov emails your submission to the IRS EO Determinations office in Cincinnati, OH.",
      "WEEK 2 — Application enters the IRS EO Determinations queue. Status: 'In Review.'",
      "WEEK 2–4 — An IRS reviewer opens the application. Most approvals come at this stage.",
      "APPROVAL — You receive a DETERMINATION LETTER by mail. This is the golden ticket. Frame a copy, upload another to every grant application forever.",
      "REJECTION or QUESTIONS — The IRS may send a Form 4425 asking for more info. You have 30 days to respond.",
    ],
    closer: "Check status by calling IRS Customer Account Services: 877-829-5500. Have your EIN + Tracking ID ready.",
  },

  // 26 — THE ASSIGNMENT
  {
    kind: "opportunity",
    eyebrow: "Your assignment",
    title: "File this month.",
    subtitle: "Not 'someday.' Not 'when I'm ready.' This month.",
    bullets: [
      "WEEK 1 — Complete the Eligibility Worksheet (30 questions). Confirm you can file EZ.",
      "WEEK 2 — Get your EIN + verify your Articles have the purpose + dissolution clauses.",
      "WEEK 3 — Recruit your third board member (if you don't have one), hold your first meeting, adopt bylaws.",
      "WEEK 4 — Sit down for 2 hours, fill out the 1023-EZ on Pay.gov, pay the $275, hit submit.",
      "MONTH 2 — Determination letter arrives. Every grant, every donation, every partnership just got easier.",
    ],
    closer: "The founders who wait 'until it feels right' are still waiting three years from now. File. This. Month.",
    url: "flash-grants.com/formation-toolkit",
  },
];
DECKS["1023-ez-thursday"] = SLIDES_1023EZ_THURSDAY;


// =============================================================================
// DECK: BEST-GRANT-THURSDAY — "Your Best Grant Probably Isn't A Grant"
// Route: /slides/best-grant-thursday/1
// 5-path funding framework for nonprofits, small biz, pre-501(c)(3) founders.
// All stats sourced from Giving USA + IRS + Kiva primary sources.
// =============================================================================
const SLIDES_BEST_GRANT_THURSDAY = [
  // 1 — TITLE
  {
    kind: "title",
    eyebrow: "FlashGrants · Thursday Live Session · July 30, 2026",
    title: "Your Best Grant Probably Isn't A Grant.",
    subtitle: "The 5 funding paths founders overlook — and how to stack them.",
    footer: "Kennardo \"Greg\" James · Founder, FlashGrants · flash-grants.com · Myrtle Beach, SC",
  },

  // 2 — OPENING HOOK
  {
    kind: "stat",
    eyebrow: "The number that reframes everything",
    bigNumber: "64%",
    headline: "64% of ALL money flowing to nonprofits comes from individual donors — not grants.",
    body: "About another quarter comes from foundations and corporations combined. Grants are ONE funding path. There are at least four others most founders never learn — and the founders who stack them win. Tonight we walk all five.",
    sourceLine: "Source: Giving USA 2025 Annual Report — givingusa.org",
  },

  // 3 — THE GRANT OBSESSION
  {
    kind: "opportunity",
    eyebrow: "The trap",
    title: "The grant obsession problem.",
    subtitle: "Why founders default to chasing grants — and pay for it.",
    bullets: [
      "Grants FEEL like the 'legitimate' funding path — the one you can put on your LinkedIn.",
      "Grants are the most COMPETITIVE. Most foundations reject 80–90% of applications.",
      "Grants are the SLOWEST. Application-to-check can take 6–12 months.",
      "Grants are the MOST RESTRICTED. Reporting requirements, program restrictions, matching-funds rules.",
      "And most founders spend 80% of their fundraising time on the path with 20% of the actual dollars.",
    ],
    closer: "The math is upside-down. Tonight we flip it.",
  },

  // 4 — THE PREVIEW
  {
    kind: "opportunity",
    eyebrow: "The map",
    title: "5 funding paths — most founders know 1.",
    subtitle: "Every one of these funds real organizations in South Carolina today.",
    bullets: [
      "PATH 1 — Grants (the one you know)",
      "PATH 2 — Corporate Sponsorships (faster money, different math)",
      "PATH 3 — In-Kind Donations (equivalent to cash, most under-asked)",
      "PATH 4 — Fiscal Sponsorship (grants for orgs that aren't 501(c)(3) yet)",
      "PATH 5 — Community Loans (0% interest, no credit check, no collateral)",
    ],
    closer: "By the end of tonight, you'll know which paths YOU should be running.",
  },

  // ============ PATH 1: GRANTS ============

  // 5 — TITLE
  {
    kind: "title",
    eyebrow: "Path 1 of 5",
    title: "GRANTS.",
    subtitle: "The one you know. The one you probably shouldn't chase first.",
    footer: "You've heard this word your whole founder journey. Let's set the reality baseline before we move past it.",
  },

  // 6 — WHAT GRANTS ARE
  {
    kind: "opportunity",
    eyebrow: "Definition",
    title: "What grants actually are.",
    subtitle: "Non-repayable funds from foundations, corporations, or governments.",
    bullets: [
      "PRIVATE FOUNDATION GRANTS — Family foundations, community foundations. Typically $5K–$50K for small orgs.",
      "CORPORATE FOUNDATION GRANTS — Duke Energy Foundation, BCBS SC, Sisters of Charity. Program-restricted.",
      "GOVERNMENT GRANTS — Federal (USDA, HHS, EPA), state (SC Arts Commission, SC Humanities), local.",
      "Almost always require 501(c)(3) status. Almost always have specific geography, focus area, and mission-fit criteria.",
      "Almost always require reporting AFTER the money hits — measurable outcomes, audit-ready records.",
    ],
    closer: "Grants aren't wrong. They're just narrow. And they punish founders who don't know the other paths.",
  },

  // 7 — REALITY STAT
  {
    kind: "stat",
    eyebrow: "The reality baseline",
    bigNumber: "10–30%",
    headline: "Grant acceptance rates typically range from 10% to 30% — depending on funder type.",
    body: "First-time applicants tend to land at the low end. Federal grants average around 25% acceptance; private foundations around 15–30%; corporate funders around 22%. For a first-time founder, expect to write 5–10 applications to win 1 grant. Grants are worth pursuing. They are NOT worth pursuing FIRST if you have any of the other four options open.",
    sourceLine: "Source: Instrumentl aggregate grant success data + GrantStation State of Grantseeking Report",
  },

  // ============ PATH 2: CORPORATE SPONSORSHIPS ============

  // 8 — TITLE
  {
    kind: "title",
    eyebrow: "Path 2 of 5",
    title: "CORPORATE SPONSORSHIPS.",
    subtitle: "Faster money. Different math. Very few founders ask right.",
    footer: "Marketing budgets and foundation budgets are two separate pockets — and marketing is often significantly larger.",
  },

  // 9 — WHAT SPONSORSHIPS ARE
  {
    kind: "opportunity",
    eyebrow: "Definition",
    title: "Sponsorships ≠ Grants.",
    subtitle: "You're not asking for charity. You're selling brand exposure.",
    bullets: [
      "A grant comes from a foundation's CHARITY budget — restricted, competitive, mission-fit driven.",
      "A sponsorship comes from a corporation's MARKETING budget — much larger, less restricted, ROI-driven.",
      "The corporation isn't donating; they're BUYING visibility, community goodwill, or customer acquisition.",
      "Your job: give them a value exchange, not a sob story. Sponsorship logos on your event = ad spend.",
      "Sponsorship deals close in weeks, not months. And the corporate side pays FAST.",
    ],
    closer: "The mental switch: stop asking for a favor. Start offering an audience.",
  },

  // 10 — CHEAT CODE #1
  {
    kind: "stat",
    spicy: true,
    eyebrow: "🎯 Cheat Code #1",
    bigNumber: "TWO POCKETS",
    headline: "Every big company has TWO giving pockets — and most founders only ask one.",
    body: "The foundation gives grants ($10K–$50K typical) with restrictions, deadlines, and mission-fit review. The marketing team writes sponsorship checks funded from a completely different budget — often much larger, faster to close, and evaluated on brand ROI instead of charity fit. Most founders only knock on the foundation door. The founders who also knock on the marketing door often unlock bigger checks with less competition.",
    sourceLine: "Framework: Council on Foundations glossary + Association of Fundraising Professionals corporate-giving guidance",
  },

  // 11 — HOW TO STRUCTURE
  {
    kind: "opportunity",
    eyebrow: "The pitch mechanics",
    title: "How to structure a sponsorship ask.",
    subtitle: "You are selling brand exposure. Sell it like a marketer would.",
    bullets: [
      "PACKAGES, not asks — Bronze ($1K), Silver ($5K), Gold ($10K), Presenting ($25K). Give them a menu.",
      "SPECIFIC BENEFITS per tier — logo placement, social mentions, stage time, VIP tickets, custom branding.",
      "REAL AUDIENCE NUMBERS — 'Our event reaches 3,000 attendees, plus 12,000 email subscribers, plus 8K IG.'",
      "DEADLINE — 'To be included in printed materials, commitment by August 15.' Deadlines close deals.",
      "IN-KIND OPTIONS — 'Or provide the venue, catering, or media buy.' Lowers the friction to a first 'yes.'",
    ],
    closer: "Local businesses (Grand Strand banks, hotels, restaurants, health systems) all have sponsorship budgets.",
  },

  // ============ PATH 3: IN-KIND ============

  // 12 — TITLE
  {
    kind: "title",
    eyebrow: "Path 3 of 5",
    title: "IN-KIND DONATIONS.",
    subtitle: "Equivalent to cash. Most under-asked. Fastest 'yes' available.",
    footer: "Every dollar of donated goods or services is a dollar you don't have to raise. And the donor gets a tax deduction.",
  },

  // 13 — WHAT IN-KIND IS
  {
    kind: "opportunity",
    eyebrow: "Definition",
    title: "What in-kind actually covers.",
    subtitle: "Anything a business would normally SELL, given to your org for free.",
    bullets: [
      "PROFESSIONAL SERVICES — Legal work, accounting, web design, printing, graphic design, PR.",
      "SPACE — Meeting rooms, event venues, storage, office space. Often the biggest single line item.",
      "GOODS — Food for events, office supplies, technology, furniture, uniforms.",
      "MEDIA — Advertising spots, sponsored social posts, newspaper placements.",
      "HOURS — Volunteer time at market rate (professional volunteers can be $100+/hour equivalent).",
    ],
    closer: "IRS recognizes in-kind donations as tax-deductible at fair market value. It's real money on paper.",
  },

  // 14 — CHEAT CODE #2
  {
    kind: "stat",
    spicy: true,
    eyebrow: "🎯 Cheat Code #2",
    bigNumber: "1:1",
    headline: "Every dollar of in-kind = one dollar you don't have to raise.",
    body: "A $2,000 donated venue for your gala = $2,000 you don't need in cash. A $5,000 pro-bono legal review of your bylaws = $5,000 you can spend on programs instead. In-kind doesn't feel as sexy as a grant check, but on the P&L it's identical. And the ask is dramatically easier: 'Can you cover printing?' beats 'Can you write us a $2K check?' every time.",
    sourceLine: "Source: IRS Publication 526 — Charitable Contributions valuation rules",
  },

  // 15 — HOW TO ASK
  {
    kind: "opportunity",
    eyebrow: "The ask",
    title: "How to ask for in-kind (right way).",
    subtitle: "Specific, small, and easy to say yes to.",
    bullets: [
      "BE SPECIFIC — 'Can you donate 200 branded t-shirts for our Sept 12 event?' beats 'Any support?'",
      "MATCH TO THEIR PRODUCT — Ask restaurants for food, printers for printing, hotels for room blocks.",
      "OFFER RECOGNITION — Logo on the item, mention in your program, social tag, newsletter shoutout.",
      "PROVIDE THE TAX DOC — Give them a written acknowledgment with fair market value estimated. Makes them look good to their accountant.",
      "ASK EARLY — In-kind donors need lead time. 60–90 days out beats 2 weeks out.",
    ],
    closer: "The Grand Strand has 2,000+ chamber-member businesses. Half of them will say yes to a specific, respectful in-kind ask.",
  },

  // ============ PATH 4: FISCAL SPONSORSHIP ============

  // 16 — TITLE
  {
    kind: "title",
    eyebrow: "Path 4 of 5",
    title: "FISCAL SPONSORSHIP.",
    subtitle: "Grant money for organizations that aren't 501(c)(3) — yet.",
    footer: "If you're building toward nonprofit status but haven't filed yet, this path unlocks grants you thought were off-limits.",
  },

  // 17 — WHAT IT IS
  {
    kind: "opportunity",
    eyebrow: "Definition",
    title: "What is fiscal sponsorship?",
    subtitle: "An established 501(c)(3) acts as your legal & financial umbrella.",
    bullets: [
      "You partner with an existing 501(c)(3) organization — the 'fiscal sponsor.'",
      "Donations to YOUR project flow through the sponsor's 501(c)(3) status.",
      "Donors get their tax deduction. Grant funders will accept the sponsor's status as your legal home.",
      "The sponsor typically charges 5–10% administrative fee off the top.",
      "Two models: Model A (Comprehensive — you're a project of the sponsor) and Model C (Pre-Approved Grant Relationship — narrower).",
    ],
    closer: "Fiscal sponsorship is not a shortcut. It's a legitimate legal structure used by thousands of orgs.",
    url: "councilofnonprofits.org/tools-resources/fiscal-sponsorship",
  },

  // 18 — CHEAT CODE #3
  {
    kind: "stat",
    spicy: true,
    eyebrow: "🎯 Cheat Code #3",
    bigNumber: "0 MONTHS",
    headline: "You can start accepting tax-deductible donations THIS WEEK.",
    body: "IRS 501(c)(3) approval takes 2–6 months (Form 1023-EZ) or 6+ months (Form 1023). Fiscal sponsorship: you can find a sponsor, sign an MOU, and start collecting funds within 2–4 weeks. That's the difference between raising money THIS quarter versus NEXT year. If you have a real program, a real board, and a real plan — sponsor first, formalize later.",
    sourceLine: "Source: National Council of Nonprofits Fiscal Sponsorship Guide",
  },

  // 19 — HOW TO FIND ONE
  {
    kind: "opportunity",
    eyebrow: "Practical steps",
    title: "How to find a fiscal sponsor.",
    subtitle: "Start local. Match mission. Understand the fee structure.",
    bullets: [
      "LOCAL FIRST — SC-based community foundations often act as fiscal sponsors (Coastal Community Foundation, Central Carolina Community Foundation).",
      "MISSION MATCH — Sponsor should share your general focus area. Arts nonprofit? Look for an arts fiscal sponsor.",
      "READ THE FEE — Typically 5–10% off donations. Some sponsors charge 3%. Some charge 15%. Read the agreement.",
      "ASK ABOUT SERVICES — Some sponsors handle bookkeeping, insurance, HR. Others just process the money. Both are legitimate.",
      "GET IT IN WRITING — MOU or Fiscal Sponsorship Agreement. Never verbal-only.",
    ],
    closer: "For SC founders, FlashGrants tracks fiscal sponsors on our /pathways directory — verified and current.",
    url: "flash-grants.com/pathways",
  },

  // ============ PATH 5: COMMUNITY LOANS ============

  // 20 — TITLE
  {
    kind: "title",
    eyebrow: "Path 5 of 5",
    title: "COMMUNITY LOANS.",
    subtitle: "0% interest. No credit check. Character-based lending.",
    footer: "Kiva U.S. has funded $200M+ in microloans to founders traditional banks turn away. And they operate in every SC county.",
  },

  // 21 — WHAT KIVA IS
  {
    kind: "opportunity",
    eyebrow: "Definition",
    title: "Kiva U.S. in one paragraph.",
    subtitle: "A 501(c)(3) that runs a global peer-to-peer microloan platform.",
    bullets: [
      "Loans up to $15,000. 0% interest. No fees. Up to 36-month repayment.",
      "NO PERSONAL CREDIT CHECK. NO COLLATERAL. NO COSIGNER.",
      "Instead, you recruit 15–25 personal endorsers who each lend $25+ publicly to your loan.",
      "Once endorsers hit target, Kiva's 2M+ global lender network funds the balance.",
      "Total application-to-funds: about 4–6 weeks.",
    ],
    closer: "For pre-revenue small businesses, minority-owned startups, and community nonprofits — this is often the cheapest capital on Earth.",
    url: "kiva.org/borrow",
  },

  // 22 — CHEAT CODE #4
  {
    kind: "stat",
    spicy: true,
    eyebrow: "🎯 Cheat Code #4",
    bigNumber: "$0",
    headline: "$0 interest. $0 fees. Only what you borrow, paid back.",
    body: "$15,000 at Kiva over 36 months = $416.67/month, no interest, no fees. The same $15,000 at a typical CDFI at 8% APR = $470/month plus ~$1,900 in total interest paid. Kiva saves the founder about $2,000 in real dollars — while also opening a path for founders whose personal credit disqualifies them from bank loans entirely.",
    sourceLine: "Source: Kiva.org public loan terms + comparative CDFI standard rates",
  },

  // 23 — ENDORSER STRATEGY
  {
    kind: "opportunity",
    eyebrow: "The critical piece",
    title: "The endorser piece — where most Kiva loans die.",
    subtitle: "You need 15–25 people who know you to lend publicly. Prep this BEFORE you apply.",
    bullets: [
      "TIER 1 — Family + close friends + church + former coworkers (target 10–12 confirmed yeses).",
      "TIER 2 — Business network, extended family, professional acquaintances (target 8–10 more).",
      "TIER 3 — Community reach — local business owners, chamber contacts, church leaders (target 5–8).",
      "Lock Tier 1 IN WRITING before you submit. When the Kiva application goes live, they must click and lend within 15 days.",
      "This is why community-rooted founders WIN Kiva — the platform rewards real human networks.",
    ],
    closer: "If you can name 25 people who'd vouch for you with $25 — you can raise $15K at 0%.",
  },

  // ============ THE STACK ============

  // 24 — TITLE
  {
    kind: "title",
    eyebrow: "Bringing it together",
    title: "THE STACK.",
    subtitle: "The founders who win use multiple paths at the same time.",
    footer: "One path = one bet. Five paths = a portfolio.",
  },

  // 25 — HOW TO STACK
  {
    kind: "opportunity",
    eyebrow: "The strategic move",
    title: "How to stack 3+ paths together.",
    subtitle: "Real example from a Grand Strand founder building right now.",
    bullets: [
      "PATH 5 (Kiva) — $15K bridge working capital while credit is being repaired.",
      "PATH 2 (Sponsorships) — $10K–25K from local corporate partners for opening events.",
      "PATH 3 (In-Kind) — $5K–15K of donated legal work, printing, media placement, and menu photography.",
      "PATH 4 (Fiscal Sponsorship) — Used ONLY if org is pre-501(c)(3) — pass-through for foundation grants.",
      "PATH 1 (Grants) — Applied LAST, after 6 months of runway from paths 2, 3, and 5 have proven traction.",
    ],
    closer: "Total unlocked in the stack: ~$40K–$60K in Year 1 without a single foundation grant closing.",
  },

  // 26 — THE FRAMEWORK
  {
    kind: "opportunity",
    eyebrow: "Choose your path",
    title: "How to know which path is right for you.",
    subtitle: "Two questions decide most of it.",
    bullets: [
      "ARE YOU 501(c)(3)? — If no, and you can't wait 4–6 months: Path 4 (Fiscal Sponsorship) or Path 5 (Kiva).",
      "DO YOU NEED CASH IN 60 DAYS? — Yes: Path 2 (Sponsorships), Path 3 (In-Kind), Path 5 (Kiva). No: Path 1 (Grants) is on the table.",
      "IS YOUR EVENT/PROGRAM THE ASSET? — Yes: Path 2 (Sponsorships) is your first move. Sell brand exposure.",
      "DO YOU HAVE 25 PEOPLE WHO'D VOUCH FOR YOU? — Yes: Path 5 (Kiva). No: Do Path 3 (In-Kind) first while building community.",
      "IF ALL THE ABOVE ARE 'YES' — Stack all five. That's how you get from $0 to $60K in Year 1.",
    ],
    closer: "Grants are one path. They should not be your ONLY path. And they should almost never be your FIRST path.",
  },

  // 27 — YOUR ASSIGNMENT
  {
    kind: "opportunity",
    eyebrow: "Your assignment",
    title: "Pick two paths this week.",
    subtitle: "Not five. Two. And take action on both by Sunday.",
    bullets: [
      "WEEK 1 — Pick your two paths from the framework above. Write them down.",
      "PATH A — Take one specific action this week. Email one corporate sponsor. Ask one business for one in-kind item. Or research two fiscal sponsors.",
      "PATH B — Take one specific action THIS WEEK on the second path. Two paths, two actions. That's it.",
      "WEEK 2 — Follow up on both. Move each one to a next step (meeting, signed MOU, sponsor commitment, in-kind confirmation).",
      "WEEK 4 — Look at what came in. THEN decide if a grant application is worth 20 hours this month.",
    ],
    closer: "Every founder who succeeded before you did this. They stacked. Start stacking.",
    url: "flash-grants.com",
  },
];
DECKS["best-grant-thursday"] = SLIDES_BEST_GRANT_THURSDAY;


// =============================================================================
// DECK: TOGETHERSC-AUG18 — Live-share deck for the Aug 18, 2026 meeting
// with Karen Riordan / Together SC. Mirrors the full pilot proposal doc at
// /playbook/doc/together_sc_pilot_proposal_v1.
// Route: /slides/togethersc-aug18/1
// =============================================================================
const SLIDES_TOGETHERSC_AUG18 = [
  // 1 — TITLE
  {
    kind: "title",
    eyebrow: "FlashGrants × Together SC · Aug 18, 2026",
    title: "A Complementary Pilot.",
    subtitle: "Serving the 80% Grantlytics wasn't built for.",
    footer: "Kennardo \"Greg\" James · Founder, FlashGrants · flash-grants.com",
  },

  // 2 — THE FRAME
  {
    kind: "stat",
    eyebrow: "The frame",
    bigNumber: "AND",
    headline: "Not \"instead of\" Grantlytics. Alongside it.",
    body: "Grantlytics is a strong fit for the segment of your members with dedicated development staff and the budget for $1,600+/yr in software. This pilot is for the segment that partnership doesn't reach.",
    sourceLine: "Together SC + Grantlytics partnership announcement · Grantlytics public pricing",
  },

  // 3 — THE SEGMENT
  {
    kind: "opportunity",
    eyebrow: "Who this pilot serves",
    title: "The segment Grantlytics wasn't built for.",
    subtitle: "Three overlapping criteria that describe most of your membership by headcount.",
    bullets: [
      "Annual operating budget under $500K.",
      "No dedicated development officer — the ED is the grant writer.",
      "Serves a specific SC county or region, not statewide or multi-state.",
      "Success depends more on knowing the SC ecosystem than filtering thousands of national grants.",
    ],
    closer: "This is the majority of Together SC's membership by headcount. And the minority by revenue.",
  },

  // 4 — WHAT'S ON THE PLATFORM
  {
    kind: "stat",
    eyebrow: "What's live today",
    bigNumber: "98",
    headline: "SC-native grants live on the platform. Every one primary-source verified.",
    body: "Weekly automated funder-status sweep. Grants auto-hide when the funder's own website shows closure language. No stale listings. No invented data. Ever.",
    sourceLine: "FlashGrants internal grant registry · verified daily against funder primary sources",
  },

  // 5 — THE KILLER FEATURE (Partnership → Coalition)
  {
    kind: "opportunity",
    eyebrow: "The differentiator",
    title: "Partnership → Coalition.",
    subtitle: "The layer no incumbent grant tool offers.",
    bullets: [
      "STEP 1 — Every grant detail page shows the SC nonprofits whose profile also fits that grant.",
      "STEP 2 — Members initiate an org-to-org connection (Form a Partnership) with visibility into mission, geography, capacity.",
      "STEP 3 — When a partnership is ready to co-apply, they formalize a Coalition inside FlashGrants: shared outcome, shared population, pooled ask.",
      "The platform auto-generates the MOU, Subgrant Agreements, and Coalition Setup Checklist from the coalition data.",
    ],
    closer: "Funders reward pooled impact. This workflow makes coalitions easy instead of accidental.",
  },

  // 6 — HANDS-ON LAYER
  {
    kind: "stat",
    eyebrow: "Founder-led support",
    bigNumber: "30 min",
    headline: "per quarter, per pilot member. 1:1 with the founder.",
    body: "Live grant-pipeline review. Application feedback. Funder-fit judgment calls. Not a support form. Not a chatbot. Not a rotating success manager. Priced deliberately — free founder-time doesn't scale and doesn't set the right expectations for a paid product.",
    sourceLine: "Retail rate: $150/session · Waived for pilot members during the 12-week term",
  },

  // 7 — PILOT MECHANICS
  {
    kind: "opportunity",
    eyebrow: "The ask",
    title: "12 weeks. 15 members. $0 to Together SC.",
    subtitle: "Sep 15 → Dec 7, 2026.",
    bullets: [
      "Cohort of 15 Together SC members — hand-selected by your team, not by us.",
      "Personal onboarding by Greg in Week 1 (~45 min per org).",
      "Weekly platform use + one 30-min hands-on call in weeks 4, 8, 12.",
      "Mid-pilot check-in (Week 6) + full outcomes report (Week 13) shared with Karen.",
      "At Week 13, each member decides: continue paid at $15/mo, pause, or exit. Together SC decides whether to formalize.",
    ],
    closer: "The pilot's real cost is my time. I'd rather invest it in 15 orgs I can point to than in marketing I can't measure.",
  },

  // 8 — MUTUAL SUCCESS CRITERIA
  {
    kind: "opportunity",
    eyebrow: "How we measure",
    title: "What we agree to measure. And what we don't.",
    subtitle: "Reported at Week 6 and Week 13. Honest metrics only.",
    bullets: [
      "MEMBERS: Grants surfaced · Applications initiated · Applications submitted · Partnerships formed · Coalitions formalized.",
      "TOGETHER SC: Member NPS (3-question survey delivered by your team) · Renewal signal at $15/mo · Sector-level case narrative.",
      "WE DO NOT MEASURE \"grant dollars won\" inside the 12-week window.",
      "SC grant cycles run 60–120 days from submission to decision. Awards from pilot applications land Q1 2027 at the earliest.",
      "Claiming those numbers inside the pilot window would be dishonest. So we won't.",
    ],
    closer: "Under-promising builds trust. Over-claiming gets you dropped in 18 months.",
  },

  // 9 — INVESTMENT
  {
    kind: "opportunity",
    eyebrow: "The economics",
    title: "Pilot investment. Zero on your side.",
    subtitle: "Members pay less than they would retail. Together SC pays nothing.",
    bullets: [
      "FlashGrants Starter tier (retail $19/mo) → $0 for pilot members during the 12 weeks.",
      "Founder hands-on (retail $150/session × 3) → $0 for pilot members during the 12 weeks.",
      "Together SC pilot fee → $0.",
      "Post-pilot member pricing (if pilot extends) → $15/mo, a 21% discount for Together SC members. Matches or beats the Grantlytics discount.",
    ],
    closer: "This isn't a discount promo. It's a commitment to build value first and price second.",
  },

  // 10 — WHAT WE ARE *NOT* PROMISING
  {
    kind: "opportunity",
    eyebrow: "Trust-building",
    title: "What FlashGrants is not promising.",
    subtitle: "Because under-promising is how the relationship earns a second year.",
    bullets: [
      "NOT the best AI proposal-writing tool. That's Grantlytics's strength today. Our Application Co-Pilot is on the roadmap, not shipped.",
      "NOT a national platform. SC-native is the strategy — narrowing focus, not broadening it.",
      "NOT white-glove grant-writing service. Hands-on is 30 min/quarter of judgment, not 30 hours/month of writing.",
      "NOT going to email your membership without your explicit sign-off. Every touchpoint routes through Karen's team.",
    ],
    closer: "If any of these change, I'll tell you before I ship them — not after.",
  },

  // 11 — THREE EXIT PATHS
  {
    kind: "opportunity",
    eyebrow: "What happens at Week 13",
    title: "Three legitimate exits. All decided jointly.",
    subtitle: "This is a \"learn together and decide together\" pilot — not a convert-or-fail ask.",
    bullets: [
      "OPTION 1 — Formalize as ongoing member benefit. Together SC endorses FlashGrants as a complementary tool at the $15/mo rate. Rev-share terms negotiable.",
      "OPTION 2 — Sunset gracefully. If the outcomes don't warrant it, we shut down clean. Members retain their accounts at retail if they wish. Your endorsement is not on the line.",
      "OPTION 3 — Iterate on scope. Adjust cohort size, feature set, or delivery model based on what the pilot data reveals. Run a second cycle.",
    ],
    closer: "All three are real outcomes. None of them cost you political capital.",
  },

  // 12 — WHAT WE NEED FROM YOU
  {
    kind: "opportunity",
    eyebrow: "The lift on your side",
    title: "What Together SC is being asked to provide.",
    subtitle: "Small. Defined. Time-boxed.",
    bullets: [
      "15 hand-selected members. Karen's team picks the cohort. We do not pick.",
      "One introductory email to the cohort at pilot kickoff, from Karen's office.",
      "Distribution of a 3-question NPS survey at Week 13.",
      "A 30-minute mid-pilot check-in call with Greg at Week 6.",
    ],
    closer: "That's it. No integrations. No data handoffs. No legal work beyond a 1-page mutual pilot agreement.",
  },

  // 13 — THE ONE-LINE ASK
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The ask",
    bigNumber: "3 dates",
    headline: "Signed agreement by Fri Sep 5. Cohort by Fri Sep 12. Kickoff Mon Sep 15.",
    body: "One-page mutual pilot agreement, no exclusivity clause (protects your Grantlytics relationship), 14-day termination clause either way. Draft attached as Appendix A of the written proposal.",
    sourceLine: "Full written proposal: flash-grants.com/playbook/doc/together_sc_pilot_proposal_v1",
  },

  // 14 — CLOSE
  {
    kind: "title",
    eyebrow: "The close",
    title: "SC-native. Hands-on. Partnership → Coalition first.",
    subtitle: "That's the strategy. That's the pilot. That's the ask.",
    footer: "Kennardo \"Greg\" James · flash-grants.com · Aug 18, 2026",
  },
];
DECKS["togethersc-aug18"] = SLIDES_TOGETHERSC_AUG18;


const SlideShell = ({ children, accent = false, idx, total }) => (
  <div
    className={`relative w-screen h-screen overflow-hidden ${
      accent ? "bg-gradient-to-br from-emerald-950 via-black to-black" : "bg-black"
    }`}
    data-testid={`founder-slide-${idx + 1}`}
  >
    {/* Diagonal grain texture */}
    <div
      className="absolute inset-0 opacity-[0.04] pointer-events-none"
      style={{
        backgroundImage:
          "repeating-linear-gradient(45deg, rgba(255,255,255,0.6) 0, rgba(255,255,255,0.6) 1px, transparent 1px, transparent 12px)",
      }}
    />
    {/* Top corner mark */}
    <div className="absolute top-10 left-12 flex items-center gap-2.5 text-zinc-300">
      <Lightning weight="fill" className="w-7 h-7 text-emerald-400" />
      <span className="font-bold tracking-tight text-2xl">FlashGrants</span>
    </div>
    {/* Slide # / total */}
    <div className="absolute top-12 right-12 text-zinc-500 text-sm font-mono">
      {String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
    </div>
    {/* Big emerald glow accent */}
    <div className="absolute -top-32 -right-32 w-[600px] h-[600px] bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none" />
    <div className="absolute -bottom-32 -left-32 w-[700px] h-[700px] bg-emerald-700/8 rounded-full blur-[140px] pointer-events-none" />

    <div className="relative w-full h-full flex">{children}</div>
  </div>
);

const StatSlide = ({ slide, idx, total }) => (
  <SlideShell idx={idx} total={total} accent={slide.spicy}>
    <div className="w-full h-full flex flex-col justify-center px-24 max-w-[1700px] mx-auto">
      <div
        className={`inline-flex self-start items-center gap-2 px-3.5 py-1 rounded-full mb-10 text-xs font-bold uppercase tracking-[0.18em] ${
          slide.spicy
            ? "bg-amber-400/15 border border-amber-400/40 text-amber-200"
            : "bg-emerald-500/15 border border-emerald-500/40 text-emerald-300"
        }`}
      >
        {slide.spicy && <Star weight="fill" className="w-3.5 h-3.5" />}
        {slide.eyebrow}
      </div>

      <div
        className={`font-black leading-none mb-10 tracking-tight ${
          slide.spicy ? "text-amber-300" : "text-emerald-400"
        }`}
        style={{ fontSize: "clamp(150px, 19vw, 300px)" }}
      >
        {slide.bigNumber}
      </div>

      <div className="text-white font-bold tracking-tight leading-[1.05] mb-7 max-w-[1400px]" style={{ fontSize: "clamp(40px, 4vw, 64px)" }}>
        {slide.headline}
      </div>

      <p className="text-zinc-300 leading-snug max-w-[1100px]" style={{ fontSize: "clamp(22px, 1.6vw, 28px)" }}>
        {slide.body}
      </p>

      <p className="absolute bottom-10 left-24 right-24 text-xs text-zinc-600 max-w-[1000px]">
        {slide.sourceLine}
      </p>
    </div>
  </SlideShell>
);

const OpportunitySlide = ({ slide, idx, total }) => (
  <SlideShell idx={idx} total={total} accent>
    <div className="w-full h-full flex flex-col justify-center px-24 max-w-[1700px] mx-auto">
      <div className="inline-flex self-start items-center gap-2 px-3.5 py-1 rounded-full mb-8 text-xs font-bold uppercase tracking-[0.18em] bg-emerald-500/20 border border-emerald-500/50 text-emerald-200">
        <Lightning weight="fill" className="w-3.5 h-3.5" />
        {slide.eyebrow}
      </div>

      <h2 className="text-white font-black tracking-tight leading-none mb-3" style={{ fontSize: "clamp(64px, 6vw, 110px)" }}>
        {slide.title}
      </h2>
      <p className="text-emerald-400 font-bold mb-12 leading-tight" style={{ fontSize: "clamp(28px, 2.2vw, 40px)" }}>
        {slide.subtitle}
      </p>

      <ul className="space-y-4 mb-10 max-w-[1400px]">
        {slide.bullets.map((b) => {
          const isObj = typeof b === "object" && b !== null;
          const text = isObj ? b.text : b;
          const highlighted = isObj && b.highlighted;
          return (
            <li
              key={text}
              className={
                highlighted
                  ? "flex items-start gap-4 text-amber-100"
                  : "flex items-start gap-4 text-zinc-100"
              }
              style={{ fontSize: "clamp(22px, 1.7vw, 30px)" }}
              data-testid={highlighted ? "bullet-highlighted" : undefined}
            >
              {highlighted ? (
                <Star
                  weight="fill"
                  className="mt-1 w-6 h-6 text-amber-300 flex-shrink-0"
                />
              ) : (
                <span className="mt-2 w-2.5 h-2.5 bg-emerald-400 rounded-full flex-shrink-0" />
              )}
              <span
                className={
                  highlighted ? "leading-snug font-bold" : "leading-snug"
                }
              >
                {text}
                {highlighted && b.tag && (
                  <span className="ml-3 inline-block px-2.5 py-0.5 rounded-full text-xs font-black uppercase tracking-[0.15em] bg-amber-300 text-black align-middle">
                    {b.tag}
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      <p className="text-zinc-300 italic max-w-[1300px] leading-snug border-l-2 border-emerald-500/50 pl-5" style={{ fontSize: "clamp(20px, 1.4vw, 26px)" }}>
        {slide.closer}
      </p>

      {slide.url && (
        <p className="absolute bottom-10 left-24 text-zinc-500 font-mono text-base">
          →{" "}
          <a
            href={slide.url.startsWith("http") ? slide.url : `https://${slide.url}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-400 hover:text-emerald-300 underline decoration-emerald-500/40 decoration-1 underline-offset-4 transition-colors"
            data-testid="slide-url-link"
          >
            {slide.url}
          </a>
        </p>
      )}
    </div>
  </SlideShell>
);

const TitleSlide = ({ slide, idx, total }) => (
  <SlideShell idx={idx} total={total} accent>
    <div className="w-full h-full flex flex-col justify-center px-24 max-w-[1700px] mx-auto">
      {slide.eyebrow && (
        <div className="inline-flex self-start items-center gap-2 px-3.5 py-1 rounded-full mb-10 text-xs font-bold uppercase tracking-[0.18em] bg-emerald-500/15 border border-emerald-500/40 text-emerald-300">
          {slide.eyebrow}
        </div>
      )}
      <h1 className="text-white font-black tracking-tighter leading-[0.95] mb-2" style={{ fontSize: "clamp(80px, 8.5vw, 160px)" }}>
        {slide.title}
      </h1>
      <h2 className="text-emerald-400 font-black tracking-tighter leading-[0.95] mb-16" style={{ fontSize: "clamp(60px, 6.5vw, 120px)" }}>
        {slide.subtitle}
      </h2>
      <p className="text-zinc-400 max-w-[1200px] leading-relaxed" style={{ fontSize: "clamp(24px, 1.6vw, 32px)" }}>
        {slide.footer}
      </p>
    </div>
  </SlideShell>
);

const QrSlide = ({ slide, idx, total }) => {
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=600x600&margin=10&data=${encodeURIComponent(slide.qrUrl)}`;
  return (
    <SlideShell idx={idx} total={total} accent>
      <div className="w-full h-full grid grid-cols-[1.1fr_1fr] gap-16 items-center px-24 max-w-[1700px] mx-auto">
        <div className="flex flex-col justify-center">
          {slide.eyebrow && (
            <div className="inline-flex self-start items-center gap-2 px-3.5 py-1 rounded-full mb-8 text-xs font-bold uppercase tracking-[0.18em] bg-emerald-500/20 border border-emerald-500/50 text-emerald-200">
              {slide.eyebrow}
            </div>
          )}
          <h1 className="text-white font-black tracking-tighter leading-[0.95] mb-3" style={{ fontSize: "clamp(48px, 5.2vw, 88px)" }}>
            {slide.title}
          </h1>
          <h2 className="text-emerald-400 font-black tracking-tighter leading-[0.95] mb-8" style={{ fontSize: "clamp(36px, 3.8vw, 64px)" }}>
            {slide.subtitle}
          </h2>
          <p className="text-zinc-300 leading-snug mb-6" style={{ fontSize: "clamp(20px, 1.4vw, 26px)" }}>
            {slide.body}
          </p>
          <p className="text-zinc-500 text-sm leading-relaxed max-w-[600px]">
            {slide.footer}
          </p>
        </div>
        <div className="flex flex-col items-center justify-center" data-testid="qr-slide-code">
          <div className="bg-white p-6 rounded-2xl shadow-[0_0_60px_rgba(16,185,129,0.25)]">
            <img
              src={qrSrc}
              alt={`QR code linking to ${slide.shortUrl}`}
              className="w-[420px] h-[420px] block"
            />
          </div>
          <div className="mt-6 px-4 py-2 rounded-full bg-zinc-900/80 border border-zinc-700 text-emerald-300 font-mono text-sm tracking-tight">
            {slide.shortUrl}
          </div>
        </div>
      </div>
    </SlideShell>
  );
};

const FounderSlides = () => {
  const { n, deck } = useParams();

  // Support /slides/:deckSlug (no slide number) — infer deck from the URL param.
  // Without this, /slides/best-grant-thursday treats "best-grant-thursday" as a
  // slide number, falls back to the default deck. This branch fixes that.
  let actualDeck = deck;
  let actualN = n;
  if (!deck && n && DECKS[n]) {
    actualDeck = n;
    actualN = "1";
  }

  const deckKey = actualDeck && DECKS[actualDeck] ? actualDeck : "default";
  const slides = DECKS[deckKey];
  const idx = Math.max(0, Math.min(parseInt(actualN, 10) - 1 || 0, slides.length - 1));
  const slide = slides[idx];

  const basePath = deckKey === "default" ? "/slides" : `/slides/${deckKey}`;

  let body = null;
  if (slide.kind === "stat") body = <StatSlide slide={slide} idx={idx} total={slides.length} />;
  else if (slide.kind === "opportunity") body = <OpportunitySlide slide={slide} idx={idx} total={slides.length} />;
  else if (slide.kind === "qr") body = <QrSlide slide={slide} idx={idx} total={slides.length} />;
  else body = <TitleSlide slide={slide} idx={idx} total={slides.length} />;

  return (
    <div className="bg-black" data-testid="founder-slides-root">
      {body}
      {/* Invisible full-edge tap zones — left half = prev, right half = next.
          Works around the platform-injected "Made with Emergent" badge at
          bottom-right that blocks small nav buttons on mobile. Zones sit
          BELOW the slide content z-index, but ABOVE most decorative layers,
          and EXCLUDE the bottom 88px on the right so the Emergent badge
          itself is still clickable if the user wants to dismiss it. */}
      <Link
        to={idx > 0 ? `${basePath}/${idx}` : `${basePath}/1`}
        aria-label="Previous slide"
        className="fixed top-0 left-0 w-1/2 h-full z-40 print:hidden"
        data-testid="slide-tap-prev"
        style={{ background: "transparent" }}
      />
      <Link
        to={idx < slides.length - 1 ? `${basePath}/${idx + 2}` : `${basePath}/${slides.length}`}
        aria-label="Next slide"
        className="fixed top-0 right-0 w-1/2 z-40 print:hidden"
        data-testid="slide-tap-next"
        style={{ background: "transparent", height: "calc(100% - 88px)" }}
      />
      {/* Visible affordance — small buttons in bottom-LEFT so Emergent badge
          doesn't cover them. Still useful as a hint to first-time viewers. */}
      <div className="fixed bottom-4 left-4 z-50 flex gap-2 print:hidden">
        {idx > 0 && (
          <Link
            to={`${basePath}/${idx}`}
            className="px-3 py-1.5 rounded-sm bg-zinc-900/80 border border-zinc-700 text-zinc-300 text-xs hover:bg-zinc-800"
            data-testid="slide-prev"
          >
            ← Prev
          </Link>
        )}
        {idx < slides.length - 1 && (
          <Link
            to={`${basePath}/${idx + 2}`}
            className="px-3 py-1.5 rounded-sm bg-emerald-500/80 border border-emerald-400 text-black text-xs font-semibold hover:bg-emerald-400"
            data-testid="slide-next"
          >
            Next →
          </Link>
        )}
      </div>
    </div>
  );
};

export default FounderSlides;
// =============================================================================
// DECK: INKIND-THURSDAY — In-Kind Donations Deep Dive
// Requested by tonight's 7:30 PM meeting attendees. Route: /slides/inkind-thursday
// =============================================================================
const SLIDES_INKIND_THURSDAY = [
  {
    kind: "title",
    title: "In-Kind Donations:",
    subtitle: "The Free Money Hiding in Plain Sight.",
    bullets: [
      "Best Grant Thursday · Deep Dive",
      "Every category. Every ask. Every trick.",
    ],
    url: "flash-grants.com",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The Reality",
    bigNumber: "$1 = $1",
    headline: "In-kind is real money on your P&L.",
    body: "A donated venue worth $2,000 is $2,000 you don't have to raise. A $5,000 pro-bono legal review is $5,000 you can redirect to programs. The IRS treats it as tax-deductible at fair market value. Funders count it as match. But 90% of small nonprofits leave it on the table because 'it doesn't feel like real money.' It absolutely does.",
    sourceLine: "Source: IRS Publication 526 — Charitable Contributions valuation rules",
  },
  {
    kind: "opportunity",
    eyebrow: "Definition",
    title: "What in-kind actually covers.",
    subtitle: "Anything a business would normally SELL — given to your org for free.",
    bullets: [
      "PROFESSIONAL SERVICES — Legal, accounting, web design, PR, graphic design, printing, IT.",
      "SPACE — Meeting rooms, event venues, storage, office. Often the biggest line item.",
      "GOODS — Food, supplies, tech, furniture, uniforms, gift bags, silent auction items.",
      "MEDIA — Ad spots, sponsored social posts, newspaper placements, radio underwriting.",
      "HOURS — Skilled volunteer time at market rate ($100+/hour equivalent for professionals).",
    ],
    closer: "If a business sells it, someone will donate it. You just have to ask the right way.",
  },
  {
    kind: "opportunity",
    eyebrow: "The 3 rules of asking",
    title: "Ask like a pro, not a beggar.",
    subtitle: "Specific. Small. Easy to say yes to.",
    bullets: [
      "BE SPECIFIC — 'Can you donate 200 branded T-shirts for our Sept 12 event?' crushes 'Any support?'",
      "MATCH TO THEIR PRODUCT — Restaurants → food. Printers → printing. Hotels → room blocks. Barbers → haircuts. Salons → hair/beauty services.",
      "MAKE IT A DECISION, NOT A CONVERSATION — 'Yes or no, and if no, who do you think would say yes?' Always give them an easy exit that keeps them helpful.",
    ],
    closer: "The riskier the ask, the LESS likely they say yes. Small + specific = 60%+ hit rate.",
  },
  {
    kind: "opportunity",
    eyebrow: "Where to look FIRST",
    title: "Your five closest wins are within 3 miles.",
    subtitle: "Start local. Start warm. Start small.",
    bullets: [
      "1. YOUR OWN NETWORK — Who owns a business you already do business with? Start there.",
      "2. YOUR HAIR SALON / BARBER — They know EVERY small business in the neighborhood. One conversation opens 20 doors.",
      "3. YOUR PLACE OF WORSHIP — Congregation-owned businesses are often the fastest yes.",
      "4. RESTAURANTS YOU FREQUENT — Free catering, gift cards for raffles, private dining space.",
      "5. YOUR PRINT SHOP / COPY CENTER — Programs, flyers, signage — often the biggest hidden line item.",
    ],
    closer: "Every business owner has a favorite cause. The question is whether they know yours yet.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "🎯 Cheat Code",
    bigNumber: "60-90",
    headline: "Days of lead time = 3x the yes rate.",
    body: "Asking for in-kind two weeks before your event? You'll get a polite 'we're already booked.' Asking 90 days out? You get on the calendar, in the marketing budget, and often paired with a paid sponsorship on top. Business calendars work in quarters. Adjust your ask timeline to match theirs, not yours.",
    sourceLine: "Rule of thumb: for any event, in-kind asks go out the same day the venue is booked.",
  },
  {
    kind: "opportunity",
    eyebrow: "The tax doc power move",
    title: "Give them a written acknowledgment. Every time.",
    subtitle: "Makes them look good. Locks in the relationship for next year.",
    bullets: [
      "WITHIN 30 DAYS OF THE DONATION — Send a written acknowledgment letter with your EIN, the item/service donated, and your best estimate of fair market value.",
      "SIGN IT — Executive director or board chair. On letterhead. Not an email.",
      "REFERENCE IRS PUB 526 — Signal you know the rules. Businesses' accountants will love you.",
      "SEND A THANK-YOU BEYOND THE TAX DOC — Public shoutout on social. Photo of them at your event. Mention in your annual report.",
      "ASK FOR THE NEXT YEAR IN THE SAME LETTER — 'We hope to work with you again in 2027.'",
    ],
    closer: "One clean tax doc + one warm thank-you = a recurring in-kind partner for 5+ years.",
  },
  {
    kind: "opportunity",
    eyebrow: "Advanced play",
    title: "Pro-bono professional services.",
    subtitle: "The biggest hidden gold in the nonprofit world.",
    bullets: [
      "LEGAL — Every big law firm has a pro-bono committee with a QUOTA to hit. Ask for bylaws review, contract templates, IP work. Nelson Mullins, McNair, Haynsworth all take SC nonprofits.",
      "ACCOUNTING — Big-4 and mid-market firms give pro-bono to audit small nonprofits. BDO USA (see BLUSH's partnership) matches employee volunteer time with dollars.",
      "MARKETING — Ad agencies and PR firms often donate a 'pro-bono client' slot each year. Ask.",
      "WEB / TECH — Local web dev shops, WordPress agencies, and IT consultants routinely donate their skills to align with community engagement goals.",
      "MOCK-UP THE ASK — Draft the specific work you want them to do BEFORE the meeting. Makes it easy to say yes.",
    ],
    closer: "A single pro-bono partnership can be worth $10,000–$50,000 per year in retained services.",
  },
  {
    kind: "opportunity",
    eyebrow: "The trap to avoid",
    title: "In-kind is not a substitute for cash.",
    subtitle: "Know when to say no to a donated item.",
    bullets: [
      "IF YOU CAN'T USE IT, DON'T ACCEPT IT — A donated warehouse of expired paint helps nobody.",
      "IF IT COSTS YOU MONEY TO USE IT, IT'S NOT A GIFT — Free van that needs $3,000 in repairs = liability, not asset.",
      "DON'T LET IN-KIND REPLACE PAID STAFF — Volunteer hours are golden, but they can't run your operations.",
      "TRACK EVERY DONATION — Your 990 requires it. Your funders will ask for it. Build the tracker on Day 1.",
      "SAY THANK YOU LIKE YOU MEAN IT — Ungracious nonprofits don't get second gifts.",
    ],
    closer: "The best in-kind is the item you would've paid cash for anyway. Everything else is clutter with a bow.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "Real Talk",
    bigNumber: "80%",
    headline: "Of small SC business owners have never been asked for in-kind.",
    body: "Every Chamber of Commerce, every Rotary Club, every small-town Main Street has business owners who give when asked — and give NOTHING when nobody asks. The reason isn't stinginess. It's invisibility. You are competing with silence, not with other nonprofits. Silence is easy to beat.",
    sourceLine: "Ask 10 SC business owners this month. You'll get 4 yeses. Guaranteed.",
  },
  {
    kind: "opportunity",
    eyebrow: "The ask template",
    title: "The 4-sentence email/DM that works.",
    subtitle: "Copy this. Paste this. Send this today.",
    bullets: [
      "SENTENCE 1 — 'Hi [name], I'm [your name] with [org] and I've been a fan of [specific thing about their business] for a while.'",
      "SENTENCE 2 — 'We're hosting [specific event/need] on [date] serving [specific population].'",
      "SENTENCE 3 — 'Would [specific business owner] be open to donating [specific item/service] in exchange for [logo placement, tax deduction, thank-you spotlight]?'",
      "SENTENCE 4 — 'If no, no worries — but I'd love to grab coffee sometime and hear how you built [their business].'",
      "PRE-501(c)(3)? — Either route the donation through your fiscal sponsor (deduction stays intact) OR be upfront: 'We're not 501(c)(3) approved YET, so this wouldn't be tax-deductible — but we'll formally re-acknowledge it once we are.' Small local donors say yes to good people, not just to write-offs.",
    ],
    closer: "60% yes rate. Sentence 4 is what makes the 'no' still turn into a relationship.",
  },
  {
    kind: "closer",
    title: "Your homework tonight.",
    subtitle: "Before you go to bed — pick one and take action.",
    bullets: [
      "ACTION 1 — Text or DM ONE business owner you already know. Use the 4-sentence template above.",
      "ACTION 2 — Make a list of the 10 businesses within 3 miles of your org whose products/services you already need.",
      "ACTION 3 — Draft your in-kind tax acknowledgment letter template. One page. Save it as a Google Doc so it's ready when your first in-kind donor says yes.",
      "ACTION 4 — Add an 'In-Kind Received' line item to your budget for 2026. Track it monthly.",
      "ACTION 5 — Report back next Thursday with your first in-kind commitment. We'll celebrate it together.",
    ],
    closer: "Every dollar of in-kind is a dollar you don't have to raise. Start stacking.",
    url: "flash-grants.com",
  },
];
DECKS["inkind-thursday"] = SLIDES_INKIND_THURSDAY;


// =============================================================================
// DECK: SUSTAINABILITY-THURSDAY — Building Real Sustainability
// Requested for tonight's meeting. Route: /slides/sustainability-thursday
// Themes: WCF insights (Mike Mancuso), 5 revenue streams, contracts for
// nonprofits, things NOT to ask a foundation for, hiring the right people.
// =============================================================================
const SLIDES_SUSTAINABILITY_THURSDAY = [
  {
    kind: "title",
    title: "Sustainability:",
    subtitle: "Why Great Nonprofits Get Weaker Over Time.",
    bullets: [
      "Best Grant Thursday · Deep Dive",
      "What community foundations wish every nonprofit already knew.",
    ],
    url: "flash-grants.com",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The Reality",
    bigNumber: "40%",
    headline: "The moment ONE funder is more than 40% of your budget, you're not a nonprofit — you're a subsidiary.",
    body: "The 60/40 rule is the widely-taught benchmark in nonprofit finance: no single funder should ever exceed 40% of your annual revenue. Cross that line, and one grant-cycle miss can end programs, force layoffs, or close doors. Foundations know this benchmark. When they see resilience in your financials, they lean in. When they see fragility, they quietly step back.",
    sourceLine: "Source: Standard nonprofit finance guidance — the same threshold used by every major community foundation in their grantmaking review.",
  },
  {
    kind: "opportunity",
    eyebrow: "What a community foundation officer told us this week",
    title: "Foundations are tired of being your only lifeline.",
    subtitle: "The signal underneath every polite \"no.\"",
    bullets: [
      "\"We see the SAME orgs coming back every year for the SAME thing.\" — Foundations are looking for orgs that build sustainability, not orgs that build funder-dependence.",
      "Foundations LOVE funding growth. LOVE funding pilots. Get quietly exhausted funding rent and salaries year after year.",
      "The orgs that get MORE money over time are the ones whose reports show OTHER revenue growing ALONGSIDE the foundation gift.",
      "Grants are seasonal. Grants are competitive. Grant themes shift every time a program officer turns over. Building your whole revenue model on grants is building a house on sand.",
    ],
    closer: "The foundation is not the future. The foundation is the runway. The plane has to take off.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The Ask That Got Remembered",
    bigNumber: "$0",
    headline: "\"Can you fund my film?\" — a real ask sent to a South Carolina community foundation this year.",
    body: "This week we heard from a community foundation officer that someone asked them to fund a film. That foundation funds community impact — not indie productions. Every foundation has a MISSION, a THEORY OF CHANGE, and a GEOGRAPHY. Ask outside those three boxes, and you don't just lose the grant — you burn the relationship. Read the guidelines. All of them. Then read them again.",
    sourceLine: "The person who asked will not get a second look. Foundation staff talk to each other.",
  },
  {
    kind: "opportunity",
    eyebrow: "Save the ask. Save the relationship.",
    title: "Things NOT to ask a community foundation for.",
    subtitle: "What foundations will politely reject — and quietly remember.",
    bullets: [
      "FILMS, ALBUMS, ART PROJECTS with no community-engagement plan — unless the funder is explicitly an ARTS funder.",
      "INDIVIDUAL SCHOLARSHIPS unless the funder runs a specific scholarship program (most community foundations don't).",
      "CAPITAL for a building the funder never signaled interest in — capital is a relationship ask, not a cold application.",
      "ONGOING SALARIES unless \"general operating support\" is explicitly named in the RFP.",
      "REPLACEMENT OF LOST GOVERNMENT FUNDING — foundations are not the emergency room for federal budget cuts.",
      "TRAVEL, CONFERENCE FEES, RETREATS unless tied to a specific program deliverable.",
    ],
    closer: "Every rejected ask that fits the funder can lead to a bigger yes later. Every rejected ask that DIDN'T fit becomes a story the staff tell each other.",
  },
  {
    kind: "opportunity",
    eyebrow: "The five streams",
    title: "Five income streams. Not one. Not two. Five.",
    subtitle: "If a single funder disappears tomorrow, no more than 20% of your budget goes with them.",
    bullets: [
      "1. GRANTS — Foundation + government grants. The runway, not the destination.",
      "2. INDIVIDUAL DONORS — Monthly recurring giving, major gifts, annual campaigns. The most stable dollar you can raise.",
      "3. EARNED REVENUE — Fee-for-service, program income, mission-aligned enterprise. Nonprofit does NOT mean can't earn money.",
      "4. CONTRACTS — Corporate AND government contracts. Yes — you read that right. More in a moment.",
      "5. IN-KIND & SPONSORSHIPS — Trade for value, corporate partnerships, event sponsors, pro bono services.",
    ],
    closer: "A 20/20/20/20/20 revenue split is a bulletproof nonprofit. A 90% grant-funded nonprofit is a lit fuse.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The Most Loyal Dollar",
    bigNumber: "80%",
    headline: "Monthly recurring donors renew at 80%+. One-time annual donors renew at 20-45%.",
    body: "The single most stable revenue stream in nonprofit finance is the monthly recurring donor. Not the big check. Not the gala table. The $25-a-month donor who signed up three years ago and stopped noticing the debit. National benchmark data all land in the same range: monthly donors retain at 80%+; one-time annual donors retain at 20-45%. Build the recurring engine, and your budget stops depending on next year's grant cycle.",
    sourceLine: "Sources: M+R Benchmarks 2025, Bloomerang Fundraising Effectiveness Project, Classy 'Why America Gives.'",
  },
  {
    kind: "opportunity",
    eyebrow: "3 moves to start a monthly donor engine",
    title: "The recurring donor engine — from zero in 90 days.",
    subtitle: "You don't need a database. You don't need a CRM. You need a decision.",
    bullets: [
      "MOVE 1 — Your board chair leads. ONE board member signs up as a monthly donor at $50-100/mo. Announce it at the next board meeting. Peer momentum does the rest.",
      "MOVE 2 — Convert ONE annual donor to recurring. Call your top 5 individual donors from last year. Ask each: \"Would you be open to converting your annual gift to monthly — same total, spread over 12?\" Two of five say yes.",
      "MOVE 3 — Add a \"give monthly\" toggle to your existing donate page. Default it to monthly. Default toggles alone increase monthly conversions 40-60%.",
      "MOVE 4 — Every gift acknowledgment: \"Thank you. Would you consider making this recurring next year?\" Ask at the peak of gratitude.",
      "MOVE 5 — Public wall. Recognize your monthly donors publicly (with permission). Nothing recruits the next monthly donor like seeing their peers on the list.",
    ],
    closer: "Ten monthly donors at $50/mo is $6,000/year. Fifty of them is $30,000/year. Every single one comes back next year without a grant application.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The Board Reality",
    bigNumber: "100%",
    headline: "High-performing nonprofits have 100% board giving. Not most. All.",
    body: "BoardSource's national research consistently shows: boards with 100% giving participation raise 3-4x more from external donors than boards with partial participation. It's not because the board dollars are large. It's because major donors, foundations, and grant reviewers ASK: \"Does 100% of your board give?\" A \"no\" or \"we're not sure\" is a red flag on your funding application. A \"yes\" is a green light — and it costs your board members nothing to be personally meaningful.",
    sourceLine: "Source: BoardSource 'Leading with Intent' — the standard reference for nonprofit board benchmarks.",
  },
  {
    kind: "opportunity",
    eyebrow: "The board giving conversation",
    title: "How to ask your board — without begging.",
    subtitle: "The give-or-get expectation, spoken out loud.",
    bullets: [
      "PUT THE POLICY IN WRITING — Your board policy should state: \"Every board member is expected to make a personally meaningful gift AND help raise $X within their term.\" \"Personally meaningful\" means it's real to THEM, not to you.",
      "GIVE THEM THE NUMBER — \"This year we're asking every board member to give OR get $5,000.\" Vague expectations produce vague results.",
      "SEPARATE \"GIVE\" FROM \"GET\" — Some board members can write a check. Others open doors. Both count.",
      "MAKE IT PART OF ONBOARDING — Every new board member signs an expectations letter that includes the giving requirement. No surprises 12 months in.",
      "TRACK IT INTERNALLY — Share board-giving progress at every board meeting. Peer accountability works better than any ED speech.",
      "IF A MEMBER CAN'T MEET THE ASK — Have the conversation early. Some members become \"advisors\" instead. That's a healthy outcome, not a failure.",
    ],
    closer: "A board that can't or won't raise money isn't a board. It's a club with letterhead.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "🎯 The Hidden Lane",
    bigNumber: "$700B+",
    headline: "The federal government spent over $700 billion on contracts last year alone.",
    body: "Nonprofits are eligible to bid on almost all of it. Youth services. Housing. Health outreach. Workforce development. Food distribution. Cultural programming. Printing. Consulting. Every single one has federal contract set-asides that nonprofits win regularly. Contracts pay on DELIVERY, not on HOPE. Fixed price, negotiated scope, real cash flow.",
    sourceLine: "Source: USAspending.gov / FPDS federal contract obligations, FY2024.",
  },
  {
    kind: "opportunity",
    eyebrow: "Yes — nonprofits win contracts",
    title: "Contracts change your P&L. Grants change your calendar.",
    subtitle: "Same dollars. Very different behavior.",
    bullets: [
      "CONTRACTS pay you for deliverables, not for the story. You get paid when you do the work.",
      "CONTRACTS renew — a good contract comes back next year with a bigger scope.",
      "CONTRACTS build institutional muscle — invoicing, project management, deliverable tracking — which make your GRANT reports better too.",
      "CONTRACT SET-ASIDES — SDVOSB (veteran-owned), 8(a) (minority-owned), HUBZone (rural), WOSB (women-owned). Nonprofits with the right structure qualify.",
      "LOCAL FIRST — Your county, your city, your school district, your state agencies all procure services. Every RFP is a door.",
    ],
    closer: "If you can execute a grant, you can execute a contract. The paperwork is 80% the same.",
  },
  {
    kind: "opportunity",
    eyebrow: "The 5-step contract-ready checklist",
    title: "How to become contract-ready in 30 days.",
    subtitle: "The paperwork is the barrier — but the paperwork is small.",
    bullets: [
      "STEP 1 — SAM.gov registration. Free. Takes 3-10 business days for approval. Every federal contract, grant, and set-aside opportunity flows through SAM.",
      "STEP 2 — UEI number (Unique Entity Identifier). Automatically issued during SAM.gov registration. Replaces the old DUNS.",
      "STEP 3 — Capabilities Statement. One-pager. What you do, who you serve, past performance, NAICS codes, certifications, contact info. This is your resume for government contracting.",
      "STEP 4 — NAICS code identification. Every contract opportunity is coded to specific NAICS codes. Pick the 2-3 that describe what you actually do.",
      "STEP 5 — Past performance narrative. Two paragraphs on your last 3 major grants or projects, formatted like case studies with outcomes.",
    ],
    closer: "30 days of paperwork opens $700B in annual federal spending. That's not a bad ROI.",
  },
  {
    kind: "opportunity",
    eyebrow: "The corporate sponsor pyramid",
    title: "Three tiers of corporate sponsors. Three different asks.",
    subtitle: "The same email does NOT work for a local restaurant AND a Fortune 500.",
    bullets: [
      "TIER 1 — LOCAL BUSINESSES ($500-$5,000). Owned by someone in your community. Personal ask, in-person or handwritten. Sell them visibility + community goodwill.",
      "TIER 2 — REGIONAL COMPANIES ($5,000-$25,000). Regional bank branches, hospital systems, utilities, franchisees with multiple locations. Formal sponsor prospectus with tiered benefits (logo placement, employee volunteer day, table at your event).",
      "TIER 3 — NATIONAL / HQ ($25,000-$100,000+). Fortune 500 corporate giving programs and foundation arms. 6-12 month lead time. Formal LOI. Alignment with their published giving priorities.",
      "MATCH BENEFIT TO TIER — A local restaurant wants a shout-out. A national brand wants a case study for their sustainability report.",
      "ONE ASK IS NOT THE WHOLE RELATIONSHIP — Every sponsor you land is a candidate to renew next year AT A HIGHER TIER. Steward like the future depends on it — because it does.",
    ],
    closer: "Every nonprofit that scales past $500K has a corporate sponsor pyramid built out. Not one company. A pyramid.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "Earned Revenue Is Not a Sin",
    bigNumber: "$1 = $1",
    headline: "Nonprofit is a tax status. It's not a business model.",
    body: "Every major community-serving nonprofit generates earned revenue. Goodwill (thrift stores). YMCA (memberships). Habitat (ReStore retail). Salvation Army (thrift + services). Your local Boys & Girls Club (facility rentals). Earned revenue is UNRESTRICTED. It doesn't come with a reporting deadline. It doesn't get cut when a program officer changes jobs. The question is not WHETHER your nonprofit can earn income. The question is which mission-aligned enterprise fits your capacity.",
    sourceLine: "Source: The Bridgespan Group — Nonprofit Business Models research.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "Real Talk",
    bigNumber: "1 person",
    headline: "Sustainability is a hiring decision, not a strategy decision.",
    body: "You can strategize forever. But the org that hires ONE right person — a real grant writer, a real GovCon-trained development consultant, a real fractional CFO — outperforms the org that reads more articles by 10x. Sustainability isn't a mindset. It's an org chart. It's a headcount decision.",
    sourceLine: "Every nonprofit that scaled past $1M in annual revenue did it by hiring at least one dedicated development or finance role. Every single one.",
  },
  {
    kind: "opportunity",
    eyebrow: "Where sustainability actually lives",
    title: "The right people, in the right seats.",
    subtitle: "Not on your strategy doc. On your org chart.",
    bullets: [
      "A GRANT WRITER who knows how to READ AN RFP — not one who \"loves to write.\" Two completely different skills.",
      "A DEVELOPMENT CONSULTANT who runs your individual donor engine — recurring gifts, moves management, major gift cultivation.",
      "A FRACTIONAL CFO who can build (and reconcile monthly) a real 5-stream revenue budget.",
      "A CONTRACT / RFP RESPONSE PROFESSIONAL for government work — a separate skill set from grant writing.",
      "A BOARD CHAIR WHO GIVES OR GETS — not one who \"provides oversight.\" Boards should raise money. If they can't, they're a friend of the founder, not a fiduciary of the org.",
    ],
    closer: "Hiring the right person is expensive. NOT hiring the right person is more expensive.",
  },
  {
    kind: "opportunity",
    eyebrow: "Hire slow. Fire fast.",
    title: "5 questions to ask ANY development hire — before you hire them.",
    subtitle: "Anyone can say they \"love writing grants.\" Fewer can actually do the work.",
    bullets: [
      "1. \"Walk me through the last grant you wrote from RFP to submission.\" If they can't name the funder, the deadline, and the outcome, they haven't done this recently.",
      "2. \"What's the last grant you got REJECTED for — and why?\" Anyone who claims never to be rejected is either lying or hasn't applied enough.",
      "3. \"Show me 3 recent RFPs you screened OUT because they didn't fit. Why?\" The best grant writers say NO more than they say YES.",
      "4. \"Give me the exact NAICS or focus area of the last funder you helped submit to.\" Vague answers = they don't actually work in this space.",
      "5. \"What's your win rate?\" Real answer: 15-30% is normal. 60%+ means they only apply to easy ones. Under 10% means they're not reading guidelines.",
    ],
    closer: "The wrong development hire costs you 6 months of budget AND the funder relationships they damaged. The right one pays for themselves in the first cycle.",
  },
  {
    kind: "opportunity",
    eyebrow: "The 3-year path",
    title: "What real sustainability looks like Day 1 to Year 3.",
    subtitle: "A plan you can execute, not a slogan you can post.",
    bullets: [
      "YEAR 1 — Diversify. Add ONE new income stream this year (recurring donor engine or corporate sponsors is easiest).",
      "YEAR 1.5 — Contract-ready. SAM.gov registered. UEI active. First small local government contract in hand.",
      "YEAR 2 — Board activation. Every board member gives OR gets $5K minimum. If they can't, they're a friend of the org, not a fiduciary.",
      "YEAR 2.5 — Hire the first paid development person or fractional CFO. Real. Accountable. Paid.",
      "YEAR 3 — Grants become 30% of budget, not 80%. Foundation relationships get WARMER, not more strained.",
    ],
    closer: "Three years to sustainability isn't slow. It's the pace of what actually lasts.",
  },
  {
    kind: "closer",
    title: "Your homework tonight.",
    subtitle: "One decision. Not five. Not ten.",
    bullets: [
      "ACTION 1 — List your current revenue streams and their percentages. If any single stream is over 40%, that's your biggest risk.",
      "ACTION 2 — Pick ONE new income stream to build in the next 90 days. Just one.",
      "ACTION 3 — Identify ONE role (or contractor) you need to hire in the next 6 months to make that stream real.",
      "ACTION 4 — Look at ONE government contract opportunity on SAM.gov this week. See who's already winning contracts your org could deliver.",
      "ACTION 5 — Delete one funding request from your list that has no chance of being funded. Save the relationship.",
    ],
    closer: "The nonprofits that last aren't the ones with the biggest grant. They're the ones with the fewest weak links.",
    url: "flash-grants.com",
  },
];
DECKS["sustainability-thursday"] = SLIDES_SUSTAINABILITY_THURSDAY;


// =============================================================================
// DECK: EVENT-FUNDRAISING-THURSDAY — Turn a One-Day Event Into 90 Days of Wins
// Route: /slides/event-fundraising-thursday
// Themes: event grant lanes, sponsorship stack, in-kind, A-Tax funds, IRS
// quid-pro-quo rule, matching gifts at events, 30-day post-event play.
// =============================================================================
const SLIDES_EVENT_FUNDRAISING_THURSDAY = [
  {
    kind: "title",
    title: "Event Fundraising:",
    subtitle: "Turn a One-Day Event Into 90 Days of Wins.",
    bullets: [
      "Best Grant Thursday · Deep Dive",
      "The playbook nonprofits use to fund conferences, galas, and community events.",
    ],
    url: "flash-grants.com",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The Truth",
    bigNumber: "$0",
    headline: "Most nonprofits raise $0 in event grants — because they start planning too late.",
    body: "The average founder starts working on their event 90 days out. The average event grant window closes 6-9 months BEFORE the date. If you're planning a March conference and it's already February — you missed most of the grant lanes. This one calendar mistake costs SC founders tens of thousands in unclaimed money every year.",
    sourceLine: "Source: SC Arts Commission quarterly cycles; SCPRT tourism grant timelines; typical community foundation grant calendars.",
  },
  {
    kind: "opportunity",
    eyebrow: "The 5 revenue lanes for any event",
    title: "One event. Five ways to fund it.",
    subtitle: "If you're only chasing sponsors, you're leaving four lanes empty.",
    bullets: [
      "1. EVENT GRANTS — Arts commissions, tourism agencies, community foundations, county A-Tax funds. Real dollars, published rubrics, hidden pots.",
      "2. CORPORATE SPONSORS — Local, regional, national. Cash + in-kind. The main course for most events.",
      "3. INDIVIDUAL DONORS — Ticket sales, table hosts, patron gifts, sponsor-a-seat asks. The most stable event dollar you can raise.",
      "4. IN-KIND PARTNERS — Venue, catering, AV, printing, floral, photography. Every donated service is cash you don't have to raise.",
      "5. MATCHING GIFT MULTIPLIER — Every attendee whose employer matches doubles their ticket. Most events never claim it.",
    ],
    closer: "A five-lane event budget survives a bad sponsor year. A single-lane budget doesn't.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The 90-Day Window",
    bigNumber: "6-9 months",
    headline: "Most event grants have a 6-9 month lead time. Most founders find out with 6 weeks left.",
    body: "SC Arts Commission runs quarterly grant cycles. SCPRT tourism grants review 3-4 times a year. Community foundations typically fund on a Q1 / Q3 board schedule. Sept 30 is not the date to start applying for a March event — it's the date most cycles have already closed. Build the calendar backwards from your event date, not forwards from today.",
    sourceLine: "Source: SC Arts Commission (scartscommission.gov), SC Dept of Parks, Recreation & Tourism (SCPRT), typical community foundation grant calendars.",
  },
  {
    kind: "opportunity",
    eyebrow: "Where the money actually lives",
    title: "Event-eligible grant lanes — verified.",
    subtitle: "Every one of these has funded an SC event in the last 24 months.",
    bullets: [
      "SC ARTS COMMISSION — Quarterly Grants (rolling), Project Support, Folklife & Traditional Arts. Anything with a cultural or artistic component qualifies.",
      "SCPRT — Tourism Advertising Grants, Undiscovered SC, Sports & Tourism grants. Your event needs an out-of-county visitor angle.",
      "COMMUNITY FOUNDATIONS — Coastal CF, Central Carolina CF, Spartanburg CF, Greenville CF. Most have community-impact lines that fund community-serving events.",
      "COUNTY / MUNICIPAL A-TAX FUNDS — Accommodations tax revenue that MUST be distributed to tourism-boosting events. Every SC county with hotels has this pot. Almost nobody applies.",
      "CORPORATE FOUNDATIONS — Duke, Dominion, Truist, BofA, Publix, TD Bank. All have foundation arms that fund community events in cities where they operate.",
    ],
    closer: "Five categories. One event. Each one is a separate application on a separate calendar. Stack them.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The Accommodations Tax Secret",
    bigNumber: "3%",
    headline: "Every SC county collects a 3% accommodations tax — and by state law, a chunk of it MUST fund tourism-related events.",
    body: "South Carolina Code Title 6, Chapter 4 requires counties and municipalities that collect a state accommodations tax to distribute a portion specifically to organizations that promote tourism. Your festival, your conference that draws out-of-county attendees, your homecoming week — if it puts heads in hotel beds, an A-Tax committee is legally required to consider your application. Coastal counties run 6-figure pots. Most inland counties still run 5-figure pots. And most take 10-15 applicants a year.",
    sourceLine: "Source: South Carolina Code of Laws, Title 6, Chapter 4 (Accommodations Tax).",
  },
  {
    kind: "opportunity",
    eyebrow: "The sponsorship stack",
    title: "Sponsor tiers that actually work.",
    subtitle: "The same email does NOT work for a local restaurant AND a regional bank.",
    bullets: [
      "TITLE / PRESENTING ($10,000-$50,000) — Naming rights. \"Presented by [Company]\" on every asset. Only ONE. Ideally a regional or national brand with a real community-giving budget.",
      "GOLD ($5,000-$10,000) — Main-stage logo, speaking slot or table of 8, employee volunteer opportunity. 2-3 slots max.",
      "SILVER ($2,500-$5,000) — Program logo, table for their team, mention in press release. 4-6 slots.",
      "BRONZE ($500-$2,500) — Program listing, social mention, thank-you at the mic. 8-12 slots. Where local businesses live.",
      "IN-KIND — Venue, catering, AV, printing, photography, floral. Every in-kind partner gets the logo package equivalent to their fair-market value.",
      "MEDIA PARTNERS — Local radio, TV, newspaper. Trade coverage for logo placement. The coverage IS the ask — don't add a cash line.",
    ],
    closer: "A 6-tier stack lets a $50 donor and a $50,000 sponsor both feel like they belong at the same table. That's the design goal.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The In-Kind Multiplier",
    bigNumber: "30-40%",
    headline: "In-kind donations routinely reduce total event cash spend by 30-40% at well-run events.",
    body: "A $2,500 donated venue is $2,500 less you have to raise. A $1,500 donated catering package is $1,500 less. AV, printing, floral, photography, coffee — all commonly donated at community events. Every in-kind dollar is a dollar you don't have to grant-write for. AND — every in-kind partner deserves the same tier recognition as a cash sponsor at the same value. That's how they renew.",
    sourceLine: "Source: Industry-standard event budgeting practice; in-kind valuation per IRS Publication 526 fair-market-value guidance.",
  },
  {
    kind: "opportunity",
    eyebrow: "The 90-day pre-event funder calendar",
    title: "Backwards from your event date.",
    subtitle: "The calendar that separates founders from professionals.",
    bullets: [
      "T-9 MONTHS — Apply for SC Arts Commission Quarterly Grant + county A-Tax funds. Both have long lead times and specific fiscal year windows.",
      "T-6 MONTHS — Apply for SCPRT Tourism grants + community foundation event grants. Lock title sponsor conversations.",
      "T-4 MONTHS — Corporate mid-tier sponsors (Gold / Silver). Lock venue and catering (or their in-kind equivalents).",
      "T-3 MONTHS — Bronze-tier local sponsors. Media partners confirmed. Individual donor asks begin.",
      "T-2 MONTHS — Ticket sales open. In-kind partners finalized. Grant reports drafted for anything already awarded.",
      "T-1 MONTH — Sponsor deliverables finalized (logos on collateral, program printed). Day-of run of show locked.",
    ],
    closer: "Every month inside 6 months, funder options shrink. Every month inside 3 months, sponsor options shrink. Work backwards, or work harder.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The IRS Rule Nobody Follows",
    bigNumber: "$150 - $60",
    headline: "A $150 ticket that includes a $60 dinner is only a $90 tax-deductible gift. Get it wrong and your donors get hurt at tax time.",
    body: "IRS Publication 526 is explicit: quid-pro-quo contributions — where the donor gets goods or services in exchange for a gift — must be broken out. Every ticket, every table, every gala must state the fair market value of what the attendee receives vs. the deductible portion. Most SC nonprofits skip this line on their receipts and unintentionally set up their donors for an IRS problem. Get it right — your accountant will thank you AND your donors will renew.",
    sourceLine: "Source: IRS Publication 526 (Charitable Contributions) — quid-pro-quo disclosure requirements.",
  },
  {
    kind: "opportunity",
    eyebrow: "The matching gift multiplier at events",
    title: "Double your ticket revenue with one email.",
    subtitle: "The same play from Best Grant Thursday — applied to your event.",
    bullets: [
      "EVERY ticket confirmation email ends with: \"Does your employer match charitable gifts? Check at doublethedonation.com — many SC employers match your ticket price 1-to-1.\"",
      "SC MATCHING EMPLOYERS to name on the confirmation: Boeing, Volvo Cars, Blackbaud, Publix, Duke Energy, Dominion Energy, Bank of America, Michelin, TD Bank.",
      "A 200-person event with 20 attendees at matching employers @ $100 average ticket = $2,000 in unclaimed match revenue. Every event. Every year.",
      "Put a matching gift info card at every place setting. Two-line reminder: \"If your employer is on this list, ask HR to match your ticket. Takes 60 seconds.\"",
      "The post-event thank-you email repeats the ask 30 days later — when tax-time thinking kicks in.",
    ],
    closer: "Every event under-claims matching gifts by 60-80%. Every under-claimed match is a dollar you already raised and left on the table.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The Post-Event Window",
    bigNumber: "30 days",
    headline: "The 30 days AFTER your event is where NEXT year's event actually gets funded.",
    body: "Sponsors are exhausted after events. Attendees are inspired. Program officers who attended are watching for your follow-up. The 30-day post-event window is when you send sponsor renewals for next year (at 20% higher tier), send attendee thank-yous with next year's save-the-date, and email your program officer WITH photos and outcome data. Skip the 30-day window and every sponsor becomes a cold ask again next year.",
    sourceLine: "Source: Standard fundraising cadence — moves management post-event best practice.",
  },
  {
    kind: "opportunity",
    eyebrow: "The 30-day post-event play",
    title: "How to make one event pay for two.",
    subtitle: "The follow-up is the fundraising.",
    bullets: [
      "DAY 1 — Handwritten note to your title sponsor. Not typed. Not emailed. Handwritten.",
      "DAY 3 — Photo dump email to every attendee with a save-the-date for next year AND the matching gift reminder.",
      "DAY 7 — Sponsor recap email: attendance numbers, media impressions, revenue raised, community impact. INCLUDE a soft ask for renewal at the NEXT tier up.",
      "DAY 14 — Program officer touch. Anyone from a foundation who attended (or funded) gets a personal note with 3 photos and a specific outcome you couldn't have hit without their grant.",
      "DAY 21 — Post-event survey to attendees. One page. Five questions. Data feeds your next grant application AND identifies future volunteers/donors.",
      "DAY 30 — Board meeting recap. Full P&L, sponsor renewal pipeline, and next-year theme decision — BEFORE the T-9 month grant deadlines hit.",
    ],
    closer: "Founders think fundraising ends when the event ends. Professionals know the event was the fundraiser FOR the fundraiser. Compound events, don't produce them.",
  },
  {
    kind: "opportunity",
    eyebrow: "Save the ask. Save the relationship.",
    title: "5 things NOT to do with event fundraising.",
    subtitle: "Every one of these kills a sponsor relationship faster than a bad program.",
    bullets: [
      "DON'T ask a foundation for pure event sponsorship unless \"event support\" is EXPLICITLY named in the RFP. Foundations fund COMMUNITY IMPACT, not cocktail parties.",
      "DON'T send the same sponsor packet to a local restaurant AND a regional bank. Different tier. Different letter. Different ask amount.",
      "DON'T charge sponsors for their table seats. If they gave you $5,000, they don't buy tickets on top. Ever.",
      "DON'T skip the fair-market-value disclosure on tickets. IRS Publication 526 is not a suggestion — and your donors' CPAs will notice.",
      "DON'T cash-in your in-kind partners. If they gave you the venue this year, don't turn around and ask them for $5K next quarter for a different program. Steward the relationship you already have.",
    ],
    closer: "Every sponsor and in-kind partner is a candidate for a bigger yes NEXT year. Only if you don't burn them THIS year.",
  },
  {
    kind: "closer",
    title: "Your homework tonight.",
    subtitle: "One decision. Not five. Not ten.",
    bullets: [
      "ACTION 1 — Set your event date if you don't have one. Then count backwards 9 months and mark that day as \"Grant Cycle 1 starts.\"",
      "ACTION 2 — Identify your ONE title sponsor prospect. Just one. Write their name down tonight.",
      "ACTION 3 — List every in-kind item you'd need if you had zero cash. Venue, catering, AV, printing, flowers. That's your in-kind ask list.",
      "ACTION 4 — Draft your fair-market-value line for tickets NOW so it's on the ticketing page from day 1.",
      "ACTION 5 — Look up your county's A-Tax committee application. Most are on the county website under \"Accommodations Tax Grant.\" That's a real pot most nonprofits never touch.",
    ],
    closer: "An event isn't a fundraiser. An event is a fundraising engine that runs for 9 months before, one night on, and 30 days after. Build the engine, not the party.",
    url: "flash-grants.com",
  },
  {
    kind: "opportunity",
    eyebrow: "🎯 5 seats. First look tonight.",
    title: "Want me in the room with you for the next 12 months?",
    subtitle: "The Ultimate White Glove — Founding Cohort. Lifetime Pro access + 1 year of hands-on guidance from me. One-time offer, closed after 5.",
    bullets: [
      "FOUNDING ADVOCATE — $449 · Lifetime Pro access + 1 year of monthly strategy sessions with me. For the founder who wants a steady hand every month.",
      {
        text: "FOUNDING PARTNER — $899 · Lifetime Pro access + 1 year of bi-weekly sessions + application review on every submission you file. For the founder actively grant-hunting.",
        highlighted: true,
        tag: "Strongly Recommended",
      },
      "FOUNDING ARCHITECT — $1,500 · Lifetime Pro access + 1 year of weekly access + application review + custom-drafted funder emails + Post-Grant Manager priority on wins. Full white glove.",
      "WHY IT EXISTS — FlashGrants is built to be self-serve. But some of you are running events, filing 1023-EZs, and juggling boards — and self-serve isn't enough. This is the answer.",
      "5 SEATS TOTAL — Founding pricing closes when the fifth seat is filled. Priced once. Closed after 5. Never offered again at this tier.",
    ],
    closer: "Reply tonight — or grab me after class. No pitch, no pressure. Just tell me you're interested and I'll walk you through which tier fits.",
  },
];
DECKS["event-fundraising-thursday"] = SLIDES_EVENT_FUNDRAISING_THURSDAY;


// =============================================================================
// DECK: POST-WIN-THURSDAY — After the Yes: The First 30 Days That Earn You The Next Grant
// Route: /slides/post-win-thursday
// Themes: post-award stewardship, layered thank-you, reports as marketing,
// program officer relationship cadence, renewal math, funder introductions,
// what NOT to do post-award, White Glove CTA close.
// =============================================================================
const SLIDES_POST_WIN_THURSDAY = [
  {
    kind: "title",
    title: "After the Yes:",
    subtitle: "The First 30 Days That Earn You The Next Grant.",
    bullets: [
      "Best Grant Thursday · Deep Dive",
      "What separates one-time grantees from long-term partners.",
    ],
    url: "flash-grants.com",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The Real Cost of Silence",
    bigNumber: "1 in 3",
    headline: "Most first-time grantees never get a second grant from the same funder — not because their work was bad, but because they disappeared after the check cleared.",
    body: "Funders learn about your organization in one of two ways after they fund you: (a) YOU tell them, or (b) they never hear from you again. Most nonprofits pick (b) by default. Then they wonder why the renewal application gets rejected 12 months later. Every funder relationship is either compounding or decaying — there is no neutral. The 30 days after the yes decides which one.",
    sourceLine: "Source: Standard nonprofit development benchmark; Center for Effective Philanthropy Grantee Perception Report research on renewal drivers.",
  },
  {
    kind: "opportunity",
    eyebrow: "The five phases",
    title: "The 30-day post-award playbook.",
    subtitle: "One phase per week. Each one earns you the next grant.",
    bullets: [
      "WEEK 1 — THANK. ACKNOWLEDGE. PUBLICIZE. The window when the funder is watching most closely.",
      "WEEK 2 — REPORTING INFRASTRUCTURE. Photos, quotes, and data collection built from Day 1 — not scrambled at report time.",
      "WEEK 3 — PROGRAM OFFICER TOUCH. The email that turns a transactional funder into a personal champion.",
      "WEEK 4 — THE INTRODUCTION ASK. When and how to ask your funder to open the door to the NEXT funder.",
      "DAY 30 — BOARD MEETING RECAP. Impact narrative locked. Renewal strategy set. Next grant application already drafted.",
    ],
    closer: "Every phase compounds. Skip any one and the renewal application 12 months from now will feel harder than it needs to.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The Thank-You Window",
    bigNumber: "48 hours",
    headline: "Thank-yous sent inside 48 hours are 3x more likely to be remembered than notes sent a week later. Silence past week two reads as ingratitude.",
    body: "Every foundation program officer has an inbox that fills up on Monday morning. Your thank-you email that arrives on Day 3 gets read. Your thank-you email that arrives on Day 14 gets skimmed and archived. Your thank-you email that never arrives becomes the story: \"we funded them once but never heard back.\" 48 hours isn't a suggestion — it's the compounding starting point of every renewal.",
    sourceLine: "Source: AFP Fundraising Effectiveness Project donor-acknowledgment research; general nonprofit development best practice.",
  },
  {
    kind: "opportunity",
    eyebrow: "The layered thank-you",
    title: "Not one thank-you. Five.",
    subtitle: "Different people, different asks, different tempo — but all in Week 1.",
    bullets: [
      "DAY 1 — Email to the program officer. Short. Specific. Names the outcome the grant unlocks. NO ask, no update — just gratitude.",
      "DAY 2 — Handwritten note to the foundation's Executive Director or Board Chair (small foundation) OR to the program officer (large foundation). Not typed. Handwritten. Their assistant WILL notice.",
      "DAY 3 — Public thank-you: social post, newsletter, email blast. Tag the funder (LinkedIn), name them in a newsletter section. ONLY if the funder has confirmed public recognition is welcome.",
      "DAY 5 — Board thank-you, separate from staff. Doubles the touch. Shows the funder that the ENTIRE org is grateful — not just the ED.",
      "DAY 7 — Follow-up thank-you with ONE piece of media (photo, video clip, quote) from the first week of the grant work in action.",
    ],
    closer: "Every foundation officer has funded organizations that vanished after the wire cleared. When yours doesn't, they remember.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "Reports Are Marketing",
    bigNumber: "10x",
    headline: "Grant reports are read by more decision-makers than the original application. Treat them like marketing — because that's what they are.",
    body: "When you write a grant application, ONE program officer reads it. When you file the final report, the program officer reads it AND the board committee that approves next-cycle grants AND the next-generation program officers who will handle YOUR future asks. The report isn't paperwork closing a loop — it's the marketing document that opens the next four. Most nonprofits treat it as an inconvenience. Winners treat it as the most important deliverable of the year.",
    sourceLine: "Source: Foundation reporting practice; Center for Effective Philanthropy \"The State of Foundation Practice\" research.",
  },
  {
    kind: "opportunity",
    eyebrow: "The Day-1 deliverables",
    title: "Five things to collect from Day 1 — not at report time.",
    subtitle: "The founders who scramble for photos two weeks before a report are the founders who don't get renewed.",
    bullets: [
      "PHOTOS — 3-5 high-res photos EVERY month of the grant period. Beneficiaries (with signed photo releases), events, sites, staff at work. Dated folder. Non-negotiable.",
      "QUOTES — One first-person quote per month from a beneficiary or partner. 2-3 sentences, names + permission secured. This is what makes your report READ vs. skimmed.",
      "DATA POINTS — Track the EXACT metrics named in the original application. Not extra ones. Not fewer. Not different. Reviewers search the report for the exact promise you made.",
      "MEDIA MENTIONS — Every press hit, social share, newsletter feature, community shout-out. Screenshot everything, save the links. This is your \"we're growing\" proof.",
      "FUNDER-SPECIFIC NORTH STAR — For each funder, note the SPECIFIC promise you made in the application. That promise = the North Star of report time. Everything you collect ties back to it.",
    ],
    closer: "The nonprofit that shows up at report time with 47 photos, 12 quotes, and clean data ISN'T the one that scrambled. It's the one that documented from Day 1.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The Program Officer Truth",
    bigNumber: "1 person",
    headline: "One program officer, well-cultivated, is worth more than ten cold applications. Fund the relationship, not just the report.",
    body: "Center for Effective Philanthropy research on grantee-funder relationships consistently finds that grantees who describe their program officer as \"accessible\" and \"responsive\" report significantly higher renewal rates than those who describe theirs as \"distant.\" The variable isn't the program officer's personality — it's how much the grantee invested in the relationship. Being known as a nonprofit that DOESN'T disappear after the check clears is the cheapest competitive advantage in grantseeking.",
    sourceLine: "Source: Center for Effective Philanthropy — Grantee Perception Report and \"Working Well with Grantees\" research series.",
  },
  {
    kind: "opportunity",
    eyebrow: "The 30 / 60 / 90 touch calendar",
    title: "Program officer touchpoints that actually build relationship.",
    subtitle: "Not weekly emails. Not radio silence. This exact cadence.",
    bullets: [
      "DAY 30 — The \"we started strong\" email. One paragraph + one photo + one quote. No ask. Ends with: \"Happy to jump on a 15-minute call if useful — no update needed on your end.\"",
      "DAY 60 — Mid-cycle progress note. Two paragraphs. What's working, what surprised you, ONE specific outcome you couldn't have hit without their grant. Named. Numbered. Real.",
      "DAY 90 — Site visit invite (if geographically possible) OR virtual tour offer. Some officers say yes. Even the ones who say no remember that you invited them.",
      "IF THEY REPLY — Reply within 4 business hours. Every time. Program officers give faster attention to grantees who respect their inbox.",
      "IF THEY DON'T REPLY — Don't chase. Continue the cadence. Silence isn't rejection; it's them being busy. Show up again next month with something worth reading.",
    ],
    closer: "This isn't networking. This isn't fluff. This is the difference between being one of 200 grantees on file and being the ONE they suggest to a peer funder.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The Renewal Math",
    bigNumber: "1.2x",
    headline: "The right renewal ask is 15-25% higher than your first grant. Ask for less, you signal your growth stalled. Ask for more, you signal you don't understand the funder.",
    body: "Nonprofits over-index on either \"we're afraid to ask for more\" or \"let's double the ask.\" Both are wrong. Best practice across foundation program staff: your second-year ask should be 15-25% higher than your first grant, tied to specific documented growth and impact metrics. That range signals confidence + discipline + understanding of the funder's stewardship of their own capital. Anything outside that range needs an explicit narrative reason attached.",
    sourceLine: "Source: Standard foundation renewal practice; adapted from major-donor moves management research and CEP funder benchmarks.",
  },
  {
    kind: "opportunity",
    eyebrow: "The renewal ask",
    title: "When to ask for the next grant — and how much to ask for.",
    subtitle: "The window between Month 9 and Month 11 is where renewals get won or lost.",
    bullets: [
      "MONTH 9 — Preview note to the program officer. Two paragraphs. Here's what we accomplished, here's what we'd like to do next, would you welcome an updated LOI in the next 30 days? No formal ask yet — you're asking permission to ask.",
      "MONTH 10 — Submit the renewal LOI. Reference the previous grant's outcomes SPECIFICALLY (data + one beneficiary story). Ask for 15-25% more, tied to specific new work — not general operating support (unless GOS was named in the RFP).",
      "MONTH 11 — Full renewal application (if invited). Open with: \"Since our last grant, we've strengthened [specific thing] by [specific measure].\" That one sentence signals a mature grantee.",
      "IF DECLINED — Send a genuine thank-you within 48 hours. Ask for feedback (the one-paragraph email from Best Grant Thursday). Come back in the NEXT cycle stronger. Never disappear.",
      "IF APPROVED — Restart the 30-day post-award playbook from Day 1. This is where the compounding starts.",
    ],
    closer: "One funded grant is a transaction. Two from the same funder is a relationship. Three is a partnership. Five is a legacy.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The Multiplier",
    bigNumber: "3-5x",
    headline: "A funder who introduces you to another funder is worth 3-5 cold applications. And your existing funder WANTS to make that introduction — if you ask correctly.",
    body: "Program officers know other program officers. They talk at conferences, on peer calls, in shared LinkedIn threads. When a program officer introduces you to a peer at another foundation, you skip the cold-application gate entirely. Most nonprofits never ask because they assume asking is rude. It isn't — it's expected. Every funder wants to grow their impact through the grantees they've already vetted. All they need is a specific ask, at a specific time, with a specific reason.",
    sourceLine: "Source: Foundation program officer network practice; adapted from major-gift moves management referral research.",
  },
  {
    kind: "opportunity",
    eyebrow: "The introduction ask",
    title: "How to ask your funder for a warm introduction to the next funder.",
    subtitle: "The email that unlocks the peer-funder network — sent between Month 4 and Month 8 of the grant.",
    bullets: [
      "ONLY AFTER YOU'VE DELIVERED — Never ask for an introduction in the first 90 days. The relationship has to earn itself first. Wait until you have real outcomes to point to.",
      "BE SPECIFIC — \"Do you know anyone who funds X in Y county?\" beats \"Do you know anyone who might fund us?\" The more specific the ask, the more likely the yes.",
      "MAKE IT EASY — Attach a 2-paragraph \"introduction packet\" the program officer can literally copy-paste into an email to their peer. Don't make them write the intro.",
      "OFFER TO DO THE WORK — \"Happy to reach out cold if that's easier — I just wanted to check if you knew anyone who fits before I do.\" Gives them a graceful yes or decline.",
      "THANK EVERY INTRODUCTION — Even the ones that don't turn into grants. Program officers watch how you treat their peers. One bad follow-up ends every future introduction they'd have made.",
    ],
    closer: "Every introduction compounds. One good funder relationship becomes three funder relationships in eighteen months — but only if you ask, deliver, and thank.",
  },
  {
    kind: "opportunity",
    eyebrow: "Kill the renewal in 5 easy steps",
    title: "5 things NOT to do after you win a grant.",
    subtitle: "Every one of these guarantees you become a one-time grantee — no matter how good your work was.",
    bullets: [
      "DON'T ghost after the check clears. The most common — and most fatal — post-award behavior.",
      "DON'T over-communicate either. A weekly update email is as bad as silence. Program officers don't have time. Match the 30 / 60 / 90 cadence.",
      "DON'T pivot the grant work without telling them. If the plan changes, the program officer must hear it from YOU before it appears in the report.",
      "DON'T use grant funds outside the approved scope. Ever. If the funded work isn't happening, tell them and RETURN the funds. That single move earns more renewals than any application.",
      "DON'T ask for the renewal on the same day you file the final report. Space the ask 30-60 days minimum. Same-day reads as transactional, not relational.",
    ],
    closer: "The best-run nonprofits treat every grant like a relationship deposit. The worst treat it like a withdrawal. Program officers can tell the difference by month three.",
  },
  {
    kind: "closer",
    title: "Your homework tonight.",
    subtitle: "One decision. Not five. Not ten.",
    bullets: [
      "ACTION 1 — Pull your last funded grant. Ask: \"When was the last time I contacted that program officer OUTSIDE of report deadlines?\" If the answer is \"not since we got the check\" — send a thank-you touch email TONIGHT.",
      "ACTION 2 — Build your Day-1 deliverables folder for the current grant you're on: photos, quotes, data. Even if you started 6 months ago, start collecting now.",
      "ACTION 3 — Draft your Month-9 preview email — for the grant you're currently on — TODAY, so you're not scrambling in 12 months. Save it in drafts.",
      "ACTION 4 — Identify ONE funder who introduced you to ANOTHER funder. Send them a handwritten thank-you note this week. Watch what comes back.",
      "ACTION 5 — Add a \"stewardship & reporting infrastructure\" line to your next grant application budget. A tiny line signals you take post-award seriously.",
    ],
    closer: "One funded grant is a transaction. Ten funded grants over ten years is a career. The difference is what you do in the 30 days AFTER the check clears.",
    url: "flash-grants.com",
  },
  {
    kind: "opportunity",
    eyebrow: "🎯 5 seats. First look tonight.",
    title: "Want me in the room with you for the next 12 months?",
    subtitle: "The Ultimate White Glove — Founding Cohort. Lifetime Pro access + 1 year of hands-on guidance from me. One-time offer, closed after 5.",
    bullets: [
      "FOUNDING ADVOCATE — $449 · Lifetime Pro access + 1 year of monthly strategy sessions with me. For the founder who wants a steady hand every month.",
      {
        text: "FOUNDING PARTNER — $899 · Lifetime Pro access + 1 year of bi-weekly sessions + application review on every submission you file. For the founder actively grant-hunting.",
        highlighted: true,
        tag: "Strongly Recommended",
      },
      "FOUNDING ARCHITECT — $1,500 · Lifetime Pro access + 1 year of weekly access + application review + custom-drafted funder emails + Post-Grant Manager priority on wins. Full white glove.",
      "WHY IT EXISTS — FlashGrants is built to be self-serve. But some of you are running events, filing 1023-EZs, and juggling boards — and self-serve isn't enough. This is the answer.",
      "5 SEATS TOTAL — Founding pricing closes when the fifth seat is filled. Priced once. Closed after 5. Never offered again at this tier.",
    ],
    closer: "Reply tonight — or grab me after class. No pitch, no pressure. Just tell me you're interested and I'll walk you through which tier fits.",
  },
];
DECKS["post-win-thursday"] = SLIDES_POST_WIN_THURSDAY;


// =============================================================================
// DECK: RECURRING-DONORS-THURSDAY — Turn One-Time Givers Into Monthly Money
// Route: /slides/recurring-donors-thursday
// Themes: monthly giving math, retention gap, free tools (Zeffy/Give Lively/
// GiveButter), the 5-step launch, upgrade ladder, email cadence, mistakes to
// avoid, board/staff ownership.
// =============================================================================
const SLIDES_RECURRING_DONORS_THURSDAY = [
  {
    kind: "title",
    title: "Recurring Donors:",
    subtitle: "Turn One-Time Givers Into Monthly Money.",
    bullets: [
      "Best Grant Thursday · Deep Dive",
      "The playbook SC nonprofits use to build predictable, month-over-month revenue that survives a bad grant year.",
    ],
    url: "flash-grants.com",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The Math That Changes Everything",
    bigNumber: "$12,000",
    headline: "100 donors giving $10/month = $12,000/year. And it shows up on the 1st of every month whether the grant lands or not.",
    body: "One $10,000 gala takes 9 months to plan, exhausts your board, and lives or dies on the weather. 100 monthly donors at $10 each raises MORE money, arrives predictably, and requires almost zero ongoing work once set up. Every SC nonprofit under $500K in revenue should be running a monthly program. Most aren't — because nobody showed them the math.",
    sourceLine: "Source: Standard fundraising unit-economics math; average monthly gift benchmarks from M+R Benchmarks + Classy Fundraising Reports.",
  },
  {
    kind: "opportunity",
    eyebrow: "5 reasons monthly beats one-time",
    title: "One-time donors buy your story. Monthly donors buy in.",
    subtitle: "The unit economics are not close.",
    bullets: [
      "1. PREDICTABILITY — Monthly revenue lets you budget for staff, rent, and programs. One-time gifts fund emergencies. Different tools for different problems.",
      "2. HIGHER LIFETIME VALUE — Monthly donors typically give 40-60% MORE per year than one-time donors at the same starting gift size. And they stay longer.",
      "3. RETENTION — First-year one-time donor retention runs around 19%. Monthly donor retention runs 80-90%. That's a 4x-5x difference in relationship durability.",
      "4. GRANT LEVERAGE — Foundations LOVE seeing a monthly giving program. It signals community trust, financial sustainability, and organizational maturity. It answers the \"how will you sustain this after our grant?\" question before it's asked.",
      "5. NO HEAVY LIFT — Once a donor signs up, the money moves automatically. Zero ongoing ask work per donor. Compare to grant reporting — which never ends.",
    ],
    closer: "One-time gifts are transactions. Monthly gifts are relationships. Build the relationship engine.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The Retention Cliff",
    bigNumber: "19%",
    headline: "Only about 1 in 5 first-time donors gives a second time. The other 4 walk away — usually because you never asked them to come back the right way.",
    body: "The Fundraising Effectiveness Project tracks donor retention across thousands of US nonprofits every year. Overall retention hovers around 42-45%. NEW donor retention crashes to around 19%. But among monthly donors, retention typically runs 80-90% year-over-year. Ask any founder \"do you have a donor retention problem?\" — the answer is almost always yes and they don't know it. The recurring program IS the retention strategy.",
    sourceLine: "Source: Fundraising Effectiveness Project (fepretention.org) annual reports; retention rates consistent across 2022-2024 datasets.",
  },
  {
    kind: "opportunity",
    eyebrow: "The 3 free tools that make this work",
    title: "You don't need a $200/month platform to launch monthly giving.",
    subtitle: "All three are used by real SC nonprofits under $500K revenue.",
    bullets: [
      "ZEFFY — Literally free. Zero platform fee. They ask the DONOR (not you) for an optional tip. Best for nonprofits who want 100% of the gift to hit their bank account.",
      "GIVE LIVELY — Free forever, no tips shown to donors. Well-designed donor experience. Slightly more setup than Zeffy but the payment page looks premium.",
      "GIVEBUTTER — Free tier available (donor can optionally cover fees). Extra features for events + text-to-give if you want to grow into it.",
      "WHAT ABOUT DONORBOX / CLASSY / NEON? — Great platforms. Not free. If you're already paying for them, keep using them. If you're not, start with a free tool and upgrade only when your monthly revenue justifies it.",
      "THE ONE RULE — Whatever platform you pick, embed the giving form on YOUR OWN website. Do NOT send donors to a third-party checkout page. Every extra click loses 5-15% of conversions.",
    ],
    closer: "The best platform is the one that ships this week. Not the one you're still comparing next month.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The Mental-Threshold Trick",
    bigNumber: "$25/mo",
    headline: "A donor who won't write a $300 check tonight will happily sign up for $25/month — and give you $300 across the year without noticing.",
    body: "The average one-time online gift in the US hovers around $120-$150. The average monthly gift hovers around $24-$26 — which annualizes to roughly $290-$310. Donors don't experience monthly commitments the way they experience one-time asks. $25 a month feels like a Netflix subscription. $300 all at once feels like a decision. Your job is to give them the emotionally-easier option — and then thank them like their gift really is $300 (because it is).",
    sourceLine: "Source: M+R Benchmarks Study (2023-2024); Classy \"Why America Gives\" reports; consistent across small-nonprofit datasets.",
  },
  {
    kind: "opportunity",
    eyebrow: "The 5-step monthly launch",
    title: "Zero to first 10 monthly donors — in 14 days.",
    subtitle: "You don't need to hit 100 donors on day one. You need to hit 10.",
    bullets: [
      "DAY 1-3 — Pick your platform. Sign up. Embed the monthly giving form on your website homepage AND your donate page. Non-negotiable: it must load in under 3 seconds.",
      "DAY 4-5 — Write ONE story: a real person your nonprofit served + what $25/month funds. Not \"our mission\" — one story, one outcome, one photo. This is your monthly-giving anchor.",
      "DAY 6-7 — Email your existing donor list. Subject line: \"A quieter way to give.\" Frame it as a service to THEM — for donors who want to give more predictably. Ask specifically for $10, $25, or $50/month.",
      "DAY 8-10 — Personally text or call your 10 most engaged donors. Tell them the program launched. Ask them to be your first 5-10 charter monthly donors. Most will say yes if you ask directly.",
      "DAY 11-14 — Social media rollout. Include your board members' names as \"charter monthly donors #1-#N.\" Board leadership drives peer conversion faster than any ad.",
    ],
    closer: "Ten monthly donors at $25 = $250/month = $3,000/year of predictable revenue. That's your entry point. Compound from there.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The Upgrade Ladder",
    bigNumber: "35-50%",
    headline: "Roughly 1 in 3 monthly donors will accept an upgrade ask each year — if you send the right email at the right time.",
    body: "The annual upgrade email is the single highest-ROI email a monthly program sends. Sent to donors on their giving anniversary (or first thing in January), it asks a simple, specific question: \"Would you consider increasing your monthly gift from $25 to $35? That extra $10 funds one additional [specific outcome] every month.\" Industry benchmarks put upgrade acceptance rates at 35-50% for mid-tier monthly donors. The $10-$50 monthly donor is often eager to give more — they're just waiting to be asked.",
    sourceLine: "Source: Nonprofit fundraising benchmarks — DonorPerfect, Bloomerang, Blackbaud Institute upgrade-campaign case studies.",
  },
  {
    kind: "opportunity",
    eyebrow: "The 6 emails every monthly program needs",
    title: "Set them up once. They run forever.",
    subtitle: "Automate your appreciation before you scale your acquisition.",
    bullets: [
      "1. WELCOME EMAIL (Day 0) — Sent instantly. Warm, personal, names the specific outcome their monthly gift funds. Includes the impact story you wrote in the 14-day launch.",
      "2. 30-DAY THANK-YOU (Day 30) — Sent after first successful monthly charge. \"We just processed your first monthly gift. Here's what happened in your first month with us.\"",
      "3. QUARTERLY IMPACT UPDATE (every 90 days) — One photo, one story, one specific outcome. No ask. Ever. This is stewardship, not solicitation.",
      "4. GIVING ANNIVERSARY (Day 365) — \"You've been with us for a full year. Together we've funded [specific outcome × 12 months].\" Optional: soft upgrade ask embedded here.",
      "5. UPGRADE ASK (annual, January or on anniversary) — The single email designed to move donors up one tier. Specific dollar amount, specific outcome that the extra funds.",
      "6. FAILED-PAYMENT RECOVERY (auto-triggered) — When a card expires or fails, you have 5-7 days to save the donor. Automate a friendly reminder + a 1-click card update link. This alone saves 30-50% of lapsed monthly donors.",
    ],
    closer: "Six emails. Set up once. Every one of them either strengthens the relationship or saves revenue you already earned. Automation IS stewardship.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The Failed-Payment Silent Killer",
    bigNumber: "30-40%",
    headline: "Roughly a third of monthly donors churn every year because of failed payments — expired cards, closed accounts, hit limits. Not because they stopped caring.",
    body: "Your monthly donor didn't quit you. Their card expired. Their bank replaced their card number after a fraud alert. Their bank sent a new expiration date they never entered. Payment processors call this involuntary churn — and industry data shows it typically accounts for 30-40% of ALL monthly-donor loss. The good news: it's the most fixable churn there is. A single automated retry sequence + a friendly \"your card needs updating\" email recovers most of it. Miss it, and you're re-acquiring donors you should have kept.",
    sourceLine: "Source: Recurring-payment industry benchmarks (Chargebee, Recurly, Stripe subscription analytics); nonprofit-specific data from DonorBox retention reports.",
  },
  {
    kind: "opportunity",
    eyebrow: "5 mistakes that kill monthly programs",
    title: "Every one of these is a real founder mistake I've watched happen.",
    subtitle: "Fixable — but only if you know to look.",
    bullets: [
      "1. NO MONTHLY OPTION ON THE HOMEPAGE — If a donor has to click \"Donate\" then find a small toggle labeled \"Give monthly\" — you lose 40-60% of conversions. Monthly should be side-by-side with one-time, same prominence.",
      "2. THE DEFAULT GIFT SIZE IS TOO LOW — Setting the default to $5/month trains donors to think small. Set defaults at $10, $25, and $50 with $25 highlighted. Donors anchor to what you show them.",
      "3. NO IMPACT LANGUAGE ON THE GIVING PAGE — \"$25 funds tutoring for one student for a month\" converts 2-3x higher than \"Give $25.\" Every default gift level needs a specific outcome next to it.",
      "4. NO FAILED-PAYMENT AUTOMATION — See previous slide. Losing 30-40% of your monthly donors annually to expired cards is not a donor problem, it's a systems problem.",
      "5. NO THANK-YOU AFTER THE FIRST GIFT — If you send a receipt and nothing else, you've told the donor they're a transaction. Send a real thank-you email within 48 hours. Handwritten card within 7 days for gifts $25/month+.",
    ],
    closer: "Every one of these mistakes is invisible to the donor and expensive to you. Fix all five and your program compounds instead of leaking.",
  },
  {
    kind: "opportunity",
    eyebrow: "Who actually owns this?",
    title: "Board recruits. Staff maintains. Founder personally thanks.",
    subtitle: "Monthly programs die when nobody owns them. Assign it tonight.",
    bullets: [
      "BOARD ROLE — Every board member becomes a charter monthly donor at whatever tier they can afford. Every board member personally introduces 3 monthly donors from their network. This is a governance obligation, not a nice-to-have.",
      "STAFF ROLE — One staff member (or the founder, if you're solo) owns the platform, the 6 email sequences, and the monthly reconciliation. 2 hours a month once set up.",
      "FOUNDER ROLE — Personally thanks every new monthly donor within 7 days of their first gift. Handwritten note or a personal 90-second video. This is the one thing you cannot delegate.",
      "VOLUNTEER ROLE — One volunteer per 50 monthly donors handles quarterly impact-update production. Take one great photo, write one 3-sentence story, plug it into the template.",
      "THE RECURRING REPORT — 10 minutes at the top of every board meeting: how many donors joined, how many lapsed, upgrade acceptance rate, total monthly recurring revenue. That single dashboard changes board behavior.",
    ],
    closer: "Ownership creates growth. No owner, no growth — every time. Assign the role tonight, not \"soon.\"",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The Compounding Curve",
    bigNumber: "10 → 100",
    headline: "10 monthly donors in month 1. 100 by month 12. That's not a stretch goal — that's what happens when you just don't stop asking.",
    body: "Every SC nonprofit that hits 100 monthly donors got there the same way — they added 5-10 new monthly donors every month for 12-18 months. Not through a viral campaign. Not through a big-name endorser. Through the boring, compounding, undramatic discipline of asking every month, thanking every month, upgrading every year, and running the failed-payment sequence every week. 100 monthly donors × $25 = $2,500/month = $30,000/year of guaranteed revenue. That's a full-time program officer, or a rent-covered office, or three summer program cohorts. Every year. Forever.",
    sourceLine: "Source: Standard nonprofit compounding-growth math; typical monthly-donor acquisition rates at SC-sized organizations.",
  },
  {
    kind: "closer",
    title: "Your homework tonight.",
    subtitle: "One decision. Not five. Not ten.",
    bullets: [
      "ACTION 1 — Pick your platform. Zeffy or Give Lively or GiveButter. Sign up TONIGHT. Not tomorrow. The platform choice is 90% less important than starting.",
      "ACTION 2 — Write your ONE impact story. A real person + a real outcome + what $25/month funds. Save it in a Google Doc titled \"Monthly Giving Anchor.\"",
      "ACTION 3 — Text your 5 most loyal donors this week: \"We're launching a monthly giving program. Would you be one of our first 5 charter members at $25/month?\" That's the ask. Word-for-word.",
      "ACTION 4 — Add \"monthly recurring revenue\" as a line on your next board agenda. Report the number every meeting. What gets reported gets grown.",
      "ACTION 5 — Set up the failed-payment recovery email in your platform this week. It's the single highest-ROI 20 minutes you'll spend all quarter.",
    ],
    closer: "Ten monthly donors is a start. One hundred is a program. One thousand is a movement. And every one of them starts with the ask you send tonight.",
    url: "flash-grants.com",
  },
  {
    kind: "opportunity",
    eyebrow: "🎯 5 seats. First look tonight.",
    title: "Want me in the room with you for the next 12 months?",
    subtitle: "The Ultimate White Glove — Founding Cohort. Lifetime Pro access + 1 year of hands-on guidance from me. One-time offer, closed after 5.",
    bullets: [
      "FOUNDING ADVOCATE — $449 · Lifetime Pro access + 1 year of monthly strategy sessions with me. For the founder who wants a steady hand every month.",
      {
        text: "FOUNDING PARTNER — $899 · Lifetime Pro access + 1 year of bi-weekly sessions + application review on every submission you file. For the founder actively grant-hunting.",
        highlighted: true,
        tag: "Strongly Recommended",
      },
      "FOUNDING ARCHITECT — $1,500 · Lifetime Pro access + 1 year of weekly access + application review + custom-drafted funder emails + Post-Grant Manager priority on wins. Full white glove.",
      "WHY IT EXISTS — FlashGrants is built to be self-serve. But some of you are running events, filing 1023-EZs, launching monthly programs, and juggling boards — and self-serve isn't enough. This is the answer.",
      "5 SEATS TOTAL — Founding pricing closes when the fifth seat is filled. Priced once. Closed after 5. Never offered again at this tier.",
    ],
    closer: "Reply tonight — or grab me after class. No pitch, no pressure. Just tell me you're interested and I'll walk you through which tier fits.",
  },
];
DECKS["recurring-donors-thursday"] = SLIDES_RECURRING_DONORS_THURSDAY;


// =============================================================================
// DECK: REJECTION-THURSDAY — 30 Days After The No: The Playbook Most Founders
// Never Run
// Route: /slides/rejection-thursday
// Themes: rejection reality, decoding the letter, debrief request scripts,
// the 30-day playbook, second-application win advantage, mistakes to avoid.
// =============================================================================
const SLIDES_REJECTION_THURSDAY = [
  {
    kind: "title",
    title: "30 Days After The No:",
    subtitle: "The Playbook Most Founders Never Run.",
    bullets: [
      "Best Grant Thursday · Deep Dive",
      "Every founder loses grants. Only professionals turn the loss into the next win.",
    ],
    url: "flash-grants.com",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The Rejection Reality",
    bigNumber: "70-90%",
    headline: "Most competitive foundations reject 70-90% of applications. If you're not getting turned down, you're not applying enough.",
    body: "Foundation Directory data and Council on Foundations reporting consistently show competitive grant programs fund 10-30% of applications received. That means 70-90% of every batch gets a no. Rejection isn't a signal you're bad at this — it's a statistical certainty of grant work. What separates founders from professionals isn't whether you get rejected. It's what you do in the 30 days after.",
    sourceLine: "Source: Foundation Center / Candid grant-competition data; Council on Foundations program benchmarks; typical family and community foundation award rates.",
  },
  {
    kind: "opportunity",
    eyebrow: "What a rejection letter ACTUALLY means",
    title: "7 real reasons — in order of frequency.",
    subtitle: "It's almost never \"your work isn't good enough.\"",
    bullets: [
      "1. THE POT WAS FULL — The funder had 200 great applications and 20 slots. Yours was one of 180 great apps that didn't fit. Nothing about YOU. About the math.",
      "2. YOU WEREN'T A STRATEGIC PRIORITY THIS CYCLE — Every foundation shifts focus year to year. This cycle they prioritized rural health. Next cycle it's youth. You were fine — you were just off-cycle.",
      "3. YOUR NARRATIVE DIDN'T MATCH THEIR LANGUAGE — Their RFP used the word \"systems-change.\" You wrote about \"programs.\" Reviewers scan for their own language and yours didn't map.",
      "4. THE BUDGET FLAGGED SOMETHING — No indirect cost line. Salaries out of proportion to program spend. No local match. Reviewers pass without even reading the full narrative.",
      "5. YOU'RE UNKNOWN TO THEM — First-time applicants have measurably lower success rates than repeat applicants. Not because you're worse — because trust hasn't been built yet.",
      "6. A REVIEWER MISSED THE POINT — Real thing. Reviewers are human, tired, reading 40 apps in a weekend. Sometimes yours got a bad read and there was no appeal path.",
      "7. THE ONE YOU'RE AFRAID OF — Yes, sometimes the work genuinely didn't fit. But this is the LEAST common reason. Almost every rejection is reasons 1-6, not 7.",
    ],
    closer: "Reject the shame narrative. It's not personal. It's almost never quality. It's almost always fit + timing + language.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The 48-Hour Rule",
    bigNumber: "48 hrs",
    headline: "The first 48 hours after a rejection letter is emotional wilderness. Don't respond, don't strategize, don't email the funder. Just sit.",
    body: "Every founder wants to fire off an email defending the application, calling the program officer, or moving straight to the next grant. All three are mistakes in the first 48 hours. Emotion contaminates every one of them — the defensive email reads defensive, the phone call reads bitter, the next grant gets rushed. The 48-hour rule is a professional protection: nothing goes out, nothing gets applied for, nothing gets decided about the loss until 48 hours have passed. Sit with it. Then work.",
    sourceLine: "Source: Standard executive-decision practice; emotional-response research from moves-management literature.",
  },
  {
    kind: "opportunity",
    eyebrow: "Day 1-3: The emotional reset",
    title: "Before strategy — human maintenance.",
    subtitle: "Skip this step and every other step gets weaker.",
    bullets: [
      "DAY 1 — Tell your board chair BEFORE the whole board hears. Two sentences. \"We didn't get [X]. I'm working the debrief and will bring the next-cycle plan to our next meeting.\" You lead the narrative, not the rumor mill.",
      "DAY 1 — Tell your team ONCE, in person or in a 5-minute Zoom. Then stop bringing it up. The staff takes emotional cues from you. If you spiral, they spiral.",
      "DAY 2 — Go outside. Walk. Run. Cook something. Do anything with your hands that isn't grant-related. Loss processing is a physical event as much as a mental one.",
      "DAY 3 — Write one page. Not to send — just to write. What you thought went well. What now looks weaker with hindsight. What surprised you about the outcome. This is your PRIVATE debrief. Nobody else sees it.",
      "DO NOT — Post about the loss on social media. Ever. Foundations Google you before every grant decision. A visible bitter post today closes doors 18 months from now that you won't even know closed.",
    ],
    closer: "You cannot skip the emotional reset. Every professional who has beaten the rejection game did the first 3 days right. Do them right.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The Program Officer Truth",
    bigNumber: "Most say yes",
    headline: "Most program officers will give you a debrief call if you ask professionally. Almost no founder asks.",
    body: "Program officers are humans. They rejected 180 great applications this cycle. They know most of those applicants will disappear, then reapply blindly next year, and lose again for the same reasons. The founder who says \"can I get 15 minutes on the phone to understand what would strengthen a future application?\" is the founder they remember. Ask, and you'll usually get the call. Don't ask, and you'll spend another year guessing. The ask is the moat.",
    sourceLine: "Source: Standard practice among community and family foundation program officers; grantmaker-to-grantee feedback protocols consistent across GEO, Center for Effective Philanthropy guidance.",
  },
  {
    kind: "opportunity",
    eyebrow: "Day 4-10: The debrief request",
    title: "The exact email + phone script.",
    subtitle: "This is where 90% of founders quit. Don't be them.",
    bullets: [
      "EMAIL SUBJECT — \"Grateful for your consideration — one request for future strengthening\"",
      "EMAIL OPENING — \"Thank you for reviewing our application. We know how many great applications you had to weigh, and we're grateful ours was in the mix.\"",
      "THE ASK — \"If you have 15 minutes in the next few weeks, I'd deeply value hearing anything specific that would strengthen a future submission from us. I'll take notes, I won't argue, and we won't waste your time.\"",
      "IF THEY SAY YES — Come with 3 questions max: \"What made the winning apps different?\" · \"Where did our narrative feel weakest?\" · \"Is there a cycle or program that would fit us better?\" Take notes. Do not defend. Thank them twice.",
      "IF THEY SAY NO OR NO RESPONSE — Send a follow-up in 2 weeks. Still nothing? Note it in your file and move on graciously. Some funders don't do debriefs. Not personal. Different systems.",
      "AFTER THE CALL — Send a handwritten thank-you note to the program officer. Not typed. Not emailed. Handwritten. This is the single most compounding action of the entire 30 days.",
    ],
    closer: "The debrief conversation is the highest-ROI 15 minutes in grant work. Every founder skips it. Every professional runs it.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The Second-Try Advantage",
    bigNumber: "2-3× higher",
    headline: "Founders who debrief and reapply typically win at 2-3× the rate of first-time applicants — because reviewers see the changes.",
    body: "Reviewers on repeat-applicant panels routinely flag \"this application addresses the concerns we raised last cycle.\" That single sentence is the reason repeat applicants outperform first-timers. When you rebuild the app around the actual debrief notes — not what you WISHED they'd said, what they DID say — you turn a rejection into a competitive advantage. The founder who reapplies with visible growth wins the room. The founder who resubmits the same thing loses again.",
    sourceLine: "Source: Instrumentl user data on repeat-application success rates; Center for Effective Philanthropy grantee feedback research; standard funder-cycle practice.",
  },
  {
    kind: "opportunity",
    eyebrow: "Day 11-20: The reframe & rewrite",
    title: "Rebuild the app around the debrief notes.",
    subtitle: "Not what you wanted them to say — what they actually said.",
    bullets: [
      "STEP 1 — Turn every debrief note into a specific application change. \"Narrative was vague on outcomes\" becomes \"add 3 outcome metrics with baseline + target.\" One-to-one mapping.",
      "STEP 2 — Rewrite your problem statement in THEIR language, not yours. If their RFP said \"community-driven,\" that phrase appears in your narrative. If they said \"systems-change,\" you use it. Match their vocabulary.",
      "STEP 3 — Fix the budget flags. Add an indirect cost line (10-15% is normal). Show local match. Balance salaries and program spend. Reviewers pass over budgets with red flags in 90 seconds.",
      "STEP 4 — Get a fresh reader. Someone who did NOT see the first version. New eyes catch what tired eyes miss. Board member, peer ED, or a paid grant consultant.",
      "STEP 5 — Draft the resubmission ONE cycle out — not next cycle. Reapplying to the same funder within one cycle can read desperate. Wait one full cycle unless the funder specifically invited a resubmission.",
    ],
    closer: "The rewrite is where the loss becomes the win. Skip it and next year is a repeat of last year.",
  },
  {
    kind: "opportunity",
    eyebrow: "Day 21-30: The compound play",
    title: "Turn one loss into three new opportunities.",
    subtitle: "Loss is a data event. Data creates leads.",
    bullets: [
      "MOVE 1 — Add the funder to your \"12-month warm re-approach\" list. Set a calendar reminder for month 8: send an unsolicited program update WITHOUT an ask. That single email keeps you top of mind for the next cycle.",
      "MOVE 2 — Identify the 3 funders MOST SIMILAR to the one that rejected you. Same funding size, same geography, same focus area. Add them to your pipeline for next quarter. If Funder A was interested enough to review, Funders B/C/D will be interested too.",
      "MOVE 3 — Ask the program officer (in the debrief call) — \"Are there other funders in this space you'd suggest I look at?\" Most say yes and name 2-3. Free lead generation.",
      "MOVE 4 — Post your (paraphrased) rejection lessons on LinkedIn or your blog. NOT bitter. Curious. \"What I learned from a recent funder debrief.\" This positions you as a professional the sector wants to fund.",
      "MOVE 5 — File the debrief notes in a permanent \"funder intelligence\" doc. Every rejection you debrief adds to a database only YOU have. In 5 years that database is a competitive weapon.",
    ],
    closer: "One rejection professionally worked yields 3-5 new leads and a database entry that compounds for years. That's what \"the loss is the beginning\" actually means in practice.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The 6-Month Window",
    bigNumber: "Month 8",
    headline: "The best time to re-approach a funder who rejected you is roughly 8 months later — with a WIN update, not an ask.",
    body: "Foundations track relationships across cycles. Applicants who disappear after a rejection lose relationship capital. Applicants who send a brief \"here's what we've been building\" email around month 8 — no ask, just an update — routinely find a warmer reception the next cycle. Include one specific win from the intervening months (a grant landed, a program milestone, a new board member of note). The message is: \"We're still here. Still growing. Still worth watching.\" That's the whole email.",
    sourceLine: "Source: Standard moves-management cadence in institutional fundraising; Center for Effective Philanthropy funder-grantee relationship benchmarks.",
  },
  {
    kind: "opportunity",
    eyebrow: "5 things NOT to do after a rejection",
    title: "Every one of these has burned a real founder I've watched.",
    subtitle: "Fixable — but only before you press send.",
    bullets: [
      "DON'T argue the rejection in the reply email. Ever. The decision is made. Every defensive sentence closes a door for the next cycle.",
      "DON'T call the program officer within 48 hours. You'll be emotional and they'll remember. Wait for the reset.",
      "DON'T reapply to the same funder in the very next cycle unless they specifically invited it. Reads desperate. Wait one full cycle.",
      "DON'T post frustration about the funder on social media. Foundations Google you. Community-foundation program officers all know each other. One bitter post costs you 3 future opportunities.",
      "DON'T resubmit the same application to a different funder unchanged. The rejection was a data event — use it. Copy-paste applications lose everywhere.",
    ],
    closer: "The 30 days after a rejection is when doors close silently. Every one of these mistakes closes a door you won't see close. Avoid them and doors stay open.",
  },
  {
    kind: "opportunity",
    eyebrow: "Who owns the debrief?",
    title: "Founder runs it. Staff supports. Board hears the summary.",
    subtitle: "Rejection is not a staff problem. It's an executive one.",
    bullets: [
      "FOUNDER / ED — Owns the debrief call, the handwritten note, the reframe decision, and the 12-month re-approach calendar. Non-delegable.",
      "STAFF ROLE — Pulls the original application, gathers reviewer comments if provided, drafts the rewrite based on debrief notes. Support role.",
      "BOARD ROLE — Hears a 3-minute summary at the next board meeting. Not a debate. Not a post-mortem. A 3-minute \"here's what we learned and here's the next cycle plan.\"",
      "GRANT WRITER / CONSULTANT — If you use one, they get the debrief notes verbatim. Not paraphrased. Their next draft is measurably stronger with real feedback.",
      "THE DEBRIEF DOC — One page per rejected funder. Filed forever. This is your growing funder-intelligence library. Nobody else has it.",
    ],
    closer: "Ownership matters. If the founder doesn't run the debrief personally, it never happens. And what doesn't happen doesn't compound.",
  },
  {
    kind: "closer",
    title: "Your homework tonight.",
    subtitle: "One decision. Not five. Not ten.",
    bullets: [
      "ACTION 1 — Pick the ONE rejection you've been sitting on the longest. The one that still stings. That's the one to work.",
      "ACTION 2 — Draft the debrief-request email using tonight's script. Save it in drafts. Send it tomorrow if you're inside 90 days of the rejection.",
      "ACTION 3 — Start your \"funder intelligence\" doc. One page. Funder name, program, why you think you were rejected, what you'll change next cycle. Even without the debrief call — start filing.",
      "ACTION 4 — Identify the 3 funders MOST SIMILAR to the one that rejected you. Add them to your Q1 pipeline. The rejection just gave you a lead-gen template — use it.",
      "ACTION 5 — Set a calendar reminder for month 8: send a no-ask update email to the funder who rejected you. Warm re-approach beats blind reapplication every time.",
    ],
    closer: "Every founder gets rejected. Every professional turns rejection into a database. One loss worked professionally is worth three grants applied blindly. Work the loss.",
    url: "flash-grants.com",
  },
  {
    kind: "opportunity",
    eyebrow: "🎯 5 seats. First look tonight.",
    title: "Want me in the room with you for the next 12 months?",
    subtitle: "The Ultimate White Glove — Founding Cohort. Lifetime Pro access + 1 year of hands-on guidance from me. One-time offer, closed after 5.",
    bullets: [
      "FOUNDING ADVOCATE — $449 · Lifetime Pro access + 1 year of monthly strategy sessions with me. For the founder who wants a steady hand every month.",
      {
        text: "FOUNDING PARTNER — $899 · Lifetime Pro access + 1 year of bi-weekly sessions + application review on every submission you file. For the founder actively grant-hunting.",
        highlighted: true,
        tag: "Strongly Recommended",
      },
      "FOUNDING ARCHITECT — $1,500 · Lifetime Pro access + 1 year of weekly access + application review + custom-drafted funder emails + Post-Grant Manager priority on wins. Full white glove.",
      "WHY IT EXISTS — FlashGrants is built to be self-serve. But some of you are working debriefs, filing 1023-EZs, and juggling boards — and self-serve isn't enough. This is the answer.",
      "5 SEATS TOTAL — Founding pricing closes when the fifth seat is filled. Priced once. Closed after 5. Never offered again at this tier.",
    ],
    closer: "Reply tonight — or grab me after class. No pitch, no pressure. Just tell me you're interested and I'll walk you through which tier fits.",
  },
];
DECKS["rejection-thursday"] = SLIDES_REJECTION_THURSDAY;


// =============================================================================
// DECK: BUDGET THURSDAY — "The 90-Second Budget Page"
// Route: /slides/budget-thursday
// Themes: the reviewer's 90-second scan, the math triangle, indirect cost
// benchmarks, in-kind compounding, the 6 line items, red flags to avoid.
// =============================================================================
const SLIDES_BUDGET_THURSDAY = [
  {
    kind: "title",
    title: "The Budget Page:",
    subtitle: "90 Seconds. Life Or Death.",
    bullets: [
      "Best Grant Thursday · Deep Dive",
      "The one page reviewers judge before they read a word of your story.",
    ],
    url: "flash-grants.com",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The 90-Second Reality",
    bigNumber: "90 sec",
    headline: "Reviewers spend roughly 90 seconds on your budget before deciding whether to keep reading the narrative. If the budget page reads amateur, the narrative doesn't get a fair shot.",
    body: "Foundation review panels are volume operations. A reviewer typically has 30-60 applications to score in a weekend. The budget is the FIRST place experienced reviewers look — before the narrative, before the theory of change, before the resumes. It's a shortcut: a clean, professional budget signals a clean, professional organization. A sloppy budget signals \"we'll waste your money.\" You can write the best narrative in the state and lose the grant on the budget page. That's not fair. It's just true.",
    sourceLine: "Source: Grantmakers for Effective Organizations reviewer training; Bridgespan grantee-readiness research; standard foundation review-panel practice.",
  },
  {
    kind: "opportunity",
    eyebrow: "What reviewers actually scan for",
    title: "The 7 things they check in those 90 seconds.",
    subtitle: "In this order — every time.",
    bullets: [
      "1. DO THE NUMBERS MATCH? — Does the budget total equal the amount requested in the cover letter, in the narrative, and on the online form? If any of those three numbers disagree, reviewers pass without reading further.",
      "2. IS THERE AN INDIRECT COST LINE? — A budget with NO indirect / admin line looks amateur. A budget with a 40% indirect line looks bloated. Reviewers scan for the range and the label.",
      "3. WHAT'S THE SALARY-TO-PROGRAM RATIO? — If personnel eats more than 70% of the ask, reviewers wonder where the work is. If it's under 30%, they wonder how the work gets done.",
      "4. IS THERE MATCH / LEVERAGE? — Reviewers scan for \"in-kind,\" \"volunteer value,\" or \"other funder cash.\" No match column = looks like the funder is your only bet, which reads risky.",
      "5. ARE THE UNIT COSTS DEFENSIBLE? — \"Program supplies: $10,000\" is a red flag. \"Program supplies: 40 kits @ $247 = $9,880\" is a green flag. Round numbers everywhere means you made it up.",
      "6. DID YOU BUDGET FOR EVALUATION? — At least a small line for measurement, data, or reporting. Zero evaluation dollars = you don't plan to prove it worked.",
      "7. ARE FRINGE BENEFITS CALCULATED? — If you have salaried staff and no fringe line, either the budget is fake or the staffing is fake. Both kill the app.",
    ],
    closer: "Every one of these takes reviewers 5-15 seconds to check. Fix all 7 and you're in the top 20% of budgets on the panel before they read a word.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The Math Triangle",
    bigNumber: "3 numbers",
    headline: "The requested amount on your cover letter, the narrative dollar figure, and the budget total MUST all match to the dollar. When they don't, reviewers stop reading.",
    body: "This is the single most common budget-page failure I see in SC grant work. Founders update the budget spreadsheet Wednesday, update the narrative Thursday, and forget the cover letter still says the old ask. Reviewers see the mismatch — even $50 off — and read it as \"this organization can't do basic math with its own money.\" The fix takes 60 seconds. Print all three numbers. Circle them. If they don't match, don't submit. Ever.",
    sourceLine: "Source: Standard foundation reviewer feedback across community foundation debrief calls; grants management practice guides.",
  },
  {
    kind: "opportunity",
    eyebrow: "The 60-second pre-submit check",
    title: "Print. Circle. Match. Then submit.",
    subtitle: "Skip this once and you'll never skip it again.",
    bullets: [
      "STEP 1 — Print or PDF the cover letter, narrative, budget spreadsheet, and the online-form summary page. Physical paper if you can — it forces different eyes.",
      "STEP 2 — On each document, CIRCLE the total ask number in red pen. That's the number that MUST match across all four.",
      "STEP 3 — Add them up left-to-right. If the four circled numbers are not identical, DO NOT submit. Fix the mismatched documents first.",
      "STEP 4 — Check the arithmetic INSIDE the budget spreadsheet. Sum the column. Sum the rows. They should both equal the ask. If Excel says $47,850 and the ask says $48,000, someone rounded and forgot.",
      "STEP 5 — Have ONE OTHER PERSON do the same circle-and-match pass. Fresh eyes catch what tired eyes miss. Board treasurer is ideal. Grant writer if you have one.",
    ],
    closer: "This is the cheapest professional habit in grant work. 60 seconds. 4 documents. One red pen. It keeps you from losing grants you already won on paper.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The Indirect Cost Truth",
    bigNumber: "10-25%",
    headline: "Most foundations expect an indirect cost line between 10% and 25%. Zero indirect looks amateur. Above 25% needs documentation. The middle is where you live.",
    body: "The federal government sets a floor of 10% \"de minimis\" indirect cost for organizations without a Negotiated Indirect Cost Rate Agreement (NICRA). Traditional foundations historically capped indirect at 15-20%. Ford Foundation moved its minimum to 25% in January 2023. Bridgespan's research on 20 high-performing nonprofits found TRUE indirect costs ranged 21-89% with a median of 40% — meaning most nonprofits are underfunded on overhead. Practical rule for SC applicants: 10-15% is safe, 20-25% requires a one-sentence justification, above 25% needs a full policy attached. And ALWAYS include the line. Zero indirect = red flag.",
    sourceLine: "Sources: OMB Uniform Guidance 2 CFR 200 (10% de minimis); Ford Foundation policy update Jan 1, 2023 (25% floor); Bridgespan Group, \"Pay-What-It-Takes Philanthropy\" research.",
  },
  {
    kind: "opportunity",
    eyebrow: "The 6 line items that must appear",
    title: "Every professional budget has these six lines.",
    subtitle: "In this order, every time. No exceptions.",
    bullets: [
      "1. PERSONNEL — List each role separately: title, % FTE on this grant, annual salary, grant portion. \"Program Director · 25% FTE · $60,000 · $15,000.\" Reviewers read this line for org maturity.",
      "2. FRINGE BENEFITS — Real number. Typically 20-30% of personnel for orgs offering healthcare and retirement. \"Fringe @ 25% of personnel = $3,750.\" If you have no fringe, mark it \"$0 — see policy note\" and explain why. Never blank.",
      "3. DIRECT PROGRAM COSTS — Supplies, materials, contractors, transportation. Every line with a unit calculation: \"40 kits @ $247 = $9,880.\" Never bare round numbers.",
      "4. EVALUATION / MEASUREMENT — Data collection, reporting, surveys, external evaluator if applicable. Even a $500 line signals you plan to prove it worked.",
      "5. INDIRECT / ADMIN — 10-15% is safe. Label it \"Indirect (rent, utilities, admin, insurance) @ 12%.\" Show the calculation so reviewers don't guess.",
      "6. MATCH / IN-KIND — Volunteer hours at Independent Sector rate, donated space, donated services, other funder cash. This is the section every rookie skips and every pro maxes out.",
    ],
    closer: "If any of these six lines is missing, your budget is incomplete. Fix that before you fix the narrative. The narrative can't save an amateur budget.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The Match Multiplier",
    bigNumber: "$36.14/hr",
    headline: "The Independent Sector value of one volunteer hour is $36.14 as of April 2026. Most SC nonprofits leave $10K-$50K in match value off their budget every single application.",
    body: "This is the highest-ROI single edit you can make to your budget page tonight. Every hour a volunteer works is real economic value that funders LOVE seeing as match. A 12-person board that meets monthly for 2 hours = 288 board-hours/year = $10,408 in match value. Add mentoring hours, event volunteers, professional services donated (accountants, attorneys, marketing consultants — those bill at MUCH higher than $36.14), donated space, donated printing. In SC the match column can easily hit 30-50% of your total budget WITHOUT costing you a dollar. Funders read a strong match column as \"this org has community backing.\" That's the compound win.",
    sourceLine: "Source: Independent Sector, \"Value of Volunteer Time 2026 Report,\" released April 21, 2026 in partnership with the Do Good Institute (University of Maryland). Based on 2025 BLS Current Employment Statistics.",
  },
  {
    kind: "opportunity",
    eyebrow: "How to build a match column that lands",
    title: "In-kind valuation, done right.",
    subtitle: "Legitimate. Documented. Compounding across every application.",
    bullets: [
      "VOLUNTEER TIME — Log hours per person per month. Multiply by $36.14 for general volunteers. For skilled volunteers (attorneys, CPAs, designers, IT), use their real billable rate — but keep documentation (email, LinkedIn title) showing that's their actual profession.",
      "BOARD MEETINGS — 12 board members × 12 meetings × 2 hours = 288 hours × $36.14 = $10,408 in annual match. Add prep time and it's higher.",
      "DONATED SPACE — Church, community center, coworking space, someone's warehouse. Get the market rent per square foot from a local commercial realtor site (or Zillow) — cite the source in a footnote. Multiply by your hours of use.",
      "DONATED PROFESSIONAL SERVICES — Attorney reviewing your bylaws (attorney bills at $250/hr × 3 hrs = $750). CPA reviewing your 990 ($1,200). Marketing consultant redesigning your logo ($2,500). Get a short email from each professional confirming the hours and the rate.",
      "SUPPLIES & MATERIALS — Everything donated with a legitimate fair-market value: laptops (retail), printed materials (printer's invoice at cost), food for events (grocery receipts). Document all of it in a running log.",
      "THE COMPOUND — Once you have a match log that's updated monthly, EVERY future grant application pulls from the same file. Match value builds year over year. Rookie orgs rewrite match from scratch every time. Pros just copy the last quarter.",
    ],
    closer: "Match isn't fake money. It's real economic value your org already receives. Documenting it is the highest-ROI hour of admin work you'll do all year.",
  },
  {
    kind: "opportunity",
    eyebrow: "5 red flags that kill your budget in seconds",
    title: "Every one of these has cost a real SC founder a grant.",
    subtitle: "Fixable — but only before you press submit.",
    bullets: [
      "1. ROUND NUMBERS EVERYWHERE — \"Supplies $10,000. Travel $5,000. Marketing $3,000.\" Reviewers know nobody's real costs land on the round thousand. Round numbers = you invented the budget the night before.",
      "2. NO INDIRECT LINE — Every professional org has overhead. Zero indirect reads as \"this founder doesn't understand real cost of operations.\" Even 8% with a note is better than blank.",
      "3. EXECUTIVE SALARY = 40%+ OF ASK — If your ED / founder salary alone is 40% or more of what you're asking a foundation to cover, that's a solo-consultant grant, not a program grant. Reviewers pass.",
      "4. NO MATCH COLUMN AT ALL — Signals your org has no community backing, no volunteer base, and this funder is the only bet. Even minimal match ($3-5K in board-hour value) is better than zero.",
      "5. BUDGET NARRATIVE MISSING — Every professional application has a 1-paragraph budget narrative explaining the biggest line items. \"Personnel represents 45% of the ask because...\" Rookies submit the spreadsheet alone. Pros include the paragraph.",
    ],
    closer: "Any ONE of these red flags won't kill a strong application on its own. Two of them together will. Three and the panel doesn't even finish reading.",
  },
  {
    kind: "stat",
    spicy: true,
    eyebrow: "The Personnel Ratio Rule",
    bigNumber: "30-70",
    headline: "Reviewers expect personnel + fringe to land between 30% and 70% of a program budget. Above 70% reads as consulting. Below 30% reads as unstaffed.",
    body: "There's no formal rule that says personnel must be between 30-70% — but every reviewer on every foundation panel has an unconscious range in their head. Above 70% personnel and the reviewer thinks \"where's the actual programming? This is a paycheck disguised as a grant.\" Below 30% and the reviewer thinks \"who's doing the work? Volunteers can't run this alone.\" The 30-70 corridor is where credible programs live. If your budget lands outside it, either restructure the ask or write a very specific budget narrative paragraph explaining why. Never hope reviewers won't notice.",
    sourceLine: "Source: BBB Wise Giving Alliance financial standards; Charity Navigator financial-health methodology; standard foundation review-panel practice.",
  },
  {
    kind: "opportunity",
    eyebrow: "5 things NOT to do on the budget page",
    title: "Every one of these is fixable — but only before submit.",
    subtitle: "One-page checklist. Read it every time.",
    bullets: [
      "DON'T submit a budget with round numbers only. Every line needs unit-level math. \"25 laptops × $412 = $10,300\" beats \"laptops: $10,000\" every single time.",
      "DON'T leave the indirect cost line off. Even 10% with a one-line policy note is exponentially better than blank. Blank reads as amateur.",
      "DON'T bury the match column. Put it on the FIRST page of the budget, not the last. Reviewers glance at page one only when they're rushed — make sure they see your match.",
      "DON'T ask for the maximum grant amount when your program can be run for less. Reviewers respect \"we need $18,500\" over \"we'll take the full $25,000 because it's available.\" Right-sizing signals maturity.",
      "DON'T copy last year's budget into this year's application without revising the units. Prices change. Programs evolve. Reviewers can smell a stale budget from the first line.",
    ],
    closer: "The budget page is where reviewers decide if you're professional. Every one of these mistakes signals amateur. Every fix signals pro. Signal pro.",
  },
  {
    kind: "closer",
    title: "Your homework tonight.",
    subtitle: "One budget. Five checks. Before Sunday.",
    bullets: [
      "ACTION 1 — Pull ONE budget from an application you submitted in the last 6 months. Doesn't matter if you won or lost. Run tonight's 7-item scan on it. Circle every red flag.",
      "ACTION 2 — Add an indirect cost line if it's missing. 10-15% with a one-sentence policy note. If you already have one, check whether it's labeled clearly (\"Indirect · rent, utilities, admin, insurance @ 12%\").",
      "ACTION 3 — Build a match log. One spreadsheet. Volunteer hours, board hours, donated space, donated professional services. Every entry gets a date and a fair-market rate. This file gets reused on EVERY future application.",
      "ACTION 4 — Rewrite one round-number line. Pick your biggest \"supplies: $10,000\" line and replace it with a unit calculation. \"40 kits × $247 = $9,880.\" One line at a time.",
      "ACTION 5 — Save the 6-line-item template from tonight as your master budget structure. Every future application starts from this file. Zero blank documents ever again.",
    ],
    closer: "You cannot out-narrative an amateur budget. But you can out-budget a mediocre narrative. Fix the budget page and everything else you submit gets stronger.",
    url: "flash-grants.com",
  },
  {
    kind: "opportunity",
    eyebrow: "🎯 5 seats. First look tonight.",
    title: "Want me in the room with you for the next 12 months?",
    subtitle: "The Ultimate White Glove — Founding Cohort. Lifetime Pro access + 1 year of hands-on guidance from me. One-time offer, closed after 5.",
    bullets: [
      "FOUNDING ADVOCATE — $449 · Lifetime Pro access + 1 year of monthly strategy sessions with me. For the founder who wants a steady hand every month.",
      {
        text: "FOUNDING PARTNER — $899 · Lifetime Pro access + 1 year of bi-weekly sessions + application review on every submission you file. For the founder actively grant-hunting.",
        highlighted: true,
        tag: "Strongly Recommended",
      },
      "FOUNDING ARCHITECT — $1,500 · Lifetime Pro access + 1 year of weekly access + application review + custom-drafted funder emails + Post-Grant Manager priority on wins. Full white glove.",
      "WHY IT EXISTS — FlashGrants is built to be self-serve. But some of you are working debriefs, filing 1023-EZs, and building match logs from scratch — and self-serve isn't enough. This is the answer.",
      "5 SEATS TOTAL — Founding pricing closes when the fifth seat is filled. Priced once. Closed after 5. Never offered again at this tier.",
    ],
    closer: "Reply tonight — or grab me after class. No pitch, no pressure. Just tell me you're interested and I'll walk you through which tier fits.",
  },
];
DECKS["budget-thursday"] = SLIDES_BUDGET_THURSDAY;


export { DECKS };
